/**
 * FTC REPORT BUILDER - IMPROVED VERSION
 * 
 * Features:
 * - Tab 1: Select Section (1-5)
 * - Tab 2: Select Fraud Type (11 options)
 * - Tab 3: Add MULTIPLE accounts to that section
 * - Navigate to next section after completing one
 * - Edit any section after it's set
 * - Shows: Section totals + Grand Total fraud amount
 */

/* eslint-disable */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import DisputeLetterModal from './DisputeLetterModal';
import { previewPdf } from './PdfPreview';
import DisputeAccountsPickerModal from './DisputeAccountsPickerModal';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import SaveToFinalPackageButton from './SaveToFinalPackageButton';
import { 
  Shield, Download, RefreshCw, CheckCircle2, 
  ChevronRight, Trash2, DollarSign, ArrowLeft, ArrowRight, Edit2, Plus, AlertTriangle,
  Mail, Send, X, Loader2, FileText, Upload, MapPin, ExternalLink, Printer, PenLine,
  User, ClipboardList, FileDown, Edit,
  CreditCard, Banknote, Car, Smartphone, Zap, Home,
  Stethoscope, GraduationCap, Building2, Umbrella, HelpCircle,
  Landmark, IdCard, Feather, Package, LayoutList,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import HandwritingConverterModal from './HandwritingConverterModal';
import BureauFormEditor from './BureauFormEditor';
import { downloadBlobMobileSafe } from '../utils/mobileDownload';
import { checkFtcReport } from '../lib/ftcPreflight';
import FtcPreflightDialog from './FtcPreflightDialog';
import SectionAccountList from './SectionAccountList';

const API = process.env.REACT_APP_BACKEND_URL;

// 11 Fraud Types as specified by user
// 2026-02-28 (user request): each type gets its own lucide icon + accent
// color so admin can eyeball-pick the right category faster during the
// FTC report build.  Icon + color are used in the fraud-type tile grid
// (see TAB 2 render below).
const FRAUD_TYPES = [
  { value: "CREDIT_CARD", label: "1. Credit card opened by the thief",         Icon: CreditCard,   color: "text-blue-300",    ring: "border-blue-500/40",    tint: "bg-blue-500/10" },
  { value: "LOAN_LEASE",  label: "2. Fraudulent personal or business loan",    Icon: Banknote,     color: "text-purple-300",  ring: "border-purple-500/40",  tint: "bg-purple-500/10" },
  { value: "AUTO_LOAN",   label: "3. Fraudulent Auto Loan or Lease",           Icon: Car,          color: "text-red-300",     ring: "border-red-500/40",     tint: "bg-red-500/10" },
  { value: "PHONE",       label: "4. Fraudulent Mobile Phone Account",         Icon: Smartphone,   color: "text-amber-300",   ring: "border-amber-500/40",   tint: "bg-amber-500/10" },
  { value: "UTILITY",     label: "5. Utility account opened by the thief",     Icon: Zap,          color: "text-teal-300",    ring: "border-teal-500/40",    tint: "bg-teal-500/10" },
  { value: "RENTAL",      label: "6. Fraudulent Rental of Apartment or House", Icon: Home,         color: "text-orange-300",  ring: "border-orange-500/40",  tint: "bg-orange-500/10" },
  { value: "MEDICAL",     label: "7. Medical Services Obtained by the Thief",  Icon: Stethoscope,  color: "text-pink-300",    ring: "border-pink-500/40",    tint: "bg-pink-500/10" },
  { value: "STUDENT_LOAN",label: "8. Private Student Loans",                   Icon: GraduationCap,color: "text-indigo-300",  ring: "border-indigo-500/40",  tint: "bg-indigo-500/10" },
  { value: "REAL_ESTATE", label: "9. Fraudulent Real Estate Loan",             Icon: Building2,    color: "text-emerald-300", ring: "border-emerald-500/40", tint: "bg-emerald-500/10" },
  { value: "INSURANCE",   label: "10. Fraudulent Insurance",                   Icon: Umbrella,     color: "text-cyan-300",    ring: "border-cyan-500/40",    tint: "bg-cyan-500/10" },
  { value: "OTHER",       label: "11. Other identity theft",                   Icon: HelpCircle,   color: "text-zinc-300",    ring: "border-zinc-500/40",    tint: "bg-zinc-500/10" }
];

// 5 Personal Statement Options (including inquiry-specific and single account)
const STATEMENT_OPTIONS = [
  {
    id: 1,
    label: "Version 1 – Direct & Formal",
    text: "After reviewing my most recent credit report, I discovered multiple accounts listed that I do not recognize and did not authorize. These accounts were opened without my consent. When I contacted the associated creditors, I was informed that the personal information on file did not match my own. Based on this information, it appears that my identity has been compromised. I then contacted all three major credit bureaus and was advised that my personal information was involved in multiple data breaches. I was instructed to file an identity theft report to have these fraudulent items removed promptly."
  },
  {
    id: 2,
    label: "Version 2 – Clear & Consumer-Focused",
    text: "Upon examining my current credit report, I identified several accounts that I have no involvement with and never opened. After reaching out to the creditors reporting these accounts, they confirmed that the information provided did not align with my records. This led me to believe that my identity was used without authorization. I subsequently contacted the three major credit bureaus, where I was informed that my personal data had been exposed in multiple breaches. I was advised to submit an identity theft report to ensure the immediate removal of these unauthorized accounts."
  },
  {
    id: 3,
    label: "Version 3 – Strong Identity-Theft Language",
    text: "A review of my latest credit report revealed the presence of accounts that I do not recognize and did not establish. These accounts were opened fraudulently in my name. After contacting the reporting creditors, I was advised that the information on the accounts did not match my identity, confirming unauthorized use of my personal information. I then notified all three major credit bureaus and was informed that my data had been compromised through several security breaches. Based on their guidance, I am submitting this report to request the immediate removal of all fraudulent items."
  },
  {
    id: 4,
    label: "Version 4 – Inquiry Removal Only",
    text: "AFTER LOOKING OVER A COPY OF MY LATEST CREDIT REPORT I NOTICED THAT THERE ARE SEVERAL HARD INQUIRIES THAT WERE NOT MADE BY ME AND IT SEEMS LIKE SOMEBODY IS USING MY NAME TO TRY TO GET CREDIT OR APPLY FOR CREDIT I CONTACTED ALL OF THE INQUIRY CREDITORS AND I WAS TOLD TO MAKE A REPORT TO GET THEM REMOVED. ALSO, I FOUND OUT MY INFORMATION WAS INVOLVED IN MULTIPLE DATA BREACHES."
  },
  {
    id: 5,
    label: "Version 5 – Single Account Only",
    text: "After reviewing my most recent credit report, I discovered the account listed that I do not recognize and did not authorize. This account was opened without my consent. When I contacted the associated creditor, I was informed that the personal information on file did not match my own. Based on this information, it appears that my identity has been compromised. I then contacted all three major credit bureaus and was advised that my personal information was involved in multiple data breaches. I was instructed to file an identity theft report to have this fraudulent item removed promptly."
  },
  {
    // 2026-02-28 (user request) — AI-generated short statement that
    // enumerates the exact months + severity each account was reported
    // late and demands removal under FCRA.  Selecting this triggers a
    // call to POST /api/ftc/statement-late-payments which returns the
    // paragraph; the `text` here is a sensible placeholder until the
    // user clicks "Generate statement" with their late-payment accounts.
    id: 6,
    label: "Version 6 – Late Payments (AI-generated)",
    text: "After reviewing my most recent credit report, I identified inaccurate late-payment notations on the account(s) listed in this report. These late payments were reported in error — the account(s) were not actually past due during the periods indicated. I dispute these notations under the Fair Credit Reporting Act (15 U.S.C. §1681e(b), §1681i, and §1681s-2(b)), which requires every consumer reporting agency and furnisher to maintain maximum possible accuracy. I demand that each inaccurate late-payment notation be investigated and removed from my credit file immediately.",
    aiGenerated: true,
  }
];

// PacketCheckboxRow — single row on the Dispute Packet Builder that
// lets the user check a document generated in THIS session (Sworn
// Statement, Dispute Letter, or FTC Report) instead of re-uploading
// it from disk on mobile.  Disabled + hinted when the blob hasn't
// been captured yet (i.e. the user hasn't generated that doc yet).
function PacketCheckboxRow({ label, blobEntry, checked, onToggle, testid }) {
  const available = !!blobEntry;
  const filename = blobEntry?.name || '';
  const sizeKb = blobEntry?.blob?.size
    ? Math.max(1, Math.round(blobEntry.blob.size / 1024))
    : blobEntry?.size
      ? Math.max(1, Math.round(blobEntry.size / 1024))
      : null;
  return (
    <label
      className={`flex items-start gap-2 p-2.5 rounded-lg border transition cursor-pointer ${
        available
          ? checked
            ? 'bg-emerald-500/10 border-emerald-400/40'
            : 'bg-white/[0.04] border-white/10 hover:border-emerald-400/30'
          : 'bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed'
      }`}
      data-testid={testid}
    >
      <input
        type="checkbox"
        disabled={!available}
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer disabled:cursor-not-allowed"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white flex items-center gap-1.5">
          {label}
          {available ? (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold uppercase tracking-wider">
              Ready
            </span>
          ) : (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-600/40 text-slate-400 border border-slate-500/30 font-bold uppercase tracking-wider">
              Not generated yet
            </span>
          )}
        </div>
        {available ? (
          <div className="text-[11px] text-slate-400 mt-0.5 truncate">
            {filename}
            {sizeKb ? ` · ${sizeKb} KB` : ''}
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 mt-0.5">
            Generate this document earlier in the flow, then re-open the packet builder.
          </div>
        )}
      </div>
    </label>
  );
}

// Splits an FTC line like "CAP ONE - 09-2024 - $1,500" into name / date / balance.
const parseFtcLine = (line) => {
  const t = String(line || '').trim();
  const m = t.match(/^(.*?)(?:\s*[-–]\s*(\d{1,2}(?:[-/]\d{1,2})?[-/]\d{2,4}))?\s*[-–]\s*\$\s*([\d,]+(?:\.\d+)?)\s*$/);
  return m ? { name: m[1].trim(), date: m[2] || '', balance: m[3].replace(/,/g, '') } : { name: t, date: '', balance: '' };
};

export default function FTCReportBuilderNew({ clientId, clientData, signatureData, onComplete, preloadedAccounts = [], preloadedSections = null, selectedAccounts = [], ftcTheme = 'color', dateFormat = 'month_year', savedReportId = null, onReportSaved = null, canOpenEditIntent = false, onOpenEditIntent = null, onSwornStatementCreated = null, onEditClientInfo = null, onClientInfoEdited = null, lockedSections = [] }) {
  const [accounts, setAccounts] = useState([]);
  const [preflight, setPreflight] = useState(null);
  // Double-check before any PDF is made; resolves true to go ahead.
  const preflightOk = (ci, secs, statement) => new Promise((resolve) => {
    const issues = checkFtcReport(ci, secs, statement);
    if (!issues.length) return resolve(true);
    setPreflight({ issues, resolve });
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [accountsLoadedFromProps, setAccountsLoadedFromProps] = useState(false); // Track if accounts were loaded from props
  // Saved-report tracking — bumps when we successfully persist to /api/ftc-reports
  const navigate = useNavigate();
  const [currentSavedReportId, setCurrentSavedReportId] = useState(savedReportId);
  const [savingReportLog, setSavingReportLog] = useState(false);
  
  // Preview modal state - EDITABLE PREVIEW
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null); // Editable preview data
  // 2026-02-18 — user complained the Preview modal "still shows a generic
  // blue version" of the FTC report because the Live PDF pane defaulted OFF
  // and only the HTML mock rendered on open.  Default it ON so the actual
  // PyMuPDF-rendered FTC form appears next to the editor immediately.
  const [showLivePdf, setShowLivePdf] = useState(true);
  const [livePdfUrl, setLivePdfUrl] = useState(null);
  const [livePdfBuilding, setLivePdfBuilding] = useState(false);
  const [livePdfError, setLivePdfError] = useState(null);
  const [livePdfPages, setLivePdfPages] = useState([]); // [{page, width, height, data_url}]
  const [originalPreviewData, setOriginalPreviewData] = useState(null); // Track original for changes
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatingFromPreview, setGeneratingFromPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Current tab: 'section', 'fraud', 'accounts'
  // 2026-02-28 (iPhone bug fix) — hydrate navigation state from
  // sessionStorage so a mobile Safari tab-eviction (which happens
  // after any blob download opens a new tab and the user returns)
  // doesn't dump the user back on Step 1.  The wizard's account
  // groupings already round-trip through the server auto-save, but
  // *which tab* the user is on wasn't persisted anywhere — so on
  // remount we snapped to 'section' with everything empty.
  const _navKey = clientId ? `ftc:nav:${clientId}` : 'ftc:nav:__nocid__';
  const _hydratedNav = (() => {
    try { return JSON.parse(sessionStorage.getItem(_navKey) || 'null'); }
    catch { return null; }
  })();
  const [currentTab, setCurrentTab] = useState(_hydratedNav?.currentTab || 'section');
  const [selectedSection, setSelectedSection] = useState(_hydratedNav?.selectedSection ?? null);
  const [selectedFraudType, setSelectedFraudType] = useState(_hydratedNav?.selectedFraudType ?? null);
  
  // Sections data: { 1: { fraudType: string, accountIds: Set, customTotalAmount: string }, 2: {...}, ... }
  // MAX 5 SECTIONS - each section can have MULTIPLE accounts grouped together
  const [sections, setSections] = useState({
    1: { fraudType: null, accountIds: new Set(), customTotalAmount: '' },
    2: { fraudType: null, accountIds: new Set(), customTotalAmount: '' },
    3: { fraudType: null, accountIds: new Set(), customTotalAmount: '' },
    4: { fraudType: null, accountIds: new Set(), customTotalAmount: '' },
    5: { fraudType: null, accountIds: new Set(), customTotalAmount: '' }
  });
  
  // 2026-02-28 (iPhone bug fix) — persist navigation state to
  // sessionStorage on every change.  Combined with the hydration
  // block above and the server-side sections auto-save, this makes
  // iOS Safari tab-eviction transparent: user returns from any
  // download / tab and lands exactly where they left off.
  useEffect(() => {
    try {
      sessionStorage.setItem(_navKey, JSON.stringify({
        currentTab,
        selectedSection,
        selectedFraudType,
        savedReportId: currentSavedReportId || null,
      }));
    } catch { /* private-mode / quota — silently drop */ }
  }, [currentTab, selectedSection, selectedFraudType, currentSavedReportId]);

  // Temp selection for multi-select
  const [tempSelected, setTempSelected] = useState(new Set());

  // Account-picker view mode — Compact / Cards / Table. Persisted per-browser
  // so the user keeps their preferred view across sessions and clients.
  const [accountPickerView, setAccountPickerViewState] = useState(() => {
    try { return localStorage.getItem('ftc:accountPickerView') || 'compact'; }
    catch { return 'compact'; }
  });
  const setAccountPickerView = (v) => {
    setAccountPickerViewState(v);
    try { localStorage.setItem('ftc:accountPickerView', v); } catch { /* ignore */ }
  };
  // Per-account inline-expand state for the Compact view
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  const toggleAccountExpand = (id) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  
  // Per-section "Date I Discovered" - { sectionNum: "MM/YYYY" }
  const [dateDiscoveredPerSection, setDateDiscoveredPerSection] = useState({});
  
  // Per-account "Date I Discovered" - { accountId: "MM/YYYY" } (legacy)
  const [dateDiscoveredPerAccount, setDateDiscoveredPerAccount] = useState({});
  
  // Personal statement selection (1, 2, or 3)
  const [selectedStatementVersion, setSelectedStatementVersion] = useState(1);
  const [personalStatement, setPersonalStatement] = useState(STATEMENT_OPTIONS[0].text);
  
  // Abbreviate accounts option - for fitting many accounts on the report
  const [abbreviateAccounts, setAbbreviateAccounts] = useState(false);

  // Report date and time - pre-filled with current date/time, but editable
  const [reportDate, setReportDate] = useState('');
  const [reportTime, setReportTime] = useState('');
  
  // Default "Date I Discovered" for new accounts (current month/year)
  const [defaultDateDiscovered, setDefaultDateDiscovered] = useState('');

  // FTC Report Number (auto-generated but editable)
  const [reportNumber, setReportNumber] = useState('');
  const [loadingReportNumber, setLoadingReportNumber] = useState(true);
  
  // FTC Report Number Modal - shows BEFORE generating to ask user for the number
  const [showReportNumberModal, setShowReportNumberModal] = useState(false);
  
  // Credit Repair Steps Email Popup - shows AFTER generating FTC report
  const [showCreditRepairEmailPopup, setShowCreditRepairEmailPopup] = useState(false);
  const [sendingCreditRepairEmail, setSendingCreditRepairEmail] = useState(false);
  // 2026-02-24 — Send-mode selector shown inside the popup.  Because sending
  // 5 back-to-back emails was tripping Gmail's spam filter for some clients,
  // the user can now pick between the 5-part drip, one large combined email,
  // or BOTH.  Backend accepts these values verbatim on /send-credit-repair-series.
  //   "series"   → 5 separate emails, 1 min apart (existing default)
  //   "combined" → 1 large email containing every step
  //   "both"     → combined email now, then the 5-part series 5 min later
  const [creditRepairSendMode, setCreditRepairSendMode] = useState('series');
  const [ftcAccountsForEmail, setFtcAccountsForEmail] = useState({ transunion: [], equifax: [], experian: [] });
  const [suggestedReportNumber, setSuggestedReportNumber] = useState(null);
  const [isFirstTimeReport, setIsFirstTimeReport] = useState(true);
  const [pendingGenerateAction, setPendingGenerateAction] = useState(null); // 'generate' or 'preview'
  // Dispute Letter — new Feb 2026 flow, opens on demand from the credit-repair-email modal.
  const [showDisputeLetterModal, setShowDisputeLetterModal] = useState(false);
  const swornJustCreatedRef = useRef(false);
  // 2026-02-28 (user request) — Pre-modal picker: shows every parsed
  // account with checkboxes so the admin can uncheck any they don't want
  // on the dispute letter.  Sits BETWEEN "Create Dispute Letter" click
  // and the DisputeLetterModal opening.  Confirmed subset is stashed
  // into `ftcAccountsStructuredForDispute` before we open the modal.
  const [showDisputePicker, setShowDisputePicker] = useState(false);
  // Feb 2026 — Saved Dispute Letters for this client (persisted per generate).
  // Users can reopen any saved letter and (a) quick-edit just the font/date
  // (small dialog + re-render) OR (b) edit the full letter (opens the
  // wizard hydrated at Step 3 with all previous values).
  const [savedDisputeLetters, setSavedDisputeLetters] = useState([]);
  const [savedLettersLoading, setSavedLettersLoading] = useState(false);
  // Full letter doc to feed the DisputeLetterModal when reopening.
  const [activeSavedLetter, setActiveSavedLetter] = useState(null);
  // Quick edit dialog (font/date only) — non-modal light dialog.
  const [quickEditLetter, setQuickEditLetter] = useState(null);
  const [quickEditFont, setQuickEditFont] = useState('times');
  const [quickEditDate, setQuickEditDate] = useState('');
  const [quickEditGenerating, setQuickEditGenerating] = useState(false);
  // Structured accounts (with open date + balance) built at FTC-gen time so
  // the dispute-letter modal can render each account with the same detail
  // level as the FTC report itself.
  const [ftcAccountsStructuredForDispute, setFtcAccountsStructuredForDispute] = useState([]);
  // 2026-Feb: Post-FTC-generation dispute-letter prompt — fires immediately
  // after the FTC report PDF is downloaded (per user request).  Restored
  // 2026-02-05 after being erroneously removed.
  const [showPostFtcDisputePrompt, setShowPostFtcDisputePrompt] = useState(false);
  // Post-email dispute-letter prompt — kept for the alternate flow where
  // the user skips creating the letter on Step 1 and wants it offered
  // again after the Credit Repair emails are scheduled.
  const [showPostEmailDisputePrompt, setShowPostEmailDisputePrompt] = useState(false);
  // True when the dispute modal was opened from the post-email prompt so
  // we know to hop to the reminder popup on close instead of loop-back.
  const [disputeOpenedFromPostEmail, setDisputeOpenedFromPostEmail] = useState(false);
  // 2026-02-05 (user request) — NEW prompt fired AFTER the "mail off
  // through the program" step, offering a jump-back menu to any section
  // the user completed so they can double-check / edit before moving on.
  const [showPostMailReviewPrompt, setShowPostMailReviewPrompt] = useState(false);

  // Quick Payment Reminder Popup - shows AFTER credit repair steps email
  const [showQuickReminderPopup, setShowQuickReminderPopup] = useState(false);
  // 2026-02-05 (user request) — Dispute Packet Builder popup that fires
  // right after the mail modal closes.  Bundles FTC Report + Dispute
  // Letter + Sworn Statement + ID into ONE PDF that can be uploaded to
  // a bureau's website from a phone or desktop.
  const [showDisputePacketPrompt, setShowDisputePacketPrompt] = useState(false);
  // packetFiles.id is still an <input type="file"> because the ID lives
  // on the user's phone camera roll, but sworn/dispute/ftc are picked
  // from the in-session captured blobs (see checkbox UI below).
  const [packetFiles, setPacketFiles] = useState({ ftc: null, dispute: null, sworn: null, id: null });
  // Checkbox state — which in-session docs to bundle. Default ON when
  // we actually have that doc's blob captured. Recomputed via effect
  // when the packet prompt opens.
  const [packetIncludeSworn, setPacketIncludeSworn] = useState(true);
  const [packetIncludeDispute, setPacketIncludeDispute] = useState(true);
  const [packetIncludeFtc, setPacketIncludeFtc] = useState(true);
  const [packetFilename, setPacketFilename] = useState('');
  const [packetBuilding, setPacketBuilding] = useState(false);
  // 2026-02-06 — check whether the current client already has an ID on
  // file so the Packet Builder can offer it as a checkbox instead of
  // forcing a fresh upload every time.
  const [savedClientId, setSavedClientId] = useState(null); // {filename, exists}
  const [packetUseSavedId, setPacketUseSavedId] = useState(false);

  // Poll the client's saved documents when the packet prompt opens.
  useEffect(() => {
    if (!showDisputePacketPrompt || !clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/clients/${clientId}/documents`, { timeout: 10000 });
        if (!cancelled && data?.id_document?.exists) {
          setSavedClientId({ filename: data.id_document.filename });
          // Default: use the saved ID if we have one — user can still
          // uncheck & upload a fresh one instead.
          setPacketUseSavedId(true);
        }
      } catch (e) { /* client has no saved docs yet — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [showDisputePacketPrompt, clientId]);
  const [quickReminderForm, setQuickReminderForm] = useState({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
  const [savingQuickReminder, setSavingQuickReminder] = useState(false);

  // Mail Report popup — drag the just-generated FTC PDF (and any supporting docs)
  // into the drop zone, pick bureau(s), and physically mail via Lob.com.
  // Backend `/api/mail/send-uploaded-files` already prepends a blank cover page.
  const [showMailReportModal, setShowMailReportModal] = useState(false);
  // Copy & Fax prompt popup — sits between the Quick Reminder close and the
  // Mail Report popup, asks "do you need to convert any docs first?".
  // Per user request 2026-03-03.
  const [showCopyFaxPrompt, setShowCopyFaxPrompt] = useState(false);
  // Feb 2026 user request: after Copy & Fax, ask if the client's ID + SSN
  // card need conversion to portal-approved JPEGs BEFORE the Mail Report
  // modal opens.  Yes → opens /id-converter in a new tab (async polling
  // pipeline) and continues to Mail Report; Skip → straight to Mail Report.
  const [showIdConverterPrompt, setShowIdConverterPrompt] = useState(false);
  // 2026-02-08 — when the user chooses NOT to run the ID Converter, ask
  // whether they'd like to save the raw ID to the client's file so it
  // shows up as a checkbox in the Packet Builder later.
  const [showSaveIdPrompt, setShowSaveIdPrompt] = useState(false);
  const [saveIdFile, setSaveIdFile] = useState(null);
  const [savingIdToFile, setSavingIdToFile] = useState(false);
  // 2026-06-25 — Handwritten Letter Converter prompt + modal.  Per user
  // request: after the FTC report downloads, ask if the user wants to
  // convert any letters to handwritten form BEFORE the Copy & Fax prompt.
  const [showHandwritingPrompt, setShowHandwritingPrompt] = useState(false);
  const [showHandwritingModal, setShowHandwritingModal] = useState(false);
  // Feb 2026 — after dispute letters are downloaded from this FTC flow,
  // prompt to also generate the OFFICIAL bureau paper forms (TransUnion
  // Investigation Request + Equifax Dispute Request), identical to the
  // Dispute Wizard flow.  Editor state mirrors that page too.
  const [bureauFormsPromptOpen, setBureauFormsPromptOpen] = useState(false);
  const [bureauFormsBusy, setBureauFormsBusy] = useState(false);
  // Which bureau(s) the user picked in the redesigned prompt.  Default
  // to BOTH so the primary "Generate & Download" click Just Works.
  const [bureauFormsChoice, setBureauFormsChoice] = useState({
    id: 'both', label: 'Both', bureaus: ['transunion', 'equifax'],
    activeCls: 'bg-emerald-600/25 border-emerald-500 text-white',
  });
  const [bureauEditorOpen, setBureauEditorOpen] = useState(false);
  const [bureauEditorPdf, setBureauEditorPdf] = useState(null);
  const [bureauEditorLabel, setBureauEditorLabel] = useState("");
  const [bureauEditorFilename, setBureauEditorFilename] = useState("");
  const [bureauEditorQueue, setBureauEditorQueue] = useState([]);
  // Flag set by DisputeLetterModal's onDone: distinguishes "user
  // actually downloaded at least one letter" from "user opened the
  // modal and cancelled".  Consumed by the onClose handler below.
  //
  // Feb 2026 — MUST be a ref (not useState).  The modal calls
  // onDone(...) then IMMEDIATELY calls close() → onClose in the same
  // tick, so a useState setter would still read as `false` inside
  // proceedAfterDisputeLetters (stale-state bug — the reason the
  // bureau-forms prompt kept getting skipped even after we wired it
  // in).  A ref updates synchronously so onClose sees the true value.
  const disputeLettersJustDownloadedRef = useRef(false);

  // 2026-09-25 (user request): the Flow Overview list must be reachable
  // from ANY step of the flow, and after the user finishes a revisited
  // item the overview must reopen automatically so they can hop to
  // another step.  `pendingReturnToOverviewRef` is set to `true` when a
  // list item is clicked; the useEffect further below watches the
  // universe of relevant modal flags and, when they all go false again,
  // pops the overview back open.  Using a ref avoids extra re-renders.
  const pendingReturnToOverviewRef = useRef(false);
  const [showDoneChoiceModal, setShowDoneChoiceModal] = useState(false);

  // Feb 2026 preview harness: ?previewPostDispute=1 skips to step 3
  // then fires the exact post-dispute-letter close sequence.  Also
  // supports ?previewLetterTypes=1 which opens the DisputeLetterModal
  // directly so screenshots of the letter-type picker are easy to grab.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('previewLetterTypes') === '1') {
      // Slight delay so clientData / accounts hydrate first.
      const t = setTimeout(() => setShowDisputeLetterModal(true), 500);
      return () => clearTimeout(t);
    }
    if (qs.get('previewPostDispute') !== '1') return;
    // Wait one tick so clientData & other state hydrate first.
    const t = setTimeout(() => {
      disputeLettersJustDownloadedRef.current = true;
      proceedAfterDisputeLetters();
    }, 400);
    return () => clearTimeout(t);
  }, []);

  // Auto-return-to-overview watcher.  When ANY of the flow's revisitable
  // prompts/modals close AND the user came from the Flow Overview list,
  // pop the overview back open so they can hop to another step.
  useEffect(() => {
    if (!pendingReturnToOverviewRef.current) return;
    const anyOpen =
      showDisputePicker ||
      showDisputeLetterModal ||
      showCreditRepairEmailPopup ||
      showQuickReminderPopup ||
      showMailReportModal ||
      bureauFormsPromptOpen ||
      bureauEditorOpen ||
      showCopyFaxPrompt ||
      showIdConverterPrompt ||
      showHandwritingPrompt ||
      showDisputePacketPrompt;
    if (!anyOpen) {
      pendingReturnToOverviewRef.current = false;
      // Small delay so the closing modal fully unmounts first.
      const t = setTimeout(() => setShowPostMailReviewPrompt(true), 200);
      return () => clearTimeout(t);
    }
  });

  // Helper used by every Flow-Overview list item.  Closes the overview,
  // marks the "return-to-overview" intent, then runs the target action.
  const revisitFromOverview = (openFn) => {
    setShowPostMailReviewPrompt(false);
    pendingReturnToOverviewRef.current = true;
    // Defer so the overview close animation runs first, then open target.
    setTimeout(() => { try { openFn && openFn(); } catch (_) {} }, 100);
  };
  const [lastGeneratedFtcBlob, setLastGeneratedFtcBlob] = useState(null); // {blob, name}
  // Sworn Statement + Dispute Letter blobs captured from DisputeLetterModal
  // so the Dispute Packet Builder can offer them as checkboxes instead of
  // file uploads (per user request 2026-02-06 — mobile UX).
  const [lastGeneratedSwornBlob, setLastGeneratedSwornBlob] = useState(null); // {blob, name}
  const [lastGeneratedDisputeBlob, setLastGeneratedDisputeBlob] = useState(null); // {blob, name}
  const [mailReportDocs, setMailReportDocs] = useState([]); // [{file, name, size}]
  // 2026-06-09: user requested bureaus default OFF — they pick which to mail
  // each time. Default-ON was sending letters to all 3 by mistake.
  const [mailReportBureaus, setMailReportBureaus] = useState({ experian: false, equifax: false, transunion: false });
  const [mailReportClass, setMailReportClass] = useState('certified');
  const [mailReportSending, setMailReportSending] = useState(false);
  const [mailReportResults, setMailReportResults] = useState(null); // [{bureau, status, tracking_id, lob_url, error}]
  const [mailReportDragOver, setMailReportDragOver] = useState(false);

  // 2026-02-28 (user request) — when the Print Per Bureau modal opens,
  // auto-preload the docs we generated in this flow (FTC report, sworn
  // statement, dispute letter) as CHECKED entries.  Admin unchecks any
  // they don't want per-bureau and can still drop in extras like the
  // client's ID.  We only add entries not already present so re-opens
  // don't duplicate.
  useEffect(() => {
    if (!showMailReportModal) return;
    setMailReportDocs(prev => {
      const bySource = new Set(prev.map(d => d.source).filter(Boolean));
      const additions = [];
      const push = (blobEntry, source, label) => {
        if (!blobEntry || bySource.has(source)) return;
        const raw = blobEntry.file || blobEntry.blob;
        if (!raw) return;
        additions.push({
          file: raw,
          name: blobEntry.name || label,
          size: raw.size || 0,
          checked: true,
          system: true,
          source,
        });
      };
      push(lastGeneratedFtcBlob,     'ftc',     'FTC Identity Theft Report.pdf');
      push(lastGeneratedSwornBlob,   'sworn',   'Sworn Statement.pdf');
      push(lastGeneratedDisputeBlob, 'dispute', 'Dispute Letter.pdf');
      return additions.length ? [...prev, ...additions] : prev;
    });
  }, [showMailReportModal, lastGeneratedFtcBlob, lastGeneratedSwornBlob, lastGeneratedDisputeBlob]);

  // Portal Announcement popup — shows AFTER the mail modal closes (either
  // via Skip or after a successful send).  Lets the credit consultant
  // decide whether to send THIS client a separate email introducing the
  // Client Update Portal.  Skippable.
  const [showPortalPromptModal, setShowPortalPromptModal] = useState(false);
  const [sendingPortalAnnouncement, setSendingPortalAnnouncement] = useState(false);

  // Initialize date/time with current values on mount
  useEffect(() => {
    const now = new Date();
    const month = now.getMonth() + 1; // No padding - single digits stay single for FTC format
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const formattedDate = `${month}/${day}/${year}`;
    setReportDate(formattedDate);
    setDefaultDateDiscovered(`${month}/${year}`); // Default to current month/year (M/YYYY)
    
    let hours = now.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutes = String(now.getMinutes()).padStart(2, '0');
    setReportTime(`${hours}:${minutes} ${ampm}`);
  }, []);
  
  // AUTO-LOAD saved form data when component mounts
  useEffect(() => {
    const loadSavedData = async () => {
      if (!clientId) return;
      try {
        const response = await axios.get(`${API}/wizard-step-data/${clientId}/ftc_report`);
        if (response.data.success && response.data.form_data) {
          const saved = response.data.form_data;
          console.log('[FTC] Loading saved form data:', saved);
          
          // Restore sections if saved
          if (saved.sections) {
            // Convert accountIds arrays back to Sets
            const restoredSections = {};
            Object.keys(saved.sections).forEach(key => {
              restoredSections[key] = {
                ...saved.sections[key],
                accountIds: new Set(saved.sections[key].accountIds || [])
              };
            });
            setSections(restoredSections);
          }
          
          // Restore other settings
          if (saved.personalStatement) setPersonalStatement(saved.personalStatement);
          if (saved.selectedStatementVersion) setSelectedStatementVersion(saved.selectedStatementVersion);
          if (saved.dateDiscoveredPerAccount) setDateDiscoveredPerAccount(saved.dateDiscoveredPerAccount);
          if (saved.dateDiscoveredPerSection) setDateDiscoveredPerSection(saved.dateDiscoveredPerSection);
          if (saved.abbreviateAccounts !== undefined) setAbbreviateAccounts(saved.abbreviateAccounts);
          if (saved.reportNumber) setReportNumber(saved.reportNumber);
          
          toast.success('Restored previous FTC Report settings');
        }
      } catch (error) {
        console.log('[FTC] No saved data or error loading:', error.message);
      }
    };
    
    loadSavedData();
  }, [clientId]);
  
  // AUTO-SAVE form data when important fields change
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'
  
  useEffect(() => {
    const saveData = async () => {
      if (!clientId) return;
      
      setSaveStatus('saving');
      
      // Convert Sets to arrays for JSON serialization
      const sectionsToSave = {};
      Object.keys(sections).forEach(key => {
        sectionsToSave[key] = {
          ...sections[key],
          accountIds: Array.from(sections[key].accountIds || [])
        };
      });
      
      const formData = {
        sections: sectionsToSave,
        personalStatement,
        selectedStatementVersion,
        dateDiscoveredPerAccount,
        dateDiscoveredPerSection,
        abbreviateAccounts,
        reportNumber,
        // Also save any edited account names/values
        editedAccounts: accounts?.map(acc => ({
          id: acc.id,
          creditor_name: acc.creditor_name,
          display_name: acc.display_name,
          balance: acc.balance,
          account_number: acc.account_number
        })) || []
      };
      
      try {
        await axios.post(`${API}/wizard-step-data/${clientId}/ftc_report`, { form_data: formData });
        console.log('[FTC] Auto-saved form data');
        setSaveStatus('saved');
      } catch (error) {
        console.error('[FTC] Auto-save failed:', error.message);
        setSaveStatus('error');
      }
    };
    
    // Debounce save - wait 1 second after changes before saving
    const timeoutId = setTimeout(saveData, 1000);
    return () => clearTimeout(timeoutId);
  }, [clientId, sections, personalStatement, selectedStatementVersion, dateDiscoveredPerAccount, dateDiscoveredPerSection, abbreviateAccounts, reportNumber, accounts]);
  
  // Get date discovered for an account (defaults to current month/year)
  const getDateDiscoveredForAccount = (accountId) => {
    return dateDiscoveredPerAccount[accountId] || defaultDateDiscovered;
  };
  
  // Get date discovered for a section (all accounts in section share same date)
  const getDateDiscoveredForSection = (sectionNum) => {
    return dateDiscoveredPerSection[sectionNum] || defaultDateDiscovered;
  };
  
  // Update date discovered for a specific account
  const updateDateDiscoveredForAccount = (accountId, date) => {
    setDateDiscoveredPerAccount(prev => ({
      ...prev,
      [accountId]: date
    }));
  };
  
  // Update date discovered for a section
  const updateDateDiscoveredForSection = (sectionNum, date) => {
    setDateDiscoveredPerSection(prev => ({
      ...prev,
      [sectionNum]: date
    }));
  };
  
  // Update custom total amount for a section
  const updateSectionTotalAmount = (sectionNum, amount) => {
    setSections(prev => ({
      ...prev,
      [sectionNum]: { ...prev[sectionNum], customTotalAmount: amount }
    }));
  };
  
  // Calculate auto total for a section (sum of all account balances)
  const calculateSectionAutoTotal = (sectionNum) => {
    const sec = sections[sectionNum];
    if (!sec || sec.accountIds.size === 0) return 0;
    let total = 0;
    sec.accountIds.forEach(accountId => {
      const acc = accounts.find(a => a.id === accountId);
      if (acc) {
        total += parseFloat(acc.balance) || 0;
      }
    });
    return total;
  };
  
  // Get effective total for a section (custom if set, otherwise auto-calculated)
  const getSectionTotal = (sectionNum) => {
    const sec = sections[sectionNum];
    if (sec?.customTotalAmount && sec.customTotalAmount !== '') {
      return parseFloat(sec.customTotalAmount) || 0;
    }
    return calculateSectionAutoTotal(sectionNum);
  };
  
  // Update account details (name, amount, etc.)
  const updateAccountField = (accountId, field, value) => {
    setAccounts(prev => prev.map(acc => 
      acc.id === accountId ? { ...acc, [field]: value } : acc
    ));
  };

  // Manual entry state for custom accounts/inquiries
  const [manualEntry, setManualEntry] = useState({
    creditorName: '',
    accountNumber: '',
    dateOpened: '',
    dateFraudBegan: '',  // NEW: Date the fraud began (extracted from account name)
    dateDiscovered: '',  // NEW: Date fraud was discovered (defaults to current month/year)
    fraudAmount: '',
    status: 'FRAUDULENT',
    notes: ''
  });
  
  // Helper: Get current month/year in M/YYYY format (no leading zeros)
  const getCurrentMonthYear = () => {
    const now = new Date();
    const month = now.getMonth() + 1; // No padding
    const year = now.getFullYear();
    return `${month}/${year}`;
  };
  
  // Helper: Extract date from account name string and convert to M/YYYY format
  const extractDateFromAccountName = (accountName) => {
    if (!accountName) return '';
    
    // Try full date first: MM/DD/YYYY or MM-DD-YYYY
    const fullMatch = accountName.match(/(\d{1,2})[-\/](\d{1,2})[-\/](\d{2,4})/);
    if (fullMatch) {
      const month = parseInt(fullMatch[1], 10);
      let year = fullMatch[3];
      if (year.length === 2) year = '20' + year;
      return `${month}/${year}`;
    }
    
    // Try short date: MM/YYYY or MM-YYYY (year must be 4 digits)
    const shortMatch = accountName.match(/(\d{1,2})[-\/](\d{4})/);
    if (shortMatch) {
      const month = parseInt(shortMatch[1], 10);
      return `${month}/${shortMatch[2]}`;
    }
    
    return '';
  };

  // Rewriting state — toggled while the LLM is generating a unique
  // paraphrase for the chosen personal-statement version.
  const [rewritingStatement, setRewritingStatement] = useState(false);

  // ────────────────── Saved Dispute Letters (Feb 2026) ──────────────────
  // Load the list of previously-saved dispute letters for this client so
  // the user can reopen one and just tweak the font/date.  Keyed on
  // clientId (preferred) or clientData.name as a fallback for legacy rows
  // that predate client_id being passed to the modal.
  const refreshSavedDisputeLetters = React.useCallback(async () => {
    if (!clientId && !clientData?.name) return;
    try {
      setSavedLettersLoading(true);
      const params = clientId
        ? { client_id: clientId }
        : { client_name: clientData?.name };
      const res = await axios.get(`${API}/api/saved-dispute-letters`, {
        params,
        timeout: 15000,
      });
      setSavedDisputeLetters(res.data?.items || []);
    } catch (e) {
      console.warn('[Saved Dispute Letters] load failed:', e);
    } finally {
      setSavedLettersLoading(false);
    }
  }, [clientId, clientData?.name]);

  useEffect(() => {
    refreshSavedDisputeLetters();
  }, [refreshSavedDisputeLetters]);

  const deleteSavedLetter = async (letterId) => {
    if (!window.confirm('Delete this saved dispute letter? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/saved-dispute-letters/${letterId}`);
      toast.success('Saved letter removed');
      setSavedDisputeLetters((prev) => prev.filter((l) => l.id !== letterId));
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const openQuickEdit = (letter) => {
    setQuickEditLetter(letter);
    setQuickEditFont(letter.body_font || 'times');
    setQuickEditDate(letter.letter_date || '');
  };

  const openEditFullLetter = (letter) => {
    setActiveSavedLetter(letter);
    setShowDisputeLetterModal(true);
  };

  const runQuickEditRegenerate = async () => {
    if (!quickEditLetter) return;
    setQuickEditGenerating(true);
    try {
      // 1) Persist the new font/date to the saved letter.
      await axios.patch(
        `${API}/api/saved-dispute-letters/${quickEditLetter.id}`,
        {
          body_font: quickEditFont,
          letter_date: quickEditDate || null,
        },
        { timeout: 15000 },
      );
      // 2) Re-generate the PDF using the full saved state + new font/date.
      const genRes = await axios.post(
        `${API}/api/dispute-letter/generate`,
        {
          client_name: quickEditLetter.client_name,
          client_address_lines: quickEditLetter.client_address_lines || [],
          bureau: quickEditLetter.bureau,
          accounts: quickEditLetter.accounts || [],
          letter_type: quickEditLetter.letter_type || 'regular',
          subject: quickEditLetter.subject || null,
          pre_body: quickEditLetter.pre_body || null,
          post_body: quickEditLetter.post_body || null,
          signature_font: quickEditLetter.signature_font || 'cursive_elegant',
          body_font: quickEditFont,
          style: quickEditLetter.style || 'typed',
          handwriting_style: quickEditLetter.handwriting_style || null,
          handwriting_finish: quickEditLetter.handwriting_finish || null,
          letter_date: quickEditDate || null,
          include_sworn_statement: false,
        },
        { responseType: 'blob', timeout: 240000 },
      );
      const cd = genRes.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match?.[1] || `dispute_letter_${quickEditLetter.bureau}.pdf`;
      if (!(await previewPdf(genRes.data, filename))) return;
      // 2026-02-28 iPhone bug fix — mobile-safe download (Web Share
      // on iOS keeps the SPA state alive).
      await downloadBlobMobileSafe(genRes.data, filename, {
        shareTitle: `Dispute Letter — ${quickEditLetter.bureau}`,
      });
      toast.success(`Re-generated — ${filename}`);
      await refreshSavedDisputeLetters();
      setQuickEditLetter(null);
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || text;
        } catch { /* ignore */ }
      }
      toast.error(`Quick edit failed: ${msg}`);
    } finally {
      setQuickEditGenerating(false);
    }
  };

  // Update statement when version changes.  Feb 2026 — we now ALSO fire
  // the LLM rewriter automatically so each client gets a unique
  // paraphrase (same facts, different wording) instead of the exact
  // shared template.  On any LLM failure we fall back to the source
  // template so the UI is never blank.
  const rewriteStatementForVersion = async (versionId, baseText) => {
    try {
      setRewritingStatement(true);
      const resp = await axios.post(`${API}/api/ftc-report/personal-statement/regenerate`, {
        template: baseText,
        version_id: versionId,
        client_name: (clientData && (clientData.name || clientData.full_name)) || null,
      });
      if (resp?.data?.text) {
        setPersonalStatement(resp.data.text);
        if (resp.data.regenerated) {
          toast.success('Unique statement generated — same facts, new wording');
        }
      }
    } catch (e) {
      // Silent fallback — the textarea already shows the base template.
      console.warn('personal-statement rewrite failed', e);
    } finally {
      setRewritingStatement(false);
    }
  };

  const handleStatementVersionChange = (version) => {
    setSelectedStatementVersion(version);
    const selected = STATEMENT_OPTIONS.find(s => s.id === version);
    if (selected) {
      setPersonalStatement(selected.text);
      // Fire the LLM rewriter in the background — the textarea updates
      // in place once the unique version is returned.
      rewriteStatementForVersion(version, selected.text);
    }
  };

  // Load accounts from API or use preloaded accounts
  // Handle both preloadedAccounts (from ClientDetail paste) and selectedAccounts (from Dispute Wizard)
  const accountsToUse = preloadedAccounts?.length > 0 ? preloadedAccounts : (selectedAccounts?.length > 0 ? selectedAccounts : []);
  
  useEffect(() => { 
    console.log('FTCReportBuilderNew: useEffect triggered');
    console.log('FTCReportBuilderNew: preloadedAccounts count:', preloadedAccounts?.length);
    console.log('FTCReportBuilderNew: preloadedAccounts IDs:', preloadedAccounts?.map(a => a.id));
    console.log('FTCReportBuilderNew: accountsToUse count:', accountsToUse?.length);
    console.log('FTCReportBuilderNew: accountsLoadedFromProps:', accountsLoadedFromProps);
    
    // CRITICAL: If accounts were already loaded from props, don't reset them
    if (accountsLoadedFromProps) {
      console.log('FTCReportBuilderNew: Skipping - accounts already loaded');
      return;
    }
    
    if (accountsToUse && accountsToUse.length > 0) {
      console.log('FTCReportBuilderNew: Processing', accountsToUse.length, 'accounts');
      
      // Use preloaded accounts (from paste in standalone FTC builder or ClientDetail)
      // Also extract dateFraudBegan from parsed accounts for auto-population
      const newDateDiscoveredPerAccount = {};
      
      const formattedAccounts = accountsToUse.map((acc, index) => {
        console.log('FTCReportBuilderNew: Mapping account', index, ':', acc);
        
        // Parse the fraud amount - handle multiple field names from different sources:
        // - fraudAmount: from FTCBuilderPage parsing
        // - balance: from some parsers
        // - balance_owed: from database accounts (Dispute Wizard)
        // - current_balance: alternative database field
        let parsedBalance = 0;
        const fraudAmt = acc.fraudAmount || acc.balance || acc.balance_owed || acc.current_balance || '0';
        if (fraudAmt) {
          // Remove $ and commas, then parse as float
          const cleanAmt = String(fraudAmt).replace(/[$,]/g, '');
          parsedBalance = parseFloat(cleanAmt) || 0;
        }
        console.log('FTCReportBuilderNew: Parsed balance:', parsedBalance, 'from', fraudAmt);
        
        // Use existing ID or generate new one (handle both parsed accounts and database accounts)
        const accountId = acc.id || acc._id || acc.account_log_id || `preloaded_${Date.now()}_${index}`;
        
        // AUTO-POPULATE: If dateFraudBegan exists from parsing, use it
        // For database accounts, use date_opened as the date fraud began
        const extractedFraudDate = acc.dateFraudBegan || acc.date_fraud_began || acc.dateOpened || acc.date_opened || acc.open_date || '';
        console.log('FTCReportBuilderNew: Extracted fraud date:', extractedFraudDate);
        let formattedFraudDate = '';
        
        if (extractedFraudDate) {
          // Canonical normalizer — produces:
          //   full_date  → "MM-DD-YYYY"   (e.g. 08-15-2026)
          //   month_year → "MM-YYYY"      (e.g. 08-2026)
          // Accepts any of: M-D-YYYY, MM-DD-YYYY, M/D/YYYY, MM/D/YYYY, MM-YYYY, M/YYYY, YYYY-MM-DD
          const cleaned = String(extractedFraudDate).replace(/[\/.]/g, '-').replace(/\s+/g, '').trim();
          const pad2 = (n) => String(parseInt(n, 10)).padStart(2, '0');
          let parsedMonth = null, parsedDay = null, parsedYear = null;
          let m = cleaned.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
          if (m) { parsedMonth = pad2(m[1]); parsedDay = pad2(m[2]); parsedYear = m[3]; }
          else {
            m = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
            if (m) { parsedYear = m[1]; parsedMonth = pad2(m[2]); parsedDay = pad2(m[3]); }
            else {
              m = cleaned.match(/^(\d{1,2})-(\d{4})$/);
              if (m) { parsedMonth = pad2(m[1]); parsedYear = m[2]; parsedDay = null; }
            }
          }
          if (parsedMonth && parsedYear) {
            if (dateFormat === 'full_date' && parsedDay) {
              formattedFraudDate = `${parsedMonth}-${parsedDay}-${parsedYear}`;
            } else {
              formattedFraudDate = `${parsedMonth}-${parsedYear}`;
            }
          } else {
            formattedFraudDate = extractedFraudDate;
          }
          newDateDiscoveredPerAccount[accountId] = formattedFraudDate;
        }
        
        // Use the ORIGINAL creditor name (without the parsed date suffix) so we
        // don't double-stamp the date. The parser stores the unmodified name in
        // `originalName`; fall back to the display name only if the original
        // is missing. As a safety net, strip any trailing " - MM-YYYY" or
        // " - MM-DD-YYYY" pattern from whatever we end up with.
        let creditorName = (acc.originalName || acc.creditor_name || acc.creditor || acc.name || acc.creditorName || '').replace(/\//g, '-');
        // STRONGER cleanup: if creditorName looks like the legacy comma format
        // (e.g. "NAVY FED, , $1205.00, 02/12/2018"), strip everything from the
        // first comma onward and treat that as the bare creditor name.
        if (creditorName.includes(',')) {
          creditorName = creditorName.split(',')[0].trim();
        }
        // Iteratively strip trailing date and trailing "$amount" tokens until
        // stable, so we peel BOTH suffixes off in any order. Previously we only
        // ran each regex once, so an input like "CAPITAL ONE - 3-2021 - $500"
        // would keep the date and produce a duplicated-date display string.
        // Also handles bare (no-dash) trailing dates like "CAPITAL ONE 3-2021".
        for (let _pass = 0; _pass < 5; _pass++) {
          const before = creditorName;
          creditorName = creditorName
            .replace(/\s*[-–]?\s*\$[\d,]+(?:\.\d{1,2})?\s*$/, '')           // trailing $amount
            .replace(/\s*[-–]?\s*\d{1,2}(-\d{1,2})?-\d{4}\s*$/, '')          // trailing MM-YYYY or MM-DD-YYYY
            .trim();
          if (creditorName === before) break;
        }
        console.log('FTCReportBuilderNew: Creditor name:', creditorName);
        
        const amountStr = parsedBalance > 0 ? `$${parsedBalance}` : '';
        // Convert date format from M/YYYY to M-YYYY for display name (no slashes in account name)
        // Use global replace so full dates like "3/21/2026" become "3-21-2026" (not "3-21/2026")
        const displayDate = formattedFraudDate ? formattedFraudDate.replace(/\//g, '-') : '';
        // ALWAYS rebuild the canonical display string. Previously we honored
        // any pre-existing acc.display_name verbatim, which meant legacy
        // saved reports kept their old "NAME, , $AMT, DATE" formatting. Per
        // user request 2026-06-05: every account in the picker must read
        //   ACCOUNT NAME - MM-YYYY - $BALANCE
        // (no commas, no slashes, no double dashes).
        const fullDisplayName = `${creditorName}${displayDate ? ` - ${displayDate}` : ''}${amountStr ? ` - ${amountStr}` : ''}`;
        
        // Ensure display name has NO slashes - replace all / with -
        // Also remove commas from dollar amounts
        // FORCE strip day from dates when month_year mode (MM-DD-YYYY → MM-YYYY)
        let cleanDisplayName = fullDisplayName
          .replace(/\//g, '-')
          .replace(/\$[\d,]+/g, (match) => match.replace(/,/g, ''));
        
        // Zero-pad ANY M-YYYY / MM-YYYY / M-D-YYYY / MM-DD-YYYY in the display name,
        // and force the day to be present (full_date) or stripped (month_year) consistently.
        // This ensures output is ALWAYS exactly "MM-YYYY" or "MM-DD-YYYY" regardless of paste.
        if (dateFormat !== 'full_date') {
          // Strip day from ANY M-D-YYYY pattern AND zero-pad the month
          cleanDisplayName = cleanDisplayName.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/g, (match, p1, p2, p3) => {
            if (parseInt(p3) >= 1990 && parseInt(p3) <= 2099) {
              return `${String(parseInt(p1, 10)).padStart(2, '0')}-${p3}`;
            }
            return match;
          });
          // Zero-pad any remaining M-YYYY → MM-YYYY
          cleanDisplayName = cleanDisplayName.replace(/(?<![\d-])(\d{1,2})-(\d{4})(?!\d)/g, (match, p1, p2) => {
            if (parseInt(p2) >= 1990 && parseInt(p2) <= 2099) {
              return `${String(parseInt(p1, 10)).padStart(2, '0')}-${p2}`;
            }
            return match;
          });
        } else {
          // full_date mode → zero-pad month and day in MM-DD-YYYY
          cleanDisplayName = cleanDisplayName.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/g, (match, p1, p2, p3) => {
            if (parseInt(p3) >= 1990 && parseInt(p3) <= 2099) {
              return `${String(parseInt(p1, 10)).padStart(2, '0')}-${String(parseInt(p2, 10)).padStart(2, '0')}-${p3}`;
            }
            return match;
          });
        }
        
        console.log('FTCReportBuilderNew: Final account:', {
          id: accountId,
          creditor_name: creditorName,
          display_name: cleanDisplayName,
          balance: parsedBalance,
          date_fraud_began: formattedFraudDate
        });
        
        return {
          id: accountId,
          creditor_name: creditorName,
          // Full display name: "Name -M-YYYY $Amount" (no slashes in account name)
          display_name: cleanDisplayName,
          account_number: acc.accountNumber || acc.account_number || '',
          balance: parsedBalance,
          date_opened: acc.dateOpened || acc.date_opened || acc.open_date || '',
          // Date fraud began - extracted from account (e.g., 9/2024) - slashes OK here
          date_fraud_began: formattedFraudDate,
          // Date discovered - ALWAYS defaults to current month/year when report is created
          date_discovered: defaultDateDiscovered,
          account_type: acc.account_type || acc.accountType || acc.category || '',
          // Also preserve `category` directly so the Quick Reference
          // sidebar (which reads acc.category before falling back to
          // account_type) keeps the section the user picked in the AI
          // Extractor / Parsed Accounts view (Feb 2026 user fix — was
          // dropping the category and every row rendered as OTHER).
          category: acc.category || acc.account_type || acc.accountType || '',
          status: 'FRAUDULENT'
        };
      });
      
      console.log('FTCReportBuilderNew: Setting accounts:', formattedAccounts.length, 'accounts');
      setAccounts(formattedAccounts);
      setAccountsLoadedFromProps(true); // Mark that we've loaded accounts from props
      
      // Auto-populate dateDiscoveredPerAccount with extracted dates
      if (Object.keys(newDateDiscoveredPerAccount).length > 0) {
        setDateDiscoveredPerAccount(prev => ({
          ...prev,
          ...newDateDiscoveredPerAccount
        }));
        console.log('FTCReportBuilderNew: Auto-populated dates from parsed accounts:', newDateDiscoveredPerAccount);
      }
      
      // DO NOT auto-assign accounts to sections - let user manually place them
      // EXCEPT when re-loading a saved report — then we restore the user's
      // original section assignments + fraud types + custom totals so they
      // don't have to re-categorize everything.
      if (preloadedSections && typeof preloadedSections === 'object' && Object.keys(preloadedSections).length > 0) {
        const restoredSections = {
          1: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          2: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          3: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          4: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          5: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
        };
        Object.entries(preloadedSections).forEach(([num, sec]) => {
          const key = String(num);
          if (!restoredSections[key]) return;
          restoredSections[key].fraudType = sec.fraudType || sec.fraud_type || null;
          restoredSections[key].customTotalAmount = sec.customTotalAmount || sec.custom_total_amount || '';
          restoredSections[key].dateDiscovered = sec.dateDiscovered || sec.date_discovered || '';
          const ids = Array.isArray(sec.accounts) ? sec.accounts.map(a => a.id) : (sec.accountIds || []);
          ids.forEach(id => { if (id) restoredSections[key].accountIds.add(id); });
        });
        setSections(restoredSections);
        console.log('FTCReportBuilderNew: Restored sections from saved report', restoredSections);
      } else {
        // Fresh build — accounts available in the pool, user assigns manually
        const newSections = {
          1: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          2: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          3: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          4: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
          5: { accountIds: new Set(), fraudType: null, customTotalAmount: '', dateDiscovered: '' },
        };
        setSections(newSections);
        console.log('FTCReportBuilderNew: Accounts loaded - awaiting manual section assignment');
      }
      
      setLoading(false);
    } else if (clientId && !accountsLoadedFromProps) {
      loadAccounts();
    } else if (!accountsLoadedFromProps) {
      setAccounts([]);
      setLoading(false);
    }
  }, [clientId, JSON.stringify(preloadedAccounts), JSON.stringify(selectedAccounts)]);

  // ============ AUTO-SAVE & AUTO-LOAD FTC CONFIG ============
  // Load saved FTC config when component mounts (for data persistence)
  useEffect(() => {
    const loadSavedConfig = async () => {
      if (!clientId || accountsToUse?.length > 0) return; // Skip if preloaded or no client
      
      try {
        const response = await axios.get(`${API}/api/ftc-builder/${clientId}/config`);
        const config = response.data;
        
        if (config && config.sections) {
          console.log('[FTC] Loading saved sections:', config.sections);
          // Convert saved sections back to state format (accountIds as Set)
          const loadedSections = {};
          Object.entries(config.sections).forEach(([key, sec]) => {
            loadedSections[key] = {
              fraudType: sec.fraudType || null,
              accountIds: new Set(sec.accountIds || []),
              customTotalAmount: sec.customTotalAmount || '',
              dateDiscovered: sec.dateDiscovered || ''
            };
          });
          setSections(prev => ({ ...prev, ...loadedSections }));
          
          // Load dates discovered per section
          if (config.dateDiscoveredPerSection) {
            setDateDiscoveredPerSection(config.dateDiscoveredPerSection);
          }
          
          // Load personal statement selection
          if (config.selectedStatementVersion) {
            setSelectedStatementVersion(config.selectedStatementVersion);
            setPersonalStatement(STATEMENT_OPTIONS[config.selectedStatementVersion - 1]?.text || STATEMENT_OPTIONS[0].text);
          }
          
          toast.success('FTC report data restored');
        }
      } catch (error) {
        console.log('[FTC] No saved config found or error loading:', error.message);
      }
    };
    
    loadSavedConfig();
  }, [clientId]);
  
  // Auto-save FTC config when sections change (debounced)
  useEffect(() => {
    if (!clientId || accountsToUse?.length > 0) return; // Skip if preloaded or no client
    
    // Check if there's any meaningful data to save
    const hasData = Object.values(sections).some(sec => sec.fraudType || sec.accountIds.size > 0);
    if (!hasData) return;
    
    const saveTimeout = setTimeout(async () => {
      try {
        // Convert sections to saveable format (Sets to arrays)
        const sectionsToSave = {};
        Object.entries(sections).forEach(([key, sec]) => {
          sectionsToSave[key] = {
            fraudType: sec.fraudType,
            accountIds: Array.from(sec.accountIds || []),
            customTotalAmount: sec.customTotalAmount || '',
            dateDiscovered: sec.dateDiscovered || ''
          };
        });
        
        await axios.post(`${API}/api/ftc-builder/save-full-config`, {
          client_id: clientId,
          sections: sectionsToSave,
          dateDiscoveredPerSection,
          selectedStatementVersion,
          personalStatement
        });
        
        console.log('[FTC] Auto-saved config');
      } catch (error) {
        console.error('[FTC] Auto-save error:', error.message);
      }
    }, 1500); // 1.5 second debounce
    
    return () => clearTimeout(saveTimeout);
  }, [clientId, sections, dateDiscoveredPerSection, selectedStatementVersion]);

  // Fetch next FTC report number suggestion when component loads
  useEffect(() => {
    const fetchNextReportNumber = async () => {
      try {
        setLoadingReportNumber(true);
        const response = await axios.get(`${API}/api/ftc-affidavit/next-report-number`);
        const data = response.data;
        
        if (data.is_first_time) {
          // First time - no suggestion, user must enter manually
          setIsFirstTimeReport(true);
          setSuggestedReportNumber(null);
          setReportNumber('');
        } else {
          // We have a suggestion
          setIsFirstTimeReport(false);
          setSuggestedReportNumber(data.next_report_number);
          setReportNumber(data.next_report_number || '');
        }
      } catch (error) {
        console.error('Failed to fetch next report number:', error);
        setIsFirstTimeReport(true);
        setSuggestedReportNumber(null);
        setReportNumber('');
      } finally {
        setLoadingReportNumber(false);
      }
    };
    fetchNextReportNumber();
  }, []);

  // Function to save the report number after generating
  const saveReportNumber = async (number) => {
    try {
      await axios.post(`${API}/api/ftc-affidavit/save-report-number`, {
        report_number: number
      });
      console.log('Saved FTC report number:', number);
    } catch (error) {
      console.error('Failed to save report number:', error);
    }
  };

  // Handle opening the report number modal before generating
  const handleGenerateClick = (actionType) => {
    setPendingGenerateAction(actionType);
    setShowReportNumberModal(true);
  };

  // Handle confirming the report number and proceeding with generation
  const handleConfirmReportNumber = async () => {
    if (!reportNumber.trim()) {
      toast.error('Please enter an FTC Report Number');
      return;
    }
    
    setShowReportNumberModal(false);
    
    // Save the report number for next time (generate saves it only after the double-check passes)
    if (pendingGenerateAction !== 'generate') await saveReportNumber(reportNumber);
    
    // Proceed with the pending action
    if (pendingGenerateAction === 'preview') {
      generatePreview();
    } else if (pendingGenerateAction === 'generate') {
      generateReport();
    }
    
    setPendingGenerateAction(null);
  };


  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/accounts?client_id=${clientId}&dedupe=false`);
      setAccounts(response.data || []);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  // Get all assigned account IDs
  const getAllAssignedIds = () => {
    const assigned = new Set();
    Object.values(sections).forEach(sec => {
      sec.accountIds.forEach(id => assigned.add(id));
    });
    return assigned;
  };

  // Get unassigned accounts
  const getUnassignedAccounts = () => {
    const assigned = getAllAssignedIds();
    const unassigned = accounts.filter(a => !assigned.has(a.id));
    console.log('getUnassignedAccounts: Total accounts:', accounts.length, 'Assigned:', assigned.size, 'Unassigned:', unassigned.length);
    console.log('getUnassignedAccounts: Account IDs:', accounts.map(a => a.id));
    console.log('getUnassignedAccounts: Unassigned accounts:', unassigned.map(a => `${a.display_name} (${a.id})`));
    return unassigned;
  };

  // Per user request 2026-02-27: on the FTC builder's account picker,
  // accounts should be visually grouped by category (Auto Loan, Credit
  // Card, Personal Loan, etc.) with a bold heading above each group —
  // exactly the way ASB's post-extraction summary lays them out.  This
  // matches the mental model of "where does this account belong in the
  // FTC report" one-to-one.
  const _FTC_CATEGORY_LABELS = {
    AUTO: 'Auto Loan',
    CREDIT_CARD: 'Credit Card',
    CREDIT_UNION: 'Credit Union',
    PERSONAL_LOAN: 'Personal Loan',
    STUDENT_LOAN: 'Student Loan',
    MORTGAGE: 'Mortgage',
    APARTMENT_LEASE: 'Apartment Lease',
    RENTAL_LEASE: 'Rental Lease',
    UTILITY: 'Utility',
    MEDICAL: 'Medical',
    COLLECTION: 'Collection',
    OTHER: 'Other',
  };
  const _FTC_CATEGORY_ORDER = [
    'AUTO', 'CREDIT_CARD', 'CREDIT_UNION', 'PERSONAL_LOAN',
    'STUDENT_LOAN', 'MORTGAGE', 'APARTMENT_LEASE', 'RENTAL_LEASE',
    'UTILITY', 'MEDICAL', 'COLLECTION', 'OTHER',
  ];
  const _ftcNormalizeAccountCategory = (acc) => {
    const raw = (acc.account_type || acc.accountType || acc.category || '').toString().trim();
    if (!raw) return 'OTHER';
    const upper = raw.toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_');
    if (_FTC_CATEGORY_LABELS[upper] !== undefined) return upper;
    const aliases = {
      RETAIL_CARD: 'CREDIT_CARD',
      AUTO_LOAN: 'AUTO', AUTOMOTIVE: 'AUTO', REPOSSESSION: 'AUTO',
      PERSONAL: 'PERSONAL_LOAN', INSTALLMENT_LOAN: 'PERSONAL_LOAN',
      STUDENT: 'STUDENT_LOAN', EDUCATION: 'STUDENT_LOAN',
      MED: 'MEDICAL', HOSPITAL: 'MEDICAL',
      UTIL: 'UTILITY', TELECOM: 'UTILITY', PHONE: 'UTILITY',
      COLLECTIONS: 'COLLECTION', CHARGE_OFF: 'COLLECTION',
      MORT: 'MORTGAGE',
      // 2026-02-24 (user request) — Apartment / Rental lease aliases.
      APARTMENT: 'APARTMENT_LEASE', LEASE: 'APARTMENT_LEASE',
      RENTAL: 'RENTAL_LEASE', RENT: 'RENTAL_LEASE',
    };
    return aliases[upper] || 'OTHER';
  };
  const getUnassignedAccountsGrouped = () => {
    const list = getUnassignedAccounts();
    const buckets = {};
    for (const acc of list) {
      const cat = _ftcNormalizeAccountCategory(acc);
      (buckets[cat] = buckets[cat] || []).push(acc);
    }
    const out = [];
    const seen = new Set();
    for (const cat of _FTC_CATEGORY_ORDER) {
      if (buckets[cat] && buckets[cat].length) {
        out.push({ category: cat, label: _FTC_CATEGORY_LABELS[cat], items: buckets[cat] });
        seen.add(cat);
      }
    }
    for (const [cat, items] of Object.entries(buckets)) {
      if (!seen.has(cat) && items.length) {
        out.push({ category: cat, label: _FTC_CATEGORY_LABELS[cat] || cat, items });
      }
    }
    return out;
  };

  // Render-time normalizer — ALWAYS produces canonical
  //   "ACCOUNT NAME - MM-YYYY - $BALANCE"   (or MM-DD-YYYY in full_date mode)
  // by stripping legacy comma formatting, slashes, and dollar duplicates that
  // may have leaked in from older saved reports or paste paths. Use this
  // anywhere we render an account's display name in the picker.
  const canonicalAccountLabel = (acc) => {
    let name = (acc.display_name || acc.creditor_name || acc.creditorName || '').trim();
    // If the display_name is the legacy CSV form ("NAVY FED, , $1205.00,
    // 02/12/2018"), prefer the bare creditor_name as the basis.
    if (name.includes(',')) {
      const bare = (acc.creditor_name || acc.creditorName || name.split(',')[0]).split(',')[0].trim();
      // Pull out amount + date from the CSV pieces so we don't lose data.
      let amt = '';
      let date = '';
      for (const part of name.split(',').map(s => s.trim())) {
        if (!amt) {
          const am = part.match(/\$?([\d,]+(?:\.\d{1,2})?)/);
          if (am && /\d/.test(am[1])) amt = am[1].replace(/,/g, '').replace(/\.\d+$/, '');
        }
        if (!date) {
          const dm = part.match(/(\d{1,2})[\/\-](?:(\d{1,2})[\/\-])?(\d{4})/);
          if (dm) date = `${dm[1].padStart(2, '0')}-${dm[3]}`;
        }
      }
      name = bare;
      if (date && !name.includes(date)) name += ` - ${date}`;
      if (amt && !name.includes('$')) name += ` - $${amt}`;
    }
    // Slashes → dashes, collapse whitespace, kill empty segments like " ,  "
    name = name.replace(/\//g, '-').replace(/\s+/g, ' ').replace(/\s*-\s*-\s*/g, ' - ').trim();
    // Strip trailing comma artifacts
    name = name.replace(/[,]+$/, '').trim();
    // Force "$amount" to have no thousands commas
    name = name.replace(/\$([\d,]+)/g, (_, n) => `$${n.replace(/,/g, '')}`);
    return name;
  };

  // Calculate GRAND TOTAL fraud amount (uses section totals)
  const getTotalFraudAmount = () => {
    let total = 0;
    [1, 2, 3, 4, 5].forEach(num => {
      const sec = sections[num];
      if (sec && sec.accountIds.size > 0) {
        total += getSectionTotal(num);
      }
    });
    return total;
  };

  // Get total accounts assigned
  const getTotalAccountsAssigned = () => {
    let count = 0;
    Object.values(sections).forEach(sec => {
      count += sec.accountIds.size;
    });
    return count;
  };

  // Toggle temp selection for multi-select
  const toggleTempSelect = (accountId) => {
    setTempSelected(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  // Select all unassigned
  const selectAllUnassigned = () => {
    setTempSelected(new Set(getUnassignedAccounts().map(a => a.id)));
  };

  // Clear temp selection
  const clearTempSelection = () => {
    setTempSelected(new Set());
  };

  // Helper function to parse M/YYYY date string to comparable value
  const parseDateToComparable = (dateStr) => {
    if (!dateStr) return null;
    const match = dateStr.match(/^(\d{1,2})\/(\d{4})$/);
    if (match) {
      const month = parseInt(match[1], 10);
      const year = parseInt(match[2], 10);
      return year * 12 + month; // Convert to months for easy comparison
    }
    return null;
  };

  // Helper function to find the oldest date from a set of account IDs
  const findOldestDateInSection = (accountIds) => {
    let oldestDate = null;
    let oldestValue = Infinity;
    
    accountIds.forEach(id => {
      const dateStr = dateDiscoveredPerAccount[id];
      const value = parseDateToComparable(dateStr);
      if (value !== null && value < oldestValue) {
        oldestValue = value;
        oldestDate = dateStr;
      }
    });
    
    return oldestDate;
  };

  // Add selected accounts to current section
  const addSelectedToSection = () => {
    if (tempSelected.size === 0) {
      toast.error('Please select at least one account');
      return;
    }
    
    // Get all account IDs that will be in this section after adding
    const allAccountIds = new Set(sections[selectedSection].accountIds);
    tempSelected.forEach(id => allAccountIds.add(id));
    
    // Find the oldest date from all accounts in this section
    const oldestDate = findOldestDateInSection(allAccountIds);
    
    setSections(prev => {
      const next = { ...prev };
      const targetSet = new Set(next[selectedSection].accountIds);
      tempSelected.forEach(id => targetSet.add(id));
      next[selectedSection] = { 
        ...next[selectedSection], 
        fraudType: selectedFraudType,
        accountIds: targetSet 
      };
      return next;
    });
    
    // Auto-set the section's date to the oldest date from all accounts
    if (oldestDate) {
      setDateDiscoveredPerSection(prev => ({
        ...prev,
        [selectedSection]: oldestDate
      }));
    }
    
    const count = tempSelected.size;
    setTempSelected(new Set());
    toast.success(`Added ${count} account(s) to Section ${selectedSection}${oldestDate ? ` - Date set to ${oldestDate} (oldest)` : ''}`);
  };

  // Remove account from section
  const removeFromSection = (sectionNum, accountId) => {
    // Calculate remaining accounts after removal
    const remainingAccountIds = new Set(sections[sectionNum].accountIds);
    remainingAccountIds.delete(accountId);
    
    setSections(prev => {
      const next = { ...prev };
      const set = new Set(next[sectionNum].accountIds);
      set.delete(accountId);
      next[sectionNum] = { ...next[sectionNum], accountIds: set };
      return next;
    });
    
    // Recalculate oldest date for remaining accounts
    if (remainingAccountIds.size > 0) {
      const newOldestDate = findOldestDateInSection(remainingAccountIds);
      if (newOldestDate) {
        setDateDiscoveredPerSection(prev => ({
          ...prev,
          [sectionNum]: newOldestDate
        }));
      }
    } else {
      // No accounts left, clear the section date
      setDateDiscoveredPerSection(prev => {
        const next = { ...prev };
        delete next[sectionNum];
        return next;
      });
    }
    
    toast.success('Account removed from section');
  };

  // Clear entire section
  const clearSection = (sectionNum) => {
    if (lockedSections.includes(Number(sectionNum))) { toast.info(`Section ${sectionNum} holds the old accounts and is locked`); return; }
    setSections(prev => ({
      ...prev,
      [sectionNum]: { fraudType: null, accountIds: new Set() }
    }));
    // Also clear the section date
    setDateDiscoveredPerSection(prev => {
      const next = { ...prev };
      delete next[sectionNum];
      return next;
    });
    toast.success(`Section ${sectionNum} cleared`);
  };

  // Navigation: Go to section selection
  const goToSectionList = () => {
    setCurrentTab('section');
    setSelectedSection(null);
    setSelectedFraudType(null);
    setTempSelected(new Set());
  };

  // Navigation: Select a section (new or edit)
  const selectSection = (num) => {
    if (lockedSections.includes(Number(num))) { toast.info(`Section ${num} holds the old accounts and is locked — pick an open section`); return; }
    setSelectedSection(num);
    setSelectedFraudType(sections[num].fraudType);
    setTempSelected(new Set());
    setCurrentTab('fraud');
  };

  // Navigation: Go to accounts tab
  const selectFraudType = (type) => {
    setSelectedFraudType(type);
    setSections(prev => ({
      ...prev,
      [selectedSection]: { ...prev[selectedSection], fraudType: type }
    }));
    setTempSelected(new Set());
    setCurrentTab('accounts');
  };

  // Navigation: Go back one step
  const goBack = () => {
    if (currentTab === 'accounts') setCurrentTab('fraud');
    else if (currentTab === 'fraud') setCurrentTab('section');
  };

  // Navigation: Go to next section after completing current one
  const goToNextSection = () => {
    const nextSection = selectedSection < 5 ? selectedSection + 1 : null;
    if (nextSection) {
      setSelectedSection(nextSection);
      setSelectedFraudType(sections[nextSection].fraudType);
      setTempSelected(new Set());
      setCurrentTab('fraud');
      toast.success(`Now editing Section ${nextSection}`);
    } else {
      goToSectionList();
      toast.success('All sections complete! Review and generate report.');
    }
  };

  // Add manual entry as a new account
  const addManualEntry = async () => {
    if (!manualEntry.creditorName.trim()) {
      toast.error('Please enter a creditor/company name');
      return;
    }

    try {
      // Extract date from account name if not manually entered
      const extractedDate = extractDateFromAccountName(manualEntry.creditorName);
      const dateFraudBegan = manualEntry.dateFraudBegan || extractedDate || '';
      const dateDiscovered = manualEntry.dateDiscovered || getCurrentMonthYear();
      
      // Create a manual account object that matches existing account structure
      const newAccount = {
        id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        client_id: clientId,
        creditor_name: manualEntry.creditorName,
        account_number: manualEntry.accountNumber || 'MANUAL ENTRY',
        // BUG FIX (May 2026): Do NOT default date_opened to today's date.
        // If left empty, this account simply won't be used in oldest-date math —
        // which is correct. Defaulting to today caused "Date fraud began" to
        // wrongly equal today's date when other accounts had no parseable date.
        date_opened: manualEntry.dateOpened || dateFraudBegan || '',
        balance: parseFloat(manualEntry.fraudAmount) || 0,
        account_status: manualEntry.status,
        account_type: 'MANUAL_ENTRY',
        dispute_reason: manualEntry.notes || `Manual entry for ${selectedFraudType} fraud`,
        bureau_reporting: ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'],
        is_manual: true,
        created_at: new Date().toISOString(),
        // NEW: Include date fraud began for this account
        date_fraud_began: dateFraudBegan
      };

      // Add to local accounts list (frontend-only for now)
      setAccounts(prev => [...prev, newAccount]);
      
      // Set the date fraud began for this account
      if (dateFraudBegan) {
        setDateDiscoveredPerAccount(prev => ({
          ...prev,
          [newAccount.id]: dateFraudBegan
        }));
      }

      // Automatically add to current section
      setSections(prev => {
        const next = { ...prev };
        const set = new Set(next[selectedSection].accountIds);
        set.add(newAccount.id);
        next[selectedSection] = { ...next[selectedSection], accountIds: set };
        return next;
      });
      
      // Update section's date fraud began to oldest date
      const allAccountIdsInSection = new Set(sections[selectedSection].accountIds);
      allAccountIdsInSection.add(newAccount.id);
      
      // Include the new account's date in the calculation
      const tempDates = { ...dateDiscoveredPerAccount };
      if (dateFraudBegan) {
        tempDates[newAccount.id] = dateFraudBegan;
      }
      
      // Find oldest date among all accounts in section
      let oldestValue = Infinity;
      let oldestDate = null;
      allAccountIdsInSection.forEach(id => {
        const dateStr = tempDates[id];
        if (dateStr) {
          const match = dateStr.match(/^(\d{1,2})\/(\d{4})$/);
          if (match) {
            const value = parseInt(match[2], 10) * 12 + parseInt(match[1], 10);
            if (value < oldestValue) {
              oldestValue = value;
              oldestDate = dateStr;
            }
          }
        }
      });
      
      if (oldestDate) {
        setDateDiscoveredPerSection(prev => ({
          ...prev,
          [selectedSection]: oldestDate
        }));
      }

      // Reset form
      setManualEntry({
        creditorName: '',
        accountNumber: '',
        dateOpened: '',
        dateFraudBegan: '',
        dateDiscovered: '',
        fraudAmount: '',
        status: 'FRAUDULENT',
        notes: ''
      });

      toast.success(`Manual entry "${newAccount.creditor_name}" added to Section ${selectedSection}${dateFraudBegan ? ` - Date Fraud Began: ${dateFraudBegan}` : ''}`);
    } catch (error) {
      console.error('Error adding manual entry:', error);
      toast.error('Failed to add manual entry. Please try again.');
    }
  };

  // Generate FTC Report PDF
  const generateReport = async () => {
    const categoryOverrides = [];
    let totalAccounts = 0;
    
    Object.entries(sections).forEach(([sectionNum, sec]) => {
      if (sec.accountIds.size > 0 && sec.fraudType) {
        totalAccounts += sec.accountIds.size;
        sec.accountIds.forEach(accountId => {
          categoryOverrides.push({
            account_id: accountId,
            category: sec.fraudType,
            ftc_section: parseInt(sectionNum)
          });
        });
      }
    });

    if (totalAccounts === 0) {
      toast.error('Please add at least one account to a section first');
      return;
    }

    setGenerating(true);

    try {
      // Extract signature dataUrl if it's an object
      let sigDataUrl = null;
      if (signatureData) {
        if (typeof signatureData === 'string') {
          sigDataUrl = signatureData;
        } else if (signatureData.dataUrl) {
          sigDataUrl = signatureData.dataUrl;
        }
      }
      
      // Collect manual accounts that are included in sections
      const includedAccountIds = new Set(categoryOverrides.map(o => o.account_id));
      const manualAccountsToSend = accounts
        .filter(acc => acc.is_manual && includedAccountIds.has(acc.id))
        .map(acc => ({
          id: acc.id,
          creditor_name: acc.creditor_name,
          account_number: acc.account_number,
          date_opened: acc.date_opened,
          balance: acc.balance,
          account_status: acc.account_status,
          account_type: acc.account_type,
          dispute_reason: acc.dispute_reason,
          is_manual: true
        }));
      
      let response;
      
      // Check if we're in standalone mode (no clientId)
      // ALWAYS use generate-standalone endpoint for consistent output
      // The generate-standalone endpoint has all the new formatting we perfected
      
      // UNIFIED MODE: Always use generate-standalone endpoint for consistent PDF output
      // Format SECTIONS (grouped accounts) - MAX 5 sections
      const formattedSections = [];
      Object.entries(sections).forEach(([sectionNum, sec]) => {
        if (sec.accountIds.size > 0 && sec.fraudType) {
          // Get all account names for this section
          const sectionAccounts = [];
          sec.accountIds.forEach(accountId => {
            const account = accounts.find(a => a.id === accountId);
            if (account) {
              sectionAccounts.push({
                creditor_name: account.creditor_name,
                account_number: account.account_number || '',
                // CRITICAL: Use date_fraud_began (parsed date) as date_opened for the PDF
                // This is the date extracted from account string (e.g., "7/2025")
                date_opened: account.date_fraud_began || account.date_opened || '',
                balance: String(account.balance || 0)
              });
            }
          });
          
          // Get section total (custom if set, otherwise sum of account balances)
          const sectionTotal = getSectionTotal(parseInt(sectionNum));
          
          formattedSections.push({
            ftc_section: parseInt(sectionNum),
            fraud_type: sec.fraudType,
            accounts: sectionAccounts,
            total_fraud_amount: String(sectionTotal),
            date_discovered: getDateDiscoveredForSection(sectionNum)
          });
        }
      });
      
      // Use client data from props or from API-loaded client
      const clientInfoForPDF = {
        name: clientData?.name || '',
        suffix: clientData?.suffix || '',  // Include name suffix (II, III, Jr., etc.)
        dob: clientData?.dob || '',
        ssn: clientData?.ssn || '',
        address: clientData?.address || '',
        city: clientData?.city || '',
        state: clientData?.state || '',
        zip_code: clientData?.zip_code || '',
        phone: clientData?.phone || '',
        email: clientData?.email || ''
      };
      
      console.log('[FTC Generate] Using generate-standalone with', formattedSections.length, 'sections, clientId:', clientId);
      if (!(await preflightOk(clientInfoForPDF, formattedSections, personalStatement))) { setGenerating(false); return; }
      if (reportNumber?.trim()) saveReportNumber(reportNumber);
      
      response = await axios.post(
        `${API}/api/ftc-affidavit/generate-standalone`,
        {
          client_info: clientInfoForPDF,
          client_id: clientId,  // CRITICAL: Pass client_id directly for reliable artifact saving
          ftc_sections: formattedSections,
          custom_statement: personalStatement,
          signature_data: sigDataUrl,
          custom_report_date: reportDate || null,
          custom_report_time: reportTime || null,
          custom_report_number: reportNumber || null,
          theme: ftcTheme || 'color',  // Default to COLOR per user request
          date_format: dateFormat || 'month_year'  // 'month_year' (default — strip day) or 'full_date' (keep day)
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadName = `IDTheftReport_${reportNumber || Date.now()}.pdf`;
      // 2026-02-28 (iPhone bug fix) — swap the raw `<a download>` for
      // downloadBlobMobileSafe which uses the Web Share API on iOS.
      // That surfaces the "Save to Files" sheet natively WITHOUT
      // navigating the SPA tab away — so the user returns to Step 3
      // with all state intact.  Old code just did an anchor click
      // which iOS Safari treats as a navigation and evicts React
      // state from memory on tab return.
      await downloadBlobMobileSafe(blob, downloadName, {
        shareTitle: `FTC Identity Theft Report #${reportNumber || ''}`.trim(),
      });

      // 2026-02-28 (user request) — SILENT auto-save the FTC report to
      // the log the moment Generate fires.  User was losing progress
      // when clicking into other windows before hitting Save Report.
      // Fire-and-forget: never blocks the UI, never throws a toast.
      try {
        saveCompletedReportLog({}).catch(() => {});
      } catch { /* ignore */ }

      // Capture the just-generated PDF so the Mail Report flow can pre-fill it.
      // We store the raw blob (not the revoked URL) so the user can drag in
      // additional docs and we still have a handle to the FTC PDF.
      // 2026-06-09: user requested that the FTC report NOT be auto-added to
      // the Mail Report queue — they want to drag/pick it themselves. We
      // still keep `lastGeneratedFtcBlob` populated so other flows that
      // reference the most-recent FTC PDF (e.g. the email-prep popup) work.
      try {
        const ftcFile = new File([blob], downloadName, { type: 'application/pdf' });
        setLastGeneratedFtcBlob({ file: ftcFile, name: downloadName, size: ftcFile.size });
      } catch (e) {
        // File constructor not available — fall back to blob
        setLastGeneratedFtcBlob({ blob, name: downloadName, size: blob.size });
      }

      toast.success('FTC Identity Theft Report downloaded!');
      
      // Prepare accounts for credit repair email popup
      // Group accounts by bureau (for now, assume all accounts go to all bureaus)
      const allAccountNames = [];
      // 2026-Feb: also collect STRUCTURED accounts (with open_date + balance +
      // category) so the dispute-letter modal renders every account with the
      // same detail level as the FTC report.
      // 2026-02-28 (user request) — iterate ALL parsed `accounts` (not just
      // the ones assigned to FTC report sections).  Reasoning: the FTC
      // report has only 5 section slots and often can't fit every parsed
      // account, but the DISPUTE LETTER should list every account/inquiry
      // the user parsed so the bureaus dispute all of them.  We track
      // which account IDs are already covered by a section so any
      // section-assigned account keeps its section's tagging, then loop
      // through the full `accounts` array to append the leftovers.
      const structuredForDispute = [];
      const seenAccountIds = new Set();
      const CAT_MAP = {
        AUTO: 'AUTO', CREDIT_CARD: 'CREDIT_CARD', CREDIT_UNION: 'CREDIT_UNION',
        PERSONAL_LOAN: 'PERSONAL_LOAN', STUDENT_LOAN: 'STUDENT_LOAN',
        MORTGAGE: 'MORTGAGE',
        APARTMENT_LEASE: 'APARTMENT_LEASE', RENTAL_LEASE: 'RENTAL_LEASE',
        UTILITY: 'UTILITY', MEDICAL: 'MEDICAL',
        COLLECTION: 'COLLECTION', OTHER: 'OTHER',
      };
      const pushAccountForDispute = (account) => {
        if (!account || seenAccountIds.has(account.id)) return;
        seenAccountIds.add(account.id);
        allAccountNames.push(account.creditor_name);
        const rawBal = account.fraudAmount != null
          ? String(account.fraudAmount).replace(/[^0-9.]/g, '')
          : (account.balance != null ? String(account.balance) : null);
        structuredForDispute.push({
          account_name: (account.originalName || account.creditor_name || '').toUpperCase().trim(),
          category: CAT_MAP[account.category] || 'OTHER',
          date_opened: account.dateOpened || account.date_opened || null,
          balance: rawBal,
        });
      };
      // Pass 1: section-assigned accounts (keep them first so the dispute
      // letter's ordering matches the FTC report's when there's overlap).
      Object.entries(sections).forEach(([sectionNum, sec]) => {
        if (sec.accountIds.size > 0) {
          sec.accountIds.forEach((accountId) => {
            const account = accounts.find((a) => a.id === accountId);
            if (account) pushAccountForDispute(account);
          });
        }
      });
      // Pass 2: sweep in every parsed account that didn't make it onto
      // the FTC report — these are the "extras" the user was losing.
      accounts.forEach((account) => pushAccountForDispute(account));
      
      // Set accounts for all bureaus (in real implementation, this could be bureau-specific)
      setFtcAccountsForEmail({
        transunion: [...allAccountNames],
        equifax: [...allAccountNames],
        experian: [...allAccountNames]
      });
      setFtcAccountsStructuredForDispute(structuredForDispute);
      
      // 2026-Feb (user request restored 2026-02-05): fire the dispute-letter
      // prompt right after the FTC report is downloaded — not after the
      // credit-repair emails.  Skipping the prompt still lets the email
      // popup open.
      setShowPostFtcDisputePrompt(true);
    } catch (error) {
      console.error('FTC Report generation error:', error);
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGenerating(false);
    }
  };
  
  // ============================================================
  // Persist a snapshot of this completed FTC report to the saved-reports log
  // (so the user can reopen it later from the FTC Builder home page).
  // Called automatically after the Quick Reminder Skip/Save flow.
  // ============================================================
  const saveCompletedReportLog = async (extra = {}) => {
    try {
      setSavingReportLog(true);
      // Snapshot the full editor state so re-loading works
      const sectionsSnapshot = {};
      const ov = extra.accountOverrides || {};
      Object.entries(sections).forEach(([num, sec]) => {
        const accountList = (accounts || []).filter(a => sec.accountIds && sec.accountIds.has(a.id)).map(a => (ov[a.id] ? { ...a, ...ov[a.id] } : a));
        sectionsSnapshot[num] = {
          fraudType: sec.fraudType || null,
          customTotalAmount: sec.customTotalAmount || '',
          accounts: accountList.map(a => ({
            id: a.id,
            creditor_name: parseFtcLine(a.display_name || a.creditor_name).name || a.creditor_name,
            display_name: parseFtcLine(a.display_name || a.creditor_name).name || a.creditor_name,
            original_name: a.originalName || a.creditor_name,
            category: a.category || '',
            account_number: a.account_number || '',
            date_opened: a.date_fraud_began || a.date_opened || '',
            original_date_opened: a.original_date_opened || a.date_opened || '',
            balance: Number(a.balance || 0),
          })),
        };
      });

      // Compute totals
      let totalFraud = 0;
      Object.values(sectionsSnapshot).forEach((s) => {
        s.accounts.forEach((a) => { totalFraud += Number(a.balance || 0); });
      });

      const payload = {
        id: currentSavedReportId || undefined,
        client_id: clientId || null,
        client_name: extra.clientInfo?.name || clientData?.name || '',
        client_email: extra.clientInfo?.email || clientData?.email || '',
        sections: sectionsSnapshot,
        client_info: { ...(clientData || {}), ...(extra.clientInfo || {}) },
        date_format: dateFormat,
        total_fraud_amount: totalFraud,
        outstanding_balance: Number(extra.outstanding_balance || 0),
        notes: extra.notes || null,
        promised_payment_date: extra.promised_payment_date || null,
        promised_payment_amount: extra.promised_payment_amount ?? null,
        promised_payment_notes: extra.promised_payment_notes || null,
        status: 'completed',
      };

      const { data } = await axios.post(`${API}/api/ftc-reports/save`, payload);
      if (data?.id) {
        setCurrentSavedReportId(data.id);
        if (typeof onReportSaved === 'function') onReportSaved(data.id);
        // 2026-02-28 — Backend returns `overwrote: true` when a
        // duplicate (name, email) was auto-collapsed.  (Was `_overwrote`
        // earlier but Pydantic's response_model strips underscore-
        // prefixed keys — check both to be safe during rollout.)
        // Surface an Undo toast so a mistaken overwrite can be reversed.
        if (data.overwrote || data._overwrote) {
          toast.success('Existing report overwritten with your latest changes.', {
            action: {
              label: 'Undo',
              onClick: async () => {
                try {
                  await axios.post(`${API}/api/ftc-reports/${data.id}/undo-overwrite`);
                  toast.success('Overwrite reversed — previous report restored. Refresh to see it.');
                } catch (e) {
                  toast.error('Undo failed: ' + (e?.response?.data?.detail || e.message));
                }
              },
            },
            duration: 12000,
          });
        } else {
          toast.success('FTC report saved to log — you can reopen it later from FTC Report Builder');
        }
      }
      return data;
    } catch (err) {
      console.error('Failed to save FTC report to log', err);
      toast.error('Could not save report log: ' + (err.response?.data?.detail || err.message));
      return null;
    } finally {
      setSavingReportLog(false);
    }
  };
  
  // Send Credit Repair Steps Email to Client
  const sendCreditRepairStepsEmail = async () => {
    if (!clientData?.email) {
      toast.error('Client email is required');
      return;
    }
    
    setSendingCreditRepairEmail(true);
    
    try {
      const response = await axios.post(`${API}/api/ftc-report/send-credit-repair-steps`, {
        client_name: clientData?.name || 'Client',
        client_email: clientData?.email,
        accounts_by_bureau: ftcAccountsForEmail
      });
      
      if (response.data?.success) {
        toast.success(`Credit Repair Steps email sent to ${clientData.email}!`);
        setShowCreditRepairEmailPopup(false);
        // Show Quick Payment Reminder popup next
        setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
        setShowQuickReminderPopup(true);
      } else {
        throw new Error(response.data?.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Credit Repair Steps email error:', error);
      toast.error(`Failed to send email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingCreditRepairEmail(false);
    }
  };

  // Pick a delivery option: 'now' | 'in_1h' | 'manual' | 'skip'.
  // Persists the scheduled email on the backend and continues the post-FTC flow.
  //
  // Feb 2026: this now fires the NEW 5-email credit-repair SERIES endpoint
  // (`/api/ftc-report/send-credit-repair-series`).  Each of the 5 emails
  // is spaced 60s apart.  The 'now' choice starts the sequence
  // immediately; 'in_1h' delays the FIRST email by 60 minutes.  The old
  // 30-min / 2-hour options were removed at the user's request.
  const handleCreditRepairDeliveryChoice = async (choice) => {
    if (choice === 'skip') {
      setShowCreditRepairEmailPopup(false);
      setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
      setShowQuickReminderPopup(true);
      return;
    }
    if (!clientData?.email) {
      toast.error('Client email is required');
      return;
    }

    // 'manual' still hits the legacy scheduler so the Saved-Reports gallery
    // "Come back later" flow keeps working unchanged.
    if (choice === 'manual') {
      try {
        setSendingCreditRepairEmail(true);
        const response = await axios.post(`${API}/api/ftc-report/credit-repair-steps/schedule`, {
          ftc_report_id: savedReportId || null,
          client_name: clientData?.name || 'Client',
          client_email: clientData?.email,
          accounts_by_bureau: ftcAccountsForEmail,
          delivery_choice: 'manual',
        });
        if (response.data?.success) {
          toast.success('💾 Saved as pending. You can send it later from the Saved FTC Reports gallery.');
          setShowCreditRepairEmailPopup(false);
          // 2026-Feb: post-email dispute prompt removed — dispute letter
          // is now created BEFORE emails via the post-FTC-generation prompt.
          setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
          setShowQuickReminderPopup(true);
        } else {
          throw new Error(response.data?.error || 'Failed to schedule');
        }
      } catch (error) {
        console.error('Credit Repair Steps schedule error:', error);
        toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
      } finally {
        setSendingCreditRepairEmail(false);
      }
      return;
    }

    // 'now' or 'in_1h' → fire the new 5-email series
    try {
      setSendingCreditRepairEmail(true);
      const response = await axios.post(`${API}/api/ftc-report/send-credit-repair-series`, {
        ftc_report_id: savedReportId || null,
        client_name: clientData?.name || 'Client',
        client_email: clientData?.email,
        accounts_by_bureau: ftcAccountsForEmail,
        delivery_choice: choice,
        send_mode: creditRepairSendMode,
      }, { timeout: 15000 });
      if (response.data?.success) {
        // 2026-02-24 — toast copy varies with send_mode so the user knows
        // exactly how many emails just left the queue.
        const modeLabel = {
          series:   '📧 5-email drip series',
          combined: '📄 1 combined email',
          both:     '📧+📄 Combined email + 5-part series',
        }[creditRepairSendMode] || '📧 Credit Repair emails';
        const timingLabel = choice === 'in_1h' ? 'scheduled to start in 1 hour' : 'queued for send';
        toast.success(`${modeLabel} ${timingLabel} → ${clientData.email}`);
        setShowCreditRepairEmailPopup(false);
        // 2026-Feb: post-email dispute prompt removed — dispute letter
        // is now created BEFORE emails via the post-FTC-generation prompt.
        setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
        setShowQuickReminderPopup(true);
      } else {
        throw new Error(response.data?.error || 'Failed to schedule');
      }
    } catch (error) {
      console.error('Credit Repair Series error:', error);
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingCreditRepairEmail(false);
    }
  };

  // Send the dropped/attached PDFs to the selected bureau(s) via Lob.com.
  // Loops one POST per bureau against /api/mail/send-uploaded-files which
  // already prepends a blank cover page so Lob's address overlay never
  // collides with content.  If more than one PDF was dropped, we merge them
  // client-side using pdf-lib so Lob receives a single document.
  const handleSendMailReport = async () => {
    const bureaus = Object.entries(mailReportBureaus).filter(([, on]) => on).map(([k]) => k);
    if (bureaus.length === 0) { toast.error('Pick at least one bureau.'); return; }
    if (mailReportDocs.length === 0) { toast.error('Drop at least one PDF.'); return; }
    setMailReportSending(true);
    setMailReportResults(null);
    try {
      // Merge all uploaded PDFs into a single PDF (page order = drop order)
      let mergedBlob;
      if (mailReportDocs.length === 1) {
        mergedBlob = mailReportDocs[0].file;
      } else {
        const { PDFDocument } = await import('pdf-lib');
        const out = await PDFDocument.create();
        for (const d of mailReportDocs) {
          const bytes = await d.file.arrayBuffer();
          const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
          const pages = await out.copyPages(src, src.getPageIndices());
          pages.forEach(p => out.addPage(p));
        }
        const mergedBytes = await out.save();
        mergedBlob = new Blob([mergedBytes], { type: 'application/pdf' });
      }

      const results = [];
      for (const bureau of bureaus) {
        try {
          const form = new FormData();
          form.append('bureau', bureau);
          form.append('mail_type', mailReportClass);
          form.append('documents', mergedBlob, `FTC_Report_${bureau}.pdf`);
          if (clientId) form.append('client_id', clientId);
          // Optional fallbacks if no client_id in context — pull from clientData
          if (clientData) {
            if (clientData.name) form.append('return_name', clientData.name);
            if (clientData.address) form.append('return_address', clientData.address);
            if (clientData.city) form.append('return_city', clientData.city);
            if (clientData.state) form.append('return_state', clientData.state);
            if (clientData.zip_code || clientData.zip) form.append('return_zip', clientData.zip_code || clientData.zip);
          }
          const { data } = await axios.post(`${API}/api/mail/send-uploaded-files`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90000,
          });
          results.push({
            bureau: bureau.toUpperCase(),
            success: data.status === 'sent' || data.status === 'sent_mock',
            tracking_id: data.tracking_id || data.tracking_number,
            lob_url: data.lob_url,
            error: data.error || (data.status === 'lob_error' ? data.message : null),
          });
        } catch (err) {
          results.push({
            bureau: bureau.toUpperCase(),
            success: false,
            error: err?.response?.data?.detail || err.message || 'Send failed',
          });
        }
      }
      setMailReportResults(results);
      const ok = results.filter(r => r.success).length;
      if (ok === results.length) {
        toast.success(`Mailed to ${ok}/${results.length} bureau${results.length === 1 ? '' : 's'} via Lob.`);
        // Feb 2026: portal-announcement prompt removed — Email 4 of the
        // new 5-email Credit Repair Series already handles the portal
        // invite, so this extra popup was redundant.
        // 2026-02-05 (user request): after a successful mail, offer the
        // Dispute Packet builder → then the "review any section" prompt.
        setTimeout(() => {
          setShowMailReportModal(false);
          setPacketFilename(`${(clientData?.name || 'Client').split(/\s+/).slice(0, 2).join(' ')} Dispute Packet`.trim());
          setTimeout(() => setShowDisputePacketPrompt(true), 250);
        }, 1200);
      } else if (ok > 0) {
        toast.warning(`Mailed to ${ok}/${results.length} bureau(s). See results panel.`);
      } else {
        toast.error('All mail attempts failed. See results panel.');
      }
    } catch (e) {
      console.error('[Mail Report]', e);
      toast.error('Mail failed: ' + (e.message || 'Unknown error'));
    } finally {
      setMailReportSending(false);
    }
  };

  // Called when the Mail Report modal closes (either after send OR skip)
  // Feb 2026: no longer offers the portal announcement — that flow is
  // now inside Email 4 of the 5-email Credit Repair Series.
  // 2026-02-05 (user reported the packet + review prompts weren't
  // firing): this function was bypassing the Dialog's onOpenChange
  // handler, so the follow-up prompts never triggered.  Fire them
  // directly here too.
  const closeMailReportAndOfferPortal = () => {
    setShowMailReportModal(false);
    setPacketFilename(`${(clientData?.name || 'Client').split(/\s+/).slice(0, 2).join(' ')} Dispute Packet`.trim());
    setTimeout(() => setShowDisputePacketPrompt(true), 250);
  };

  const sendPortalAnnouncementToThisClient = async () => {
    if (!clientData?.email) return;
    setSendingPortalAnnouncement(true);
    try {
      // 10s client-side timeout — the endpoint now queues the send in
      // the background so it should return in < 500ms.  Anything
      // longer than 10s means the backend itself is unreachable.
      await axios.post(
        `${API}/api/quick-reminders/send-portal-announcement-to-client`,
        {
          client_name: clientData?.name || 'Client',
          client_email: clientData.email,
        },
        { timeout: 10000 },
      );
      toast.success(
        `Portal announcement queued for ${clientData.email} — it will land in their inbox within a minute.`,
      );
      setShowPortalPromptModal(false);
    } catch (e) {
      console.error('[Portal announce single]', e);
      const msg =
        e.code === 'ECONNABORTED'
          ? 'Send timed out. Please check your connection and try again.'
          : e.response?.data?.detail || e.message || 'Unknown error';
      toast.error('Send failed: ' + msg);
    } finally {
      setSendingPortalAnnouncement(false);
    }
  };

  // Add client to Quick Payment Reminder
  const handleAddToQuickReminder = async () => {
    if (!quickReminderForm.balance || parseFloat(quickReminderForm.balance) <= 0) {
      toast.error('Please enter the remaining balance');
      return;
    }
    
    try {
      setSavingQuickReminder(true);
      const response = await axios.post(`${API}/api/quick-reminder/send`, {
        clientName: clientData?.name || 'Client',
        clientEmail: clientData?.email || '',
        balance: quickReminderForm.balance,
        clientPhone: '',
        sendMethod: 'email',
        priority: quickReminderForm.priority,
        paymentReminderFrequency: quickReminderForm.reminderFrequency || 'weekly',
        // Per user request 2026-03-02: pass the promise-pay date + notes
        // through so the Quick Reminder section can display them.
        promisedPaymentDate: quickReminderForm.promisedDate || null,
        promisedPaymentNotes: quickReminderForm.promisedNotes || null,
      });
      
      if (response.data?.success) {
        const priorityText = quickReminderForm.priority ? ' (Priority)' : '';
        const promiseText = quickReminderForm.promisedDate ? ` · Promise reminder set for ${quickReminderForm.promisedDate}` : '';
        toast.success(`${clientData?.name || 'Client'} added to Quick Payment Reminder${priorityText}!${promiseText} They'll get automatic Friday payment alerts.`, { duration: 5000 });
        setShowQuickReminderPopup(false);
        // ALSO save the FTC report to the saved-reports log so it can be
        // reopened later from the FTC Report Builder home page.
        await saveCompletedReportLog({
          outstanding_balance: parseFloat(quickReminderForm.balance) || 0,
          notes: quickReminderForm.priority ? 'Priority client' : null,
          promised_payment_date: quickReminderForm.promisedDate || null,
          promised_payment_amount: quickReminderForm.promisedDate ? (parseFloat(quickReminderForm.balance) || 0) : null,
          promised_payment_notes: quickReminderForm.promisedNotes || null,
        });
        // 2026-02-24 — Handwriting prompt removed at user request; skip
        // straight to Copy & Fax.
        setMailReportResults(null);
        setShowCopyFaxPrompt(true);
      } else {
        throw new Error(response.data?.error || 'Failed to add');
      }
    } catch (error) {
      console.error('Quick Reminder error:', error);
      toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSavingQuickReminder(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Feb 2026 — bureau paper-form filler for the FTC flow.  Mirrors the
  // Dispute Wizard implementation so behaviour is identical: builds a
  // client_info payload, hits /api/bureau-dispute-forms/generate, then
  // either auto-downloads OR opens the free-form editor if the admin
  // asked to nudge fields first.  Editing a BOTH bundle chains through
  // both bureaus in sequence.
  // ────────────────────────────────────────────────────────────────
  const runBureauFormsFillFTC = async (bureaus, { editFirst = false } = {}) => {
    if (!bureaus || bureaus.length === 0) return;
    if (!clientData) {
      toast.error('Client data not loaded — refresh and try again');
      return;
    }
    try {
      setBureauFormsBusy(true);
      // Pull whichever accounts are staged for the dispute (falls back
      // to the raw preloaded list if nothing has been selected yet).
      const source = (ftcAccountsStructuredForDispute && ftcAccountsStructuredForDispute.length)
        ? ftcAccountsStructuredForDispute
        : preloadedAccounts;
      const acctPayload = (source || []).map((a) => ({
        creditor_name: a.creditor_name || a.company_name || a.account_name || a.name || '',
        account_number: a.account_number || a.account_last4 || '',
        // 2026-09-26 — Pass date + balance through so the backend's
        // `_compose_company_line` can render "NAME - MM-YYYY - $BAL"
        // in each dispute slot (matches the dispute-letter format so
        // duplicate creditors on the same form are distinguishable).
        // Previously these were dropped from the payload and every
        // Equifax/TU account slot showed just the raw creditor name.
        date_opened: a.date_fraud_began || a.date_opened || a.date || '',
        balance: a.balance || a.amount || '',
        reason: 'this_is_not_my_account',
      }));
      const client_info = {
        full_name: clientData.name || clientData.full_name || '',
        dob: clientData.dob || clientData.date_of_birth || '',
        ssn: clientData.ssn || '',
        address: clientData.address || clientData.street_address || '',
        city: clientData.city || '',
        state: clientData.state || '',
        zip: clientData.zip || clientData.zip_code || '',
        full_address: [clientData.address, clientData.city, clientData.state, clientData.zip]
          .filter(Boolean).join(', ') || clientData.full_address || '',
        phone: clientData.phone || '',
        email: clientData.email || '',
      };
      toast.info(`Generating ${bureaus.length === 2 ? 'TransUnion + Equifax' : bureaus[0]} form${bureaus.length > 1 ? 's' : ''}…`);
      // Extract raw signature dataUrl if it's an object
      let sigDataUrl = null;
      if (signatureData) {
        if (typeof signatureData === 'string') sigDataUrl = signatureData;
        else if (signatureData.dataUrl) sigDataUrl = signatureData.dataUrl;
      }
      const resp = await axios.post(`${API}/api/bureau-dispute-forms/generate`, {
        bureaus,
        client_info,
        accounts: acctPayload,
        signature_data: sigDataUrl,
        single_file: true,
      }, { timeout: 90000 });
      const files = resp?.data?.files || [];
      const usable = files.filter((f) => f && f.data_url);
      const failed = files.filter((f) => f && !f.data_url);
      failed.forEach((f) => toast.error(`${f.bureau}: ${f.error || 'generation failed'}`));

      if (editFirst && usable.length > 0) {
        const [first, ...rest] = usable;
        setBureauEditorPdf(first.data_url);
        setBureauEditorLabel(first.label || first.bureau);
        setBureauEditorFilename(first.filename || `${first.label}_Dispute_Form.pdf`);
        setBureauEditorQueue(rest.map((f) => ({
          data_url: f.data_url,
          label: f.label || f.bureau,
          filename: f.filename || `${f.label}_Dispute_Form.pdf`,
        })));
        setBureauEditorOpen(true);
        setBureauFormsPromptOpen(false);
      } else {
        let downloaded = 0;
        for (const f of usable) {
          const link = document.createElement('a');
          link.href = f.data_url;
          link.download = f.filename || `${f.bureau}_dispute_form.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          downloaded += 1;
          await new Promise((r) => setTimeout(r, 350));
        }
        if (downloaded > 0) {
          toast.success(`Downloaded ${downloaded} pre-filled bureau form${downloaded === 1 ? '' : 's'}`);
        } else if (!failed.length) {
          toast.error('No bureau forms were produced — check backend logs');
        }
        setBureauFormsPromptOpen(false);
        // Resume the post-dispute flow (Quick Reminder / Credit Repair
        // email) that used to fire inline on DisputeLetterModal.onClose.
        proceedAfterDisputeLetters();
      }
    } catch (e) {
      console.error('[bureau-forms:ftc] fill failed', e);
      toast.error(e?.response?.data?.detail || e.message || 'Failed to generate bureau forms');
    } finally {
      setBureauFormsBusy(false);
    }
  };

  const advanceBureauEditorQueueFTC = () => {
    setBureauEditorOpen(false);
    setBureauEditorPdf(null);
    if (bureauEditorQueue.length > 0) {
      const [next, ...rest] = bureauEditorQueue;
      setBureauEditorPdf(next.data_url);
      setBureauEditorLabel(next.label);
      setBureauEditorFilename(next.filename);
      setBureauEditorQueue(rest);
      setTimeout(() => setBureauEditorOpen(true), 40);
    } else {
      // No more bureaus queued — resume the normal post-dispute flow
      // (Quick Reminder / Credit Repair email popup).  The
      // "letters just downloaded" flag has already been consumed by
      // the earlier proceedAfterDisputeLetters call that opened the
      // bureau prompt, so this run falls straight through to the
      // existing branches.
      proceedAfterDisputeLetters();
    }
  };

  // Called when the DisputeLetterModal closes AND after the bureau-forms
  // prompt is dismissed.  Runs the same branching that used to live
  // inline on the modal's onClose so we keep every existing route
  // (post-email quick reminder popup, credit-repair email popup) intact.
  const proceedAfterDisputeLetters = () => {
    // Consume the "letters were just downloaded" flag first.  If it was
    // set, we need to offer the bureau paper forms BEFORE continuing —
    // that's the entire point of the Feb-2026 fix.
    if (disputeLettersJustDownloadedRef.current && !bureauFormsPromptOpen && !bureauEditorOpen) {
      disputeLettersJustDownloadedRef.current = false;
      setBureauFormsPromptOpen(true);
      return;   // resume the rest of the flow when the prompt closes
    }
    if (disputeOpenedFromPostEmail) {
      setDisputeOpenedFromPostEmail(false);
      setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
      setShowQuickReminderPopup(true);
    } else if (clientData?.email && !showCreditRepairEmailPopup && !showQuickReminderPopup && !activeSavedLetter) {
      // Coming from the post-FTC-generation prompt — continue the flow
      // to the credit-repair email popup so the user can still send.
      setShowCreditRepairEmailPopup(true);
    }
  };

  // Generate EDITABLE Preview of FTC Report
  const generatePreview = async () => {
    let totalAccounts = 0;
    
    Object.entries(sections).forEach(([sectionNum, sec]) => {
      if (sec.accountIds.size > 0 && sec.fraudType) {
        totalAccounts += sec.accountIds.size;
      }
    });

    if (totalAccounts === 0) {
      toast.error('Please add at least one account to a section first');
      return;
    }

    setGeneratingPreview(true);

    try {
      // Extract signature dataUrl if it's an object
      let sigDataUrl = null;
      if (signatureData) {
        if (typeof signatureData === 'string') {
          sigDataUrl = signatureData;
        } else if (signatureData.dataUrl) {
          sigDataUrl = signatureData.dataUrl;
        }
      }
      
      // Build EDITABLE sections data
      const editableSections = [];
      Object.entries(sections).forEach(([sectionNum, sec]) => {
        if (sec.accountIds.size > 0 && sec.fraudType) {
          const sectionAccounts = [];
          sec.accountIds.forEach(accountId => {
            const account = accounts.find(a => a.id === accountId);
            if (account) {
              // Format date with dash for account name display
              let displayDate = account.date_fraud_began || account.date_opened || '';
              if (displayDate && displayDate.includes('/')) {
                const parts = displayDate.split('/');
                if (parts.length >= 2) {
                  displayDate = `${parseInt(parts[0])}-${parts[parts.length - 1]}`;
                }
              }
              
              // Defensive: strip ANY trailing date and/or $amount off the
              // stored creditor_name so we don't double-stamp when we rebuild
              // the "NAME - DATE - $BAL" display string below.
              let cleanCreditor = String(account.creditor_name || '').trim();
              for (let _p = 0; _p < 5; _p++) {
                const before = cleanCreditor;
                cleanCreditor = cleanCreditor
                  .replace(/\s*[-–]?\s*\$[\d,]+(?:\.\d{1,2})?\s*$/, '')
                  .replace(/\s*[-–]?\s*\d{1,2}(-\d{1,2})?-\d{4}\s*$/, '')
                  .trim();
                if (cleanCreditor === before) break;
              }
              const balNum = parseInt(account.balance || 0);
              sectionAccounts.push({
                id: accountId,
                creditor_name: cleanCreditor,
                display_name: `${cleanCreditor}${displayDate ? ' - ' + displayDate : ''} - $${balNum.toLocaleString()}`,
                account_number: account.account_number || '',
                date_opened: account.date_fraud_began || account.date_opened || '',
                balance: String(account.balance || 0)
              });
            }
          });
          
          const sectionTotal = getSectionTotal(parseInt(sectionNum));

          // ─── Pick OLDEST date across all accounts in this section ───
          // Format: M/YYYY with NO leading zero on month, 4-digit year.
          // Handles: "3-2021", "03/2021", "3-15-2021", "03/15/2021", "2021-03-15", ISO.
          const _oldestYM = (() => {
            const parseYM = (raw) => {
              if (!raw) return null;
              const s = String(raw).trim();
              let m = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);   // ISO 2021-03 or 2021-03-15[T...]
              if (m) return [parseInt(m[1], 10), parseInt(m[2], 10)];
              m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);   // 3-15-2021 or 03/15/21
              if (m) { let y = parseInt(m[3], 10); if (y < 100) y += 2000; return [y, parseInt(m[1], 10)]; }
              m = s.match(/^(\d{1,2})[-/](\d{2,4})$/);                 // 3-2021 or 03/2021
              if (m) { let y = parseInt(m[2], 10); if (y < 100) y += 2000; return [y, parseInt(m[1], 10)]; }
              return null;
            };
            const cands = [];
            for (const a of sectionAccounts) {
              const ym = parseYM(a.date_opened);
              if (ym) cands.push(ym);
            }
            if (!cands.length) return '';
            cands.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
            return `${cands[0][1]}/${cands[0][0]}`;  // "3/2021"
          })();

          editableSections.push({
            ftc_section: parseInt(sectionNum),
            fraud_type: sec.fraudType,
            fraud_type_label: FRAUD_TYPES.find(f => f.id === sec.fraudType)?.label || sec.fraudType,
            accounts: sectionAccounts,
            total_fraud_amount: String(sectionTotal),
            date_fraud_began: _oldestYM || sectionAccounts[0]?.date_opened || '',
            date_discovered: getDateDiscoveredForSection(sectionNum)
          });
        }
      });
      
      // Build preview data object - all fields are EDITABLE
      const preview = {
        clientInfo: {
          name: clientData?.name || '',
          suffix: clientData?.suffix || '',
          dob: clientData?.dob || '',
          ssn: clientData?.ssn || '',
          address: clientData?.address || '',
          city: clientData?.city || '',
          state: clientData?.state || '',
          zip_code: clientData?.zip_code || '',
          phone: clientData?.phone || '',
          email: clientData?.email || ''
        },
        reportDate: reportDate || '',
        reportTime: reportTime || '',
        reportNumber: reportNumber || '',
        personalStatement: personalStatement,
        sections: editableSections,
        signatureData: sigDataUrl,
        theme: ftcTheme || 'color',
        dateFormat: dateFormat || 'month_year'  // Include date format setting (default: strip the day)
      };
      
      setPreviewData(preview);
      setOriginalPreviewData(JSON.parse(JSON.stringify(preview))); // Store original for comparison
      setHasUnsavedChanges(false); // Reset changes tracker
      setShowPreview(true);
      toast.success('Edit mode activated! Make your changes, then click "Generate & Download PDF" when ready.');
    } catch (error) {
      console.error('FTC Preview generation error:', error);
      toast.error(`Failed to generate preview: ${error.message}`);
    } finally {
      setGeneratingPreview(false);
    }
  };

  // Update preview data field
  const updatePreviewField = (path, value) => {
    setPreviewData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (keys[i].includes('[')) {
          const [key, index] = keys[i].replace(']', '').split('[');
          obj = obj[key][parseInt(index)];
        } else {
          obj = obj[keys[i]];
        }
      }
      obj[keys[keys.length - 1]] = value;
      return newData;
    });
    setHasUnsavedChanges(true); // Mark as edited
  };

  // Update account in preview
  //
  // Feb 2026 UX fix — when the user edits an account's display_name,
  // that line contains its own dollar figure (e.g. "CAP ONE 8-2024
  // $1,500").  If the section's total_fraud_amount stays frozen at the
  // value we computed on preview-open, the section header will show a
  // different total than the sum of the edited lines and the final PDF
  // will render whichever field the backend reads first.  We now
  // re-derive total_fraud_amount from the dollar figures in every
  // account row of the section AFTER each edit, so header + rows + PDF
  // always agree with the user's most recent edits.
  const _extractDollars = (str) => {
    if (!str) return 0;
    const m = String(str).match(/\$\s*([\d]+(?:[.,]\d+)*)/) || String(str).match(/([\d]+(?:[.,]\d+)*)\s*$/);
    if (!m) return 0;
    return parseFloat(m[1].replace(/,/g, '')) || 0;
  };
  const updatePreviewAccount = (sectionIndex, accountIndex, field, value) => {
    setPreviewData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const section = newData.sections[sectionIndex];
      section.accounts[accountIndex][field] = value;
      if (field === 'display_name') {
        const p = parseFtcLine(value);
        if (p.balance) section.accounts[accountIndex].balance = p.balance;
        if (p.date) section.accounts[accountIndex].date_opened = p.date;
      }
      // If display_name (or balance) changed, recompute the section
      // total from ALL rows so header/PDF/summary stay in lockstep.
      if (field === 'display_name' || field === 'balance') {
        const total = section.accounts.reduce((sum, acc) => {
          // Prefer the amount encoded in the edited display_name line —
          // that's the single visible source of truth the user sees.
          const fromLine = _extractDollars(acc.display_name);
          return sum + (fromLine || parseFloat(acc.balance || 0) || 0);
        }, 0);
        section.total_fraud_amount = String(Math.round(total));
      }
      return newData;
    });
    setHasUnsavedChanges(true); // Mark as edited
  };

  // Update section in preview
  const updatePreviewSection = (sectionIndex, field, value) => {
    setPreviewData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.sections[sectionIndex][field] = value;
      return newData;
    });
    setHasUnsavedChanges(true); // Mark as edited
  };

  // Close preview
  const closePreview = () => {
    if (hasUnsavedChanges) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close without generating the PDF?');
      if (!confirmClose) return;
    }
    setShowPreview(false);
    setPreviewData(null);
    setOriginalPreviewData(null);
    setHasUnsavedChanges(false);
  };

  // 2026-03-02 — Debounced live-PDF builder. Regenerates the actual
  // PDF from current previewData (same params as downloadFromPreview
  // — so what you see IS what you'll get) and shows it in an iframe.
  // 2026-03-02 — Debounced live-PDF builder. Regenerates the actual
  // PDF from current previewData (same params as downloadFromPreview
  // — so what you see IS what you'll get) and rasterizes it to PNGs
  // so it renders in EVERY browser (including iPhone Safari, which
  // can't inline-render blob PDFs).
  const buildLivePdf = React.useCallback(async () => {
    if (!previewData) return;
    setLivePdfBuilding(true);
    setLivePdfError(null);
    try {
      const formattedSections = previewData.sections.map(sec => ({
        ftc_section: sec.ftc_section,
        fraud_type: sec.fraud_type,
        accounts: sec.accounts.map(acc => ({
          creditor_name: acc.display_name || acc.creditor_name,
          account_number: acc.account_number || '',
          date_opened: acc.date_opened || '',
          balance: acc.balance || '0',
        })),
        total_fraud_amount: sec.total_fraud_amount,
        date_fraud_began: sec.date_fraud_began,
        date_discovered: sec.date_discovered,
      }));
      const payload = {
        client_info: previewData.clientInfo,
        client_id: clientId,
        ftc_sections: formattedSections,
        custom_statement: previewData.personalStatement,
        signature_data: previewData.signatureData,
        custom_report_date: previewData.reportDate || null,
        custom_report_time: previewData.reportTime || null,
        custom_report_number: previewData.reportNumber || null,
        theme: previewData.theme || 'color',
        date_format: previewData.dateFormat || dateFormat || 'month_year',
      };
      // Fetch the rasterized PNG pages
      const r = await axios.post(`${API}/api/ftc-report/live-preview-pages`, payload);
      const pages = r.data?.pages || [];
      setLivePdfPages(pages);
      // Also fetch the raw PDF for the "Open in new tab" convenience.
      // Runs in parallel with the render — no user-visible delay.
      axios.post(`${API}/api/ftc-affidavit/generate-standalone`, payload, { responseType: 'blob' })
        .then(rr => {
          const blob = new Blob([rr.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setLivePdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
        })
        .catch(() => {/* silent — main render already succeeded */});
    } catch (e) {
      console.error('[live-pdf]', e);
      const msg = e?.response?.data?.detail
        || (typeof e?.response?.data === 'string' ? e.response.data : (e?.message || 'Unknown error'));
      setLivePdfError(String(msg).slice(0, 260));
    } finally {
      setLivePdfBuilding(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewData, clientId, dateFormat]);

  // Auto-rebuild the live PDF whenever the pane is open AND the
  // previewData changes.  Debounced to 900ms so keystrokes coalesce.
  React.useEffect(() => {
    if (!showLivePdf || !previewData) return;
    const t = setTimeout(() => { buildLivePdf(); }, 900);
    return () => clearTimeout(t);
  }, [showLivePdf, previewData, buildLivePdf]);

  // Cleanup blob URL when the preview modal closes
  React.useEffect(() => {
    if (!showPreview && livePdfUrl) {
      URL.revokeObjectURL(livePdfUrl);
      setLivePdfUrl(null);
      setLivePdfPages([]);
      setLivePdfError(null);
      setShowLivePdf(false);
    }
  }, [showPreview, livePdfUrl]);

  // Generate and download from EDITED preview data
  const downloadFromPreview = async () => {
    if (!previewData) return;
    
    setGeneratingFromPreview(true);
    
    try {
      // Format sections from edited preview data
      // CRITICAL: Use display_name (editable field) as creditor_name for the PDF
      const formattedSections = previewData.sections.map(sec => ({
        ftc_section: sec.ftc_section,
        fraud_type: sec.fraud_type,
        accounts: sec.accounts.map(acc => ({
          // Use the edited display_name as creditor_name - this is what appears on the PDF
          creditor_name: acc.display_name || acc.creditor_name,
          account_number: acc.account_number || '',
          date_opened: acc.date_opened || '',
          balance: acc.balance || '0'
        })),
        total_fraud_amount: sec.total_fraud_amount,
        date_fraud_began: sec.date_fraud_began,
        date_discovered: sec.date_discovered
      }));
      if (!(await preflightOk(previewData.clientInfo, formattedSections, previewData.personalStatement))) { setGeneratingFromPreview(false); return; }
      
      const response = await axios.post(
        `${API}/api/ftc-affidavit/generate-standalone`,
        {
          client_info: previewData.clientInfo,
          client_id: clientId,
          ftc_sections: formattedSections,
          custom_statement: previewData.personalStatement,
          signature_data: previewData.signatureData,
          custom_report_date: previewData.reportDate || null,
          custom_report_time: previewData.reportTime || null,
          custom_report_number: previewData.reportNumber || null,
          theme: previewData.theme || 'color',
          date_format: previewData.dateFormat || dateFormat || 'month_year'  // Include date format from preview or prop (default: strip day)
        },
        { responseType: 'blob' }
      );

      onClientInfoEdited?.(previewData.clientInfo); // keep the builder (and saved report) in sync with preview edits
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadName = `IDTheftReport_${previewData.reportNumber || Date.now()}.pdf`;
      // 2026-02-28 iPhone bug fix — mobile-safe.
      await downloadBlobMobileSafe(blob, downloadName, {
        shareTitle: `FTC Identity Theft Report`,
      });

      // Save preview edits (names, dates, balances, client info) back to the saved report.
      const accountOverrides = {};
      previewData.sections.forEach((sec) => sec.accounts.forEach((acc) => {
        if (!acc.id) return;
        const p = parseFtcLine(acc.display_name || acc.creditor_name);
        const name = p.name || acc.creditor_name;
        accountOverrides[acc.id] = { creditor_name: name, display_name: name, balance: Number(p.balance || acc.balance || 0), ...(p.date ? { date_opened: p.date, date_fraud_began: p.date } : {}) };
      }));
      setAccounts((prev) => prev.map((a) => (accountOverrides[a.id] ? { ...a, ...accountOverrides[a.id] } : a)));
      try { saveCompletedReportLog({ accountOverrides, clientInfo: previewData.clientInfo }).catch(() => {}); } catch { /* ignore */ }

      // Capture for Mail Report flow (do NOT auto-push into the queue —
      // user requested manual control over which docs get mailed).
      try {
        const ftcFile = new File([blob], downloadName, { type: 'application/pdf' });
        setLastGeneratedFtcBlob({ file: ftcFile, name: downloadName, size: ftcFile.size });
      } catch (e) {
        setLastGeneratedFtcBlob({ blob, name: downloadName, size: blob.size });
      }

      toast.success('FTC Report with your edits downloaded!');
      setHasUnsavedChanges(false); // Reset changes state
      setShowPreview(false);
      setPreviewData(null);
      setOriginalPreviewData(null);
    } catch (error) {
      console.error('Error generating from preview:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingFromPreview(false);
    }
  };

  const formatCurrency = (amt) => `$${(amt || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="ftc-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        <span className="ml-3 text-zinc-400">Loading accounts...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ftc-report-builder">
      <FtcPreflightDialog
        preflight={preflight}
        onClose={(ok) => { preflight?.resolve(ok); setPreflight(null); }}
        onEditClientInfo={onEditClientInfo && (() => { if (showPreview && previewData?.clientInfo) onClientInfoEdited?.(previewData.clientInfo); setShowPreview(false); onEditClientInfo(); })}
      />
      {/* Header with Grand Total */}
      <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Shield className="w-10 h-10 text-orange-500" />
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">FTC Identity Theft Report</h2>
                <p className="text-zinc-400">Section → Fraud Type → Add Accounts</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              {/* Save Status Indicator */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                saveStatus === 'saving' ? 'bg-blue-500/20 text-blue-300' :
                saveStatus === 'saved' ? 'bg-green-500/20 text-green-300' :
                'bg-red-500/20 text-red-300'
              }`}>
                {saveStatus === 'saving' ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Saving...
                  </>
                ) : saveStatus === 'saved' ? (
                  <>
                    <CheckCircle2 className="w-3 h-3" />
                    Saved
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3 h-3" />
                    Save failed
                  </>
                )}
              </div>
              
              {signatureData ? (
                <Badge className="bg-green-600 text-white px-3 py-1">✓ Signature Ready</Badge>
              ) : (
                <Badge className="bg-yellow-600 text-white px-3 py-1">No Signature</Badge>
              )}
              <div className="text-right bg-zinc-900/80 p-4 rounded-lg">
                <p className="text-sm text-zinc-400">TOTAL FRAUD AMOUNT</p>
                <p className="text-3xl font-bold text-red-400" data-testid="total-fraud-amount">
                  {formatCurrency(getTotalFraudAmount())}
                </p>
                <p className="text-sm text-zinc-500">{getTotalAccountsAssigned()} account(s)</p>
              </div>
              {/* 2026-02-08 — Per user: the "How do you want to edit this
                  report?" prompt should only fire from a deliberate click,
                  not on load. This button is only visible when the builder
                  is showing a saved report (canOpenEditIntent=true). */}
              {canOpenEditIntent && onOpenEditIntent && (
                <Button
                  type="button"
                  onClick={onOpenEditIntent}
                  className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-4 py-2 shadow-lg shadow-amber-900/40"
                  data-testid="ftc-edit-report-btn"
                  title="Pick how to edit this saved report (edit as-is, add to open slot, or replace a section)"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Report
                </Button>
              )}
              {/* 2026-02-08 — Per user: "add a DONE AND SAVE button that
                  will save and take you directly back to the dashboard".
                  Snapshots the full editor state via saveCompletedReportLog
                  and hops straight to `/` on success. */}
              <Button
                type="button"
                onClick={async () => {
                  const result = await saveCompletedReportLog();
                  if (result?.id) {
                    setTimeout(() => navigate('/'), 400);
                  }
                }}
                disabled={savingReportLog}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 shadow-lg shadow-emerald-900/40 disabled:opacity-60"
                data-testid="ftc-done-and-save-btn"
                title="Save this report and return to the dashboard"
              >
                {savingReportLog ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Done &amp; Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Statement */}
      <Card className="bg-zinc-900/50 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white">Personal Statement</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Version Selector Buttons */}
          <div className="flex flex-wrap gap-2">
            {STATEMENT_OPTIONS.map((option) => (
              <Button
                key={option.id}
                variant={selectedStatementVersion === option.id ? "default" : "outline"}
                size="sm"
                onClick={() => handleStatementVersionChange(option.id)}
                className={selectedStatementVersion === option.id 
                  ? "bg-orange-500 hover:bg-orange-600 text-white" 
                  : "border-zinc-600 text-zinc-300 hover:bg-zinc-800"}
                data-testid={`statement-version-${option.id}`}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Textarea
            value={personalStatement}
            onChange={(e) => setPersonalStatement(e.target.value)}
            className="min-h-32 bg-zinc-800 border-zinc-600 text-white"
            data-testid="personal-statement"
          />
          {/* Feb 2026 — Rewrite button + status.  Every generation is
              LLM-paraphrased so no two clients share the same statement
              text, even when they pick the same version. */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              disabled={rewritingStatement}
              onClick={() => {
                const selected = STATEMENT_OPTIONS.find(s => s.id === selectedStatementVersion);
                rewriteStatementForVersion(selectedStatementVersion, selected?.text || personalStatement);
              }}
              className="border-purple-500/50 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 text-xs font-bold"
              data-testid="statement-rewrite-btn"
            >
              {rewritingStatement ? '🔄 Rewriting…' : '🔄 Rewrite unique version'}
            </Button>
            <span className="text-[11px] text-zinc-500">
              Every version keeps the exact facts of the original — only the wording changes per client.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Main Workflow */}
      <Card className="bg-zinc-900/50 border-zinc-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              {/* Clickable step-strip.  Nov 2026 — user asked to be able
                  to jump back to any of the 3 steps without losing what
                  they typed.  We DON'T reset anything on click, we just
                  switch which tab is visible. */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { tab: 'section', label: '1. Section' },
                  { tab: 'fraud', label: '2. Fraud Type' },
                  { tab: 'accounts', label: '3. Accounts' },
                ].map((s, i, arr) => {
                  const isActive = currentTab === s.tab;
                  // "Fraud" and "Accounts" only make sense after a section
                  // has been picked — disable them until then.
                  const canClick = s.tab === 'section' || selectedSection;
                  return (
                    <React.Fragment key={s.tab}>
                      <button
                        type="button"
                        onClick={() => { if (canClick) setCurrentTab(s.tab); }}
                        disabled={!canClick}
                        className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${
                          isActive
                            ? 'bg-green-600 text-white shadow-lg shadow-green-500/20'
                            : canClick
                              ? 'bg-green-500/15 border border-green-500/40 text-green-200 hover:bg-green-500/25'
                              : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                        }`}
                        data-testid={`ftc-step-jump-${s.tab}`}
                      >
                        {s.label}
                      </button>
                      {i < arr.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-zinc-600" />
                      )}
                    </React.Fragment>
                  );
                })}
                {selectedSection && currentTab !== 'section' && (
                  <span className="text-sm text-zinc-400 ml-2">
                    · Section <span className="text-white font-bold">{selectedSection}</span>
                  </span>
                )}
              </div>
            </CardTitle>
            {currentTab !== 'section' && (
              <Button variant="outline" size="sm" onClick={goBack}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          
          {/* TAB 1: Select Section */}
          {currentTab === 'section' && (
            <div className="space-y-3" data-testid="section-selection">
              <p className="text-zinc-400 mb-4">Click a section to add accounts or edit an existing section:</p>
              {[1, 2, 3, 4, 5].map(num => {
                const sec = sections[num];
                const hasAccounts = sec.accountIds.size > 0;
                const sectionTotal = getSectionTotal(num);
                const fraudLabel = FRAUD_TYPES.find(f => f.value === sec.fraudType)?.label;
                const locked = lockedSections.includes(num);
                
                return (
                  <div
                    key={num}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      hasAccounts 
                        ? 'border-green-500/50 bg-green-500/10' 
                        : 'border-zinc-700 bg-zinc-800/30'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <span className="font-bold text-white text-lg">Section {num}</span>
                          {hasAccounts && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                          {locked && <Badge className="bg-zinc-700 text-zinc-200" data-testid={`section-locked-${num}`}>Locked · old accounts</Badge>}
                        </div>
                        {fraudLabel && (
                          <p className="text-sm text-zinc-400 mt-1">{fraudLabel}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        {hasAccounts && (
                          <>
                            <Badge className="bg-green-600">{sec.accountIds.size} accounts</Badge>
                            <span className="text-green-400 font-bold text-lg">{formatCurrency(sectionTotal)}</span>
                          </>
                        )}
                        <div className="flex gap-2">
                          {hasAccounts && !locked && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => clearSection(num)}
                              className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            disabled={locked}
                            data-testid={`section-select-${num}`}
                            onClick={() => selectSection(num)}
                            className={hasAccounts ? "bg-blue-600 hover:bg-blue-700" : "bg-orange-600 hover:bg-orange-700"}
                          >
                            {hasAccounts ? (
                              <><Edit2 className="w-4 h-4 mr-1" /> Edit</>
                            ) : (
                              <><Plus className="w-4 h-4 mr-1" /> Add</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <SectionAccountList
                      num={num}
                      accounts={(accounts || []).filter((a) => sec.accountIds.has(a.id))}
                      onUpdate={(id, field, value) => { updateAccountField(id, field, value); if (field === 'date_fraud_began') setDateDiscoveredPerAccount((p) => ({ ...p, [id]: value })); }}
                      onRemove={(id) => { removeFromSection(num, id); setAccounts((prev) => prev.filter((a) => a.id !== id)); }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 2: Select Fraud Type - with account reference on right */}
          {currentTab === 'fraud' && (
            <div className="grid grid-cols-3 gap-4" data-testid="fraud-type-selection">
              {/* Left side: Fraud Types — compact 2-column grid so all 11
                  options fit on one screen without scrolling (user request
                  2026-06-05). On wider screens we bump to 3 columns. */}
              <div className="col-span-2">
                <p className="text-zinc-400 mb-3">Select the type of fraud for Section {selectedSection}:</p>
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
                  {FRAUD_TYPES.map(type => {
                    // Split the label "1. Credit card opened by the thief" into
                    // a small number badge + a tight title for the tile.
                    const m = type.label.match(/^(\d+)\.\s*(.+)$/);
                    const num = m ? m[1] : '';
                    const title = m ? m[2] : type.label;
                    const isSelected = selectedFraudType === type.value;
                    const Icon = type.Icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => selectFraudType(type.value)}
                        data-testid={`fraud-type-${type.value}`}
                        className={`group relative p-2.5 rounded-lg border-2 text-left transition-all hover:border-orange-500/60 ${
                          isSelected
                            ? 'border-orange-500 bg-orange-500/20 shadow-[0_0_0_1px_rgba(249,115,22,0.4)]'
                            : `${type.ring} ${type.tint} hover:bg-zinc-800/70`
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${isSelected ? 'bg-orange-500 text-white' : 'bg-zinc-700 text-zinc-300 group-hover:bg-zinc-600'}`}>
                            {num}
                          </span>
                          {Icon && (
                            <Icon className={`flex-shrink-0 w-4 h-4 mt-0.5 ${isSelected ? 'text-orange-300' : type.color}`} />
                          )}
                          <span className={`text-xs leading-snug ${isSelected ? 'text-white font-semibold' : 'text-zinc-200'}`}>
                            {title}
                          </span>
                        </div>
                        {isSelected && (
                          <span className="absolute top-1 right-1.5 text-orange-400 text-base leading-none">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Right side: Account Reference */}
              <div className="col-span-1">
                <div className="sticky top-0 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                  <h4 className="font-bold text-blue-400 mb-3 text-sm">📋 Quick Reference</h4>
                  
                  {/* Unassigned Accounts — grouped by category so the
                      admin can see at a glance what type each account
                      is (Credit Card / Auto / etc.) and drop it into
                      the right FTC section. */}
                  <div className="mb-4">
                    <p className="text-xs text-orange-400 font-medium mb-2">
                      Unassigned ({getUnassignedAccounts().length})
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {getUnassignedAccounts().length === 0 ? (
                        <p className="text-xs text-zinc-500">All accounts assigned</p>
                      ) : (
                        getUnassignedAccountsGrouped().map(group => {
                          // Color-code the category tag so the eye picks it
                          // out at a glance while scanning the list.
                          const CAT_COLOR = {
                            AUTO:          'bg-red-500/20 text-red-300 border-red-500/40',
                            CREDIT_CARD:   'bg-blue-500/20 text-blue-300 border-blue-500/40',
                            CREDIT_UNION:  'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
                            PERSONAL_LOAN: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
                            STUDENT_LOAN:  'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
                            MORTGAGE:      'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                            UTILITY:       'bg-teal-500/20 text-teal-300 border-teal-500/40',
                            MEDICAL:       'bg-pink-500/20 text-pink-300 border-pink-500/40',
                            COLLECTION:    'bg-rose-500/20 text-rose-300 border-rose-500/40',
                            OTHER:         'bg-zinc-500/20 text-zinc-300 border-zinc-500/40',
                          };
                          const tagCls = CAT_COLOR[group.category] || CAT_COLOR.OTHER;
                          return (
                            <div key={group.category} data-testid={`quickref-group-${group.category}`}>
                              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">
                                {group.label} · {group.items.length}
                              </div>
                              <div className="space-y-1">
                                {group.items.map(acc => (
                                  <div
                                    key={acc.id}
                                    className="text-xs text-zinc-200 p-1.5 bg-zinc-700/50 rounded flex items-center gap-2"
                                    data-testid={`quickref-unassigned-${acc.id}`}
                                  >
                                    <span className={`shrink-0 inline-block px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider leading-none ${tagCls}`}>
                                      {group.label}
                                    </span>
                                    <span className="truncate flex-1 min-w-0">{acc.creditor_name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  
                  {/* Already Assigned */}
                  <div>
                    <p className="text-xs text-green-400 font-medium mb-2">
                      Assigned ({getTotalAccountsAssigned()})
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {getTotalAccountsAssigned() === 0 ? (
                        <p className="text-xs text-zinc-500">No accounts assigned yet</p>
                      ) : (
                        Object.entries(sections).map(([num, sec]) => 
                          sec.accountIds.size > 0 && (
                            <div key={num} className="text-xs p-2 bg-green-500/10 border border-green-500/30 rounded" data-testid={`quickref-assigned-section-${num}`}>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-green-400 font-bold">Section {num}</span>
                                <span className="text-zinc-400">({sec.accountIds.size})</span>
                              </div>
                              {/* Feb 2026 — show the fraud-type label the
                                  user picked in Step 2 so this side reads
                                  as the SAME reference they'd see on the
                                  Fraud Type screen (and the AI extractor
                                  category tag). Falls back gracefully if
                                  a fraud type hasn't been chosen yet. */}
                              {sec.fraudType && (() => {
                                const ft = FRAUD_TYPES.find(t => t.value === sec.fraudType);
                                if (!ft) return null;
                                // Strip the leading "N. " numbering so the
                                // section number isn't shown twice.
                                const cleanLabel = ft.label.replace(/^\d+\.\s*/, '');
                                return (
                                  <div className="text-orange-300 text-[10px] font-semibold uppercase tracking-wide mt-0.5">
                                    {cleanLabel}
                                  </div>
                                );
                              })()}
                              <div className="text-zinc-300 text-[11px] leading-snug mt-1">
                                {accounts.filter(a => sec.accountIds.has(a.id)).map(a => a.creditor_name).join(', ')}
                              </div>
                            </div>
                          )
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Add Accounts (Multi-select) */}
          {currentTab === 'accounts' && (
            <div className="space-y-4" data-testid="account-selection">
              {/* TOP NAVIGATION BUTTONS - Add to section | Next section | Done */}
              <div className="flex flex-row gap-2 items-center pb-4 border-b border-zinc-700 sticky top-0 bg-zinc-900/95 backdrop-blur-sm z-10 -mx-4 px-4 pt-2">
                <Button 
                  onClick={addSelectedToSection}
                  disabled={tempSelected.size === 0}
                  className="bg-green-600 hover:bg-green-700 text-lg px-6 py-3"
                  data-testid="add-to-section-btn-top"
                >
                  Add to section
                </Button>
                <Button 
                  onClick={goToNextSection}
                  className="bg-blue-600 hover:bg-blue-700 text-lg px-6 py-3"
                  data-testid="next-section-btn-top"
                >
                  Next section
                </Button>
                <Button 
                  onClick={goToSectionList}
                  className="bg-orange-600 hover:bg-orange-700 text-lg px-6 py-3"
                  data-testid="done-btn-top"
                >
                  Done
                </Button>
                <span className="ml-auto text-zinc-400 text-sm">
                  {tempSelected.size > 0 && `${tempSelected.size} selected`}
                </span>
              </div>

              {/* Already assigned to this section */}
              {sections[selectedSection]?.accountIds.size > 0 && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-green-400">
                      Accounts in Section {selectedSection} ({sections[selectedSection].accountIds.size})
                    </h4>
                    <span className="text-green-400 font-bold">
                      {formatCurrency(getSectionTotal(selectedSection))}
                    </span>
                  </div>
                  
                  {/* Section-level inputs for Date Fraud Began and Custom Total */}
                  {sections[selectedSection].accountIds.size > 0 && (
                    <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div>
                        <label className="text-xs text-orange-400 block mb-1 font-semibold">
                          Date Fraud Began (oldest date in section)
                        </label>
                        <input
                          type="text"
                          placeholder="M/YYYY"
                          value={getDateDiscoveredForSection(selectedSection)}
                          onChange={(e) => updateDateDiscoveredForSection(selectedSection, e.target.value)}
                          className="w-full bg-zinc-700 border border-orange-500/30 text-white rounded px-2 py-1 text-sm"
                          data-testid={`section-date-fraud-began-${selectedSection}`}
                        />
                        <p className="text-xs text-zinc-500 mt-0.5">Auto-set to oldest account date</p>
                      </div>
                      <div>
                        <label className="text-xs text-cyan-400 block mb-1 font-semibold">
                          Date Discovered
                        </label>
                        <input
                          type="text"
                          placeholder="M/YYYY"
                          value={getCurrentMonthYear()}
                          readOnly
                          className="w-full bg-zinc-700 border border-cyan-500/30 text-cyan-400 rounded px-2 py-1 text-sm"
                          data-testid={`section-date-discovered-${selectedSection}`}
                        />
                        <p className="text-xs text-zinc-500 mt-0.5">Current month/year</p>
                      </div>
                      <div>
                        <label className="text-xs text-yellow-400 block mb-1 font-semibold">
                          Custom Section Total (optional)
                        </label>
                        <input
                          type="text"
                          placeholder={`Auto: ${formatCurrency(calculateSectionAutoTotal(selectedSection))}`}
                          value={sections[selectedSection]?.customTotalAmount || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9.]/g, '');
                            updateSectionTotalAmount(selectedSection, val);
                          }}
                          className="w-full bg-zinc-700 border border-yellow-500/30 text-yellow-400 rounded px-2 py-1 text-sm"
                          data-testid={`section-total-${selectedSection}`}
                        />
                        <p className="text-xs text-zinc-500 mt-0.5">
                          Auto-sum: {formatCurrency(calculateSectionAutoTotal(selectedSection))}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {accounts
                      .filter(a => sections[selectedSection].accountIds.has(a.id))
                      .map(acc => (
                        <div key={acc.id} className="bg-zinc-800/50 p-3 rounded">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-green-400 text-sm font-medium">✓ Added to Section {selectedSection}</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => removeFromSection(selectedSection, acc.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {/* Editable fields for assigned accounts */}
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-blue-400 block mb-1">Account Name</label>
                                <input
                                  type="text"
                                  value={acc.creditor_name || ''}
                                  onChange={(e) => updateAccountField(acc.id, 'creditor_name', e.target.value)}
                                  className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-green-400 block mb-1">Fraud Amount</label>
                                <input
                                  type="text"
                                  value={acc.balance || ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.]/g, '');
                                    updateAccountField(acc.id, 'balance', parseFloat(val) || 0);
                                  }}
                                  className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-sm"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-purple-400 block mb-1">Account #</label>
                                <input
                                  type="text"
                                  placeholder="Optional"
                                  value={acc.account_number || ''}
                                  onChange={(e) => updateAccountField(acc.id, 'account_number', e.target.value)}
                                  className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-orange-400 block mb-1">Date Fraud Began</label>
                                <input
                                  type="text"
                                  placeholder="M/YYYY"
                                  value={getDateDiscoveredForAccount(acc.id)}
                                  onChange={(e) => updateDateDiscoveredForAccount(acc.id, e.target.value)}
                                  className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-sm"
                                  data-testid={`date-discovered-assigned-${acc.id}`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Add more accounts */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-zinc-300">Select accounts to add:</h4>
                    <span className="text-[11px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
                      {getUnassignedAccounts().length} unassigned
                    </span>
                  </div>
                  {/* View switcher */}
                  <div className="inline-flex bg-zinc-900 border border-zinc-700 rounded-lg p-1" data-testid="account-view-switcher">
                    {[
                      { id: 'compact', label: '📋 Compact' },
                      { id: 'cards',   label: '🟦 Cards' },
                      { id: 'table',   label: '📊 Table' },
                    ].map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setAccountPickerView(v.id)}
                        data-testid={`account-view-${v.id}`}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                          accountPickerView === v.id
                            ? 'bg-orange-500 text-white shadow'
                            : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllUnassigned}>
                      Select All
                    </Button>
                    <Button size="sm" variant="outline" onClick={clearTempSelection}>
                      Clear
                    </Button>
                  </div>
                </div>

                <div className={`overflow-y-auto ${accountPickerView === 'compact' || accountPickerView === 'table' ? 'rounded border border-zinc-700' : ''}`} style={{ maxHeight: '480px' }}>
                  {getUnassignedAccounts().length === 0 ? (
                    <p className="text-zinc-500 text-center py-4">All accounts have been assigned to sections</p>
                  ) : accountPickerView === 'compact' ? (
                    <div className="divide-y divide-zinc-800">
                      {getUnassignedAccountsGrouped().map((g) => (
                        <React.Fragment key={g.category}>
                          <div
                            className="px-3 py-1.5 bg-zinc-800/70 border-y border-zinc-700 text-[11px] font-bold uppercase tracking-wider text-blue-300"
                            data-testid={`unassigned-group-header-${g.category}`}
                          >
                            {g.label}
                            <span className="ml-2 text-zinc-500 font-normal normal-case tracking-normal">
                              ({g.items.length})
                            </span>
                          </div>
                          {g.items.map((acc, idx) => {
                        const isSelected = tempSelected.has(acc.id);
                        // Per user request 2026-06-05: when an account is
                        // selected to be added to a section, expose the
                        // inline editors automatically so the user can fix
                        // the name, amount, or date BEFORE it goes into the
                        // section. Otherwise, keep the row tight.
                        const isExpanded = expandedAccounts.has(acc.id) || isSelected;
                        const last4 = (acc.account_number || '').slice(-4);
                        return (
                          <div key={`${acc.id}_${idx}`} className={isSelected ? 'bg-orange-500/10 border-l-2 border-orange-500' : ''} data-testid={`account-compact-row-${acc.id}`}>
                            <div className="flex items-center gap-3 px-3 py-2">
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleTempSelect(acc.id)} />
                              <span className="text-white text-sm font-medium flex-1 truncate cursor-pointer" onClick={() => toggleTempSelect(acc.id)}>
                                {canonicalAccountLabel(acc) || 'Untitled account'}
                              </span>
                              <span className="text-emerald-400 text-sm font-semibold">
                                ${typeof acc.balance === 'number' ? acc.balance.toLocaleString() : (acc.balance || '0')}
                              </span>
                              {last4 && <span className="text-purple-300 text-xs w-16 text-right">•••• {last4}</span>}
                              <button
                                type="button"
                                onClick={() => toggleAccountExpand(acc.id)}
                                className="text-zinc-400 hover:text-white text-xs w-6 text-center"
                                aria-label="Expand"
                                data-testid={`account-compact-expand-${acc.id}`}
                              >
                                {isExpanded ? '▾' : '▸'}
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="px-12 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <div>
                                  <label className="text-[10px] text-blue-400 block mb-1">Name</label>
                                  <input type="text" value={acc.display_name || canonicalAccountLabel(acc) || ''}
                                    onChange={(e) => { updateAccountField(acc.id, 'display_name', e.target.value); updateAccountField(acc.id, 'creditor_name', e.target.value.split('-')[0].trim()); }}
                                    className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-xs" data-testid={`account-name-${acc.id}`} />
                                </div>
                                <div>
                                  <label className="text-[10px] text-green-400 block mb-1">Amount</label>
                                  <input type="text" value={acc.balance || ''}
                                    onChange={(e) => { const val = e.target.value.replace(/[^0-9.]/g, ''); updateAccountField(acc.id, 'balance', parseFloat(val) || 0); }}
                                    className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-xs" data-testid={`account-amount-${acc.id}`} />
                                </div>
                                <div>
                                  <label className="text-[10px] text-purple-400 block mb-1">Account #</label>
                                  <input type="text" value={acc.account_number || ''}
                                    onChange={(e) => updateAccountField(acc.id, 'account_number', e.target.value)}
                                    className="w-full bg-zinc-700 border border-zinc-600 text-white rounded px-2 py-1 text-xs" data-testid={`account-number-${acc.id}`} />
                                </div>
                                <div>
                                  <label className="text-[10px] text-cyan-400 block mb-1">Date Discovered</label>
                                  <input type="text" placeholder="M/YYYY" value={acc.date_discovered || getCurrentMonthYear()}
                                    onChange={(e) => updateAccountField(acc.id, 'date_discovered', e.target.value)}
                                    className="w-full bg-zinc-700 border border-cyan-500/30 text-white rounded px-2 py-1 text-xs" data-testid={`date-discovered-${acc.id}`} />
                                </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </React.Fragment>
                  ))}
                </div>
                  ) : accountPickerView === 'cards' ? (
                    <div className="p-1 space-y-3">
                      {getUnassignedAccountsGrouped().map((g) => (
                        <div key={g.category}>
                          <div
                            className="mb-2 px-2 py-1 bg-zinc-800/70 border-l-2 border-blue-400 text-xs font-bold uppercase tracking-wider text-blue-300"
                            data-testid={`unassigned-group-header-cards-${g.category}`}
                          >
                            {g.label}
                            <span className="ml-2 text-zinc-500 font-normal normal-case tracking-normal">
                              ({g.items.length})
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {g.items.map((acc, idx) => {
                        const isSelected = tempSelected.has(acc.id);
                        const last4 = (acc.account_number || '').slice(-4);
                        return (
                          <div key={`${acc.id}_${idx}`}
                               className={`rounded-lg border p-2 ${isSelected ? 'bg-orange-500/15 border-orange-500/50' : 'bg-zinc-800/40 border-zinc-700'}`}
                               data-testid={`account-card-${acc.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleTempSelect(acc.id)} />
                              <span className="text-white text-sm font-semibold flex-1 truncate cursor-pointer" onClick={() => toggleTempSelect(acc.id)}>
                                {canonicalAccountLabel(acc) || 'Untitled'}
                              </span>
                              <span className="text-emerald-400 text-sm font-bold">
                                ${typeof acc.balance === 'number' ? acc.balance.toLocaleString() : (acc.balance || '0')}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 ml-6">
                              <input type="text" placeholder="Account #" value={acc.account_number || ''}
                                onChange={(e) => updateAccountField(acc.id, 'account_number', e.target.value)}
                                className="bg-zinc-700 border border-zinc-600 rounded px-2 py-0.5 text-xs text-white"
                                data-testid={`account-number-${acc.id}`} />
                              <input type="text" placeholder="M/YYYY" value={acc.date_discovered || getCurrentMonthYear()}
                                onChange={(e) => updateAccountField(acc.id, 'date_discovered', e.target.value)}
                                className="bg-zinc-700 border border-cyan-500/30 rounded px-2 py-0.5 text-xs text-white"
                                data-testid={`date-discovered-${acc.id}`} />
                            </div>
                            <div className="grid grid-cols-2 gap-1 mt-1 ml-6">
                              <input type="text" placeholder="Name" value={acc.display_name || canonicalAccountLabel(acc) || ''}
                                onChange={(e) => { updateAccountField(acc.id, 'display_name', e.target.value); updateAccountField(acc.id, 'creditor_name', e.target.value.split('-')[0].trim()); }}
                                className="bg-zinc-700 border border-blue-500/30 rounded px-2 py-0.5 text-xs text-white"
                                data-testid={`account-name-${acc.id}`} />
                              <input type="text" placeholder="Amount" value={acc.balance || ''}
                                onChange={(e) => { const val = e.target.value.replace(/[^0-9.]/g, ''); updateAccountField(acc.id, 'balance', parseFloat(val) || 0); }}
                                className="bg-zinc-700 border border-green-500/30 rounded px-2 py-0.5 text-xs text-white"
                                data-testid={`account-amount-${acc.id}`} />
                            </div>
                          </div>
                        );
                      })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // ── Table view ──
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-800 sticky top-0 z-10">
                        <tr className="text-zinc-400">
                          <th className="px-2 py-2 text-left w-8"></th>
                          <th className="px-2 py-2 text-left">Account Name</th>
                          <th className="px-2 py-2 text-right">Fraud Amount</th>
                          <th className="px-2 py-2 text-left">Account #</th>
                          <th className="px-2 py-2 text-left">Date Discovered</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800">
                        {getUnassignedAccountsGrouped().map((g) => (
                          <React.Fragment key={g.category}>
                            <tr className="bg-zinc-800/70" data-testid={`unassigned-group-header-table-${g.category}`}>
                              <td colSpan={5} className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-300">
                                {g.label}
                                <span className="ml-2 text-zinc-500 font-normal normal-case tracking-normal">
                                  ({g.items.length})
                                </span>
                              </td>
                            </tr>
                            {g.items.map((acc, idx) => {
                          const isSelected = tempSelected.has(acc.id);
                          return (
                            <tr key={`${acc.id}_${idx}`} className={isSelected ? 'bg-orange-500/10' : ''} data-testid={`account-table-row-${acc.id}`}>
                              <td className="px-2 py-1"><Checkbox checked={isSelected} onCheckedChange={() => toggleTempSelect(acc.id)} /></td>
                              <td className="px-2 py-1">
                                <input type="text" value={acc.display_name || canonicalAccountLabel(acc) || ''}
                                  onChange={(e) => { updateAccountField(acc.id, 'display_name', e.target.value); updateAccountField(acc.id, 'creditor_name', e.target.value.split('-')[0].trim()); }}
                                  className="w-full bg-transparent text-white outline-none focus:bg-zinc-800 px-1 py-0.5 rounded"
                                  data-testid={`account-name-${acc.id}`} />
                              </td>
                              <td className="px-2 py-1 text-right">
                                <input type="text" value={acc.balance || ''}
                                  onChange={(e) => { const val = e.target.value.replace(/[^0-9.]/g, ''); updateAccountField(acc.id, 'balance', parseFloat(val) || 0); }}
                                  className="w-24 bg-transparent text-emerald-400 text-right outline-none focus:bg-zinc-800 px-1 py-0.5 rounded"
                                  data-testid={`account-amount-${acc.id}`} />
                              </td>
                              <td className="px-2 py-1">
                                <input type="text" value={acc.account_number || ''}
                                  onChange={(e) => updateAccountField(acc.id, 'account_number', e.target.value)}
                                  className="w-20 bg-transparent text-white outline-none focus:bg-zinc-800 px-1 py-0.5 rounded"
                                  data-testid={`account-number-${acc.id}`} />
                              </td>
                              <td className="px-2 py-1">
                                <input type="text" placeholder="M/YYYY" value={acc.date_discovered || getCurrentMonthYear()}
                                  onChange={(e) => updateAccountField(acc.id, 'date_discovered', e.target.value)}
                                  className="w-24 bg-transparent text-cyan-300 outline-none focus:bg-zinc-800 px-1 py-0.5 rounded"
                                  data-testid={`date-discovered-${acc.id}`} />
                              </td>
                            </tr>
                          );
                        })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* MANUAL ENTRY SECTION - For Custom Accounts/Inquiries */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-zinc-300 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Manual Entry - Add Custom Account/Inquiry
                  </h4>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Account/Company Name</label>
                      <input
                        type="text"
                        placeholder="e.g., CAPITAL ONE, DISCOVER, Hard Inquiry from XYZ Bank"
                        value={manualEntry.creditorName}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, creditorName: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-creditor-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Account Number (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., ****1234"
                        value={manualEntry.accountNumber}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, accountNumber: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-account-number"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Fraud Amount ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={manualEntry.fraudAmount}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, fraudAmount: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-fraud-amount"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-orange-400 block mb-1">Date Fraud Began</label>
                      <input
                        type="text"
                        placeholder="M/YYYY (e.g., 7/2023)"
                        value={manualEntry.dateFraudBegan || extractDateFromAccountName(manualEntry.creditorName)}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, dateFraudBegan: e.target.value }))}
                        className="w-full bg-zinc-800 border border-orange-500/30 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-date-fraud-began"
                      />
                      <p className="text-[10px] text-zinc-500 mt-0.5">Auto-extracted from account name</p>
                    </div>
                    <div>
                      <label className="text-xs text-cyan-400 block mb-1">Date Discovered</label>
                      <input
                        type="text"
                        placeholder="M/YYYY"
                        value={manualEntry.dateDiscovered || getCurrentMonthYear()}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, dateDiscovered: e.target.value }))}
                        className="w-full bg-zinc-800 border border-cyan-500/30 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-date-discovered"
                      />
                      <p className="text-[10px] text-zinc-500 mt-0.5">Defaults to current month/year</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Status</label>
                      <select
                        value={manualEntry.status}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-status"
                      >
                        <option value="FRAUDULENT">Fraudulent</option>
                        <option value="UNAUTHORIZED">Unauthorized</option>
                        <option value="INQUIRY">Unauthorized Inquiry</option>
                        <option value="STOLEN">Stolen Identity</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Date Opened/Inquiry Date (Optional)</label>
                      <input
                        type="text"
                        placeholder="MM/DD/YYYY"
                        value={manualEntry.dateOpened}
                        onChange={(e) => setManualEntry(prev => ({ ...prev, dateOpened: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                        data-testid="manual-date-opened"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 block mb-1">Additional Notes (Optional)</label>
                    <textarea
                      placeholder="e.g., This inquiry was made without my permission, Account opened using stolen identity"
                      value={manualEntry.notes}
                      onChange={(e) => setManualEntry(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm h-20"
                      data-testid="manual-notes"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={addManualEntry}
                      disabled={!manualEntry.creditorName.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="add-manual-entry-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Manual Entry
                    </Button>
                  </div>
                </div>
              </div>

              {/* ALL THREE BUTTONS INLINE: Add to section | Next section | Done */}
              <div className="flex flex-row gap-2 items-center pt-4 border-t border-zinc-700">
                <Button 
                  onClick={addSelectedToSection}
                  disabled={tempSelected.size === 0}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="add-to-section-btn"
                >
                  Add to section
                </Button>
                <Button 
                  onClick={goToNextSection}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="next-section-btn"
                >
                  Next section
                </Button>
                <Button 
                  onClick={goToSectionList}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="done-btn"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Summary with Totals */}
      <Card className="bg-zinc-900/50 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Summary by Section</span>
            <div className="flex items-center gap-2 text-red-400">
              <DollarSign className="w-5 h-5" />
              <span className="text-xl font-bold">Grand Total: {formatCurrency(getTotalFraudAmount())}</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5].map(num => {
              const sec = sections[num];
              const hasData = sec.accountIds.size > 0;
              const sectionTotal = getSectionTotal(num);
              
              return (
                <div 
                  key={num}
                  onClick={() => hasData && selectSection(num)}
                  className={`relative p-4 rounded-lg border text-center cursor-pointer transition-all hover:border-orange-500/50 ${
                    hasData ? 'border-green-500/50 bg-green-500/10' : 'border-zinc-700'
                  }`}
                  data-testid={`summary-section-card-${num}`}
                >
                  {/* Clear/reset button — lets the user wipe a section
                      and start over without leaving the summary grid.
                      (User request Feb 2026: "add a button where I can
                      clear out the section, basically delete it, and
                      start over if I wanted to add something differently
                      in there.") */}
                  {hasData && !lockedSections.includes(num) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Clear Section ${num}? This removes all accounts and the fraud type so you can start over.`)) {
                          clearSection(num);
                        }
                      }}
                      title={`Clear Section ${num}`}
                      className="absolute top-1 right-1 p-1 rounded text-red-400 hover:text-white hover:bg-red-500/70 transition"
                      data-testid={`summary-section-clear-${num}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <p className="font-bold text-white">Section {num}</p>
                  <p className={`text-3xl font-bold ${hasData ? 'text-green-400' : 'text-zinc-600'}`}>
                    {sec.accountIds.size}
                  </p>
                  <p className="text-xs text-zinc-500">accounts</p>
                  {hasData && (
                    <>
                      <p className="text-sm font-bold text-green-400 mt-2">{formatCurrency(sectionTotal)}</p>
                      <p className="text-xs text-blue-400 mt-1">{lockedSections.includes(num) ? 'Locked · old accounts' : 'Click card to edit · trash to clear'}</p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Generate Button */}
      <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
              <div className="shrink-0">
                <h3 className="text-xl font-bold text-white whitespace-nowrap">Generate FTC Report</h3>
                <p className="text-zinc-400">
                  {getTotalAccountsAssigned()} accounts • {formatCurrency(getTotalFraudAmount())} total fraud
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Preview & Edit — opens an inline editor so you can
                    tweak how each account will render before the PDF is
                    generated. Requested 2026-03-02 by the user: "I've
                    always wanted a preview and edit button next to
                    generate — seeing a live version helps me fix long
                    names or merged rows before the PDF is baked in." */}
                <Button
                  onClick={() => handleGenerateClick('preview')}
                  disabled={generatingPreview || generating || getTotalAccountsAssigned() === 0}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-6 text-lg font-bold relative"
                  data-testid="preview-ftc-report-btn"
                  title="Live editable preview — trim long creditor names or fix merged rows before generating the PDF"
                >
                  {generatingPreview ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  )}
                  Preview &amp; Edit Report
                </Button>

                {/* Generate Button - Opens modal first */}
                <Button
                  onClick={() => handleGenerateClick('generate')}
                  disabled={generating || getTotalAccountsAssigned() === 0}
                  className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg font-bold"
                  data-testid="generate-ftc-report-btn"
                >
                  {generating ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Download className="w-5 h-5 mr-2" />
                  )}
                  Generate FTC Report
                </Button>
              </div>
              <p className="text-xs text-blue-300/80 -mt-2 sm:ml-auto sm:text-right">
                💡 <b>Tip:</b> Use "Preview &amp; Edit Report" first — you can trim long creditor names, fix merged rows, and see exactly what the PDF will look like before generating it.
              </p>
              
              {/* Save to Final Package Button */}
              <SaveToFinalPackageButton
                clientId={clientId}
                bureau="ALL"
                docType="FTC_REPORT"
                fileName={`FTC_Report_${clientData?.name?.replace(/\s+/g, '_') || 'Client'}.pdf`}
                sourceStep="ftc_report"
                disabled={generating || getTotalAccountsAssigned() === 0}
                getFileData={async () => {
                  // Generate the FTC report and return the blob
                  // Use generate-standalone endpoint (same as generateReport) for consistent seal rendering
                  const sigDataUrl = signatureData || '';
                  
                  // Format sections properly (same logic as generateReport)
                  const formattedSections = [];
                  Object.entries(sections).forEach(([sectionNum, sec]) => {
                    if (sec.accountIds.size > 0 && sec.fraudType) {
                      const sectionAccounts = [];
                      sec.accountIds.forEach(accountId => {
                        const account = accounts.find(a => a.id === accountId);
                        if (account) {
                          sectionAccounts.push({
                            creditor_name: account.creditor_name,
                            account_number: account.account_number || '',
                            date_opened: account.date_fraud_began || account.date_opened || '',
                            balance: String(account.balance || 0)
                          });
                        }
                      });
                      
                      const sectionTotal = getSectionTotal(parseInt(sectionNum));
                      
                      formattedSections.push({
                        ftc_section: parseInt(sectionNum),
                        fraud_type: sec.fraudType,
                        accounts: sectionAccounts,
                        total_fraud_amount: String(sectionTotal),
                        date_discovered: getDateDiscoveredForSection(sectionNum)
                      });
                    }
                  });
                  
                  const clientInfoForPDF = {
                    name: clientData?.name || '',
                    suffix: clientData?.suffix || '',
                    dob: clientData?.dob || '',
                    ssn: clientData?.ssn || '',
                    address: clientData?.address || '',
                    city: clientData?.city || '',
                    state: clientData?.state || '',
                    zip_code: clientData?.zip_code || '',
                    phone: clientData?.phone || '',
                    email: clientData?.email || ''
                  };
                  if (!(await preflightOk(clientInfoForPDF, formattedSections, personalStatement))) throw new Error('Fix the flagged report details first');
                  
                  const response = await axios.post(
                    `${API}/api/ftc-affidavit/generate-standalone`,
                    {
                      client_info: clientInfoForPDF,
                      client_id: clientId,
                      ftc_sections: formattedSections,
                      custom_statement: personalStatement,
                      signature_data: sigDataUrl,
                      custom_report_date: reportDate || null,
                      custom_report_time: reportTime || null,
                      custom_report_number: reportNumber || null,
                      theme: ftcTheme || 'color'
                    },
                    { responseType: 'blob' }
                  );
                  return response.data;
                }}
              />
            </div>
            
            {/* Abbreviate Accounts Option */}
            <div className="flex items-center gap-3 pt-3 border-t border-zinc-700">
              <Button
                variant={abbreviateAccounts ? "default" : "outline"}
                size="sm"
                onClick={() => setAbbreviateAccounts(!abbreviateAccounts)}
                className={abbreviateAccounts 
                  ? "bg-orange-600 hover:bg-orange-700 text-white" 
                  : "border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                }
                data-testid="abbreviate-accounts-btn"
              >
                {abbreviateAccounts ? "✓ Abbreviate ON" : "Abbreviate Accounts"}
              </Button>
              <span className="text-sm text-zinc-400">
                {abbreviateAccounts 
                  ? "Account names will be shortened and open dates removed to fit more accounts"
                  : "Use when you have many accounts to fit on the report"
                }
              </span>
            </div>

            {/* Report Date & Time (Always visible, pre-filled with current time, editable) */}
            <div className="pt-3 border-t border-zinc-700">
              <div className="flex items-center gap-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex-1">
                  <label className="text-xs text-zinc-400 block mb-1">
                    Report Date <span className="text-purple-400">(Editable)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                    data-testid="report-date-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-zinc-400 block mb-1">
                    Report Time <span className="text-purple-400">(Editable)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="HH:MM AM/PM"
                    value={reportTime}
                    onChange={(e) => setReportTime(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm"
                    data-testid="report-time-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-orange-400 block mb-1">
                    ℹ️ "Date I Discovered" is now per-account
                  </label>
                  <p className="text-xs text-zinc-500">
                    Set individually when selecting accounts above. Default: {defaultDateDiscovered}
                  </p>
                </div>
              </div>
            </div>

            {/* FTC Report Number (Auto-generated but Editable) */}
            <div className="pt-3 border-t border-zinc-700">
              <div className="flex items-center gap-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex-1">
                  <label className="text-xs text-zinc-400 block mb-1">
                    FTC Report Number <span className="text-blue-400">(Auto-generated, editable)</span>
                  </label>
                  {loadingReportNumber ? (
                    <div className="w-full bg-zinc-800 border border-zinc-600 text-zinc-500 rounded px-3 py-2 text-sm font-mono">
                      Loading...
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="FTC Report Number"
                      value={reportNumber}
                      onChange={(e) => setReportNumber(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2 text-sm font-mono"
                      data-testid="ftc-report-number-input"
                    />
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-zinc-500">
                  <div>Next auto: +20</div>
                  <div className="text-blue-400">Edit if needed</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => onComplete?.()} className="bg-purple-600 hover:bg-purple-700">
          Continue to Next Step
        </Button>
      </div>

      {/* ──────── Feb 2026 — Saved Dispute Letters for this client ──────── */}
      <Card className="bg-zinc-900 border-fuchsia-500/30" data-testid="saved-dispute-letters-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-white text-base">
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-fuchsia-400" />
              Saved Dispute Letters
              <span className="text-xs text-fuchsia-300/80 font-normal">
                ({savedDisputeLetters.length})
              </span>
            </span>
            <button
              onClick={refreshSavedDisputeLetters}
              disabled={savedLettersLoading}
              className="text-xs text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1 disabled:opacity-50"
              data-testid="saved-dispute-letters-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${savedLettersLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {savedDisputeLetters.length === 0 ? (
            <p className="text-xs text-zinc-400 py-4">
              No saved dispute letters yet. Every letter you generate below is automatically
              saved here so you can come back and change just the font or date without redoing
              the whole wizard.
            </p>
          ) : (
            <div className="space-y-2" data-testid="saved-dispute-letters-list">
              {savedDisputeLetters.map((l) => {
                const dateStr = l.letter_date
                  || (l.updated_at ? new Date(l.updated_at).toLocaleDateString() : '');
                const typeLabel = l.letter_type === 'second_round'
                  ? 'Second Round'
                  : l.letter_type === 'inquiry_removal'
                    ? 'Inquiry Removal'
                    : l.letter_type === 'chexsystems'
                      ? 'ChexSystems'
                      : l.letter_type === 'business'
                        ? 'Business'
                        : 'Regular';
                return (
                  <div
                    key={l.id}
                    className="flex items-center justify-between gap-3 bg-black/40 border border-white/10 rounded-lg p-3"
                    data-testid={`saved-dispute-letter-row-${l.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-semibold truncate">
                        {typeLabel} · {String(l.bureau).toUpperCase()}
                        <span className="ml-2 text-xs text-fuchsia-300 font-normal">
                          {l.style === 'handwritten' ? '✍️ Handwritten' : l.style === 'both' ? '✍️ + Typed' : '⌨️ Typed'}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
                        {(l.accounts?.length || 0)} account{(l.accounts?.length || 0) === 1 ? '' : 's'} ·
                        Font: {l.body_font || 'times'} · Date: {dateStr || '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openQuickEdit(l)}
                        className="text-[11px] font-semibold uppercase tracking-wider bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-400/40 text-fuchsia-200 px-2.5 py-1.5 rounded-md"
                        data-testid={`saved-letter-quick-edit-${l.id}`}
                        title="Change font / date only, then re-download the PDF"
                      >
                        Quick Edit
                      </button>
                      <button
                        onClick={() => openEditFullLetter(l)}
                        className="text-[11px] font-semibold uppercase tracking-wider bg-white/10 hover:bg-white/20 border border-white/20 text-white px-2.5 py-1.5 rounded-md"
                        data-testid={`saved-letter-edit-full-${l.id}`}
                        title="Open the full wizard hydrated with this letter"
                      >
                        Edit Full
                      </button>
                      <button
                        onClick={() => deleteSavedLetter(l.id)}
                        className="text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-transparent hover:border-red-400/30 p-1.5 rounded-md"
                        data-testid={`saved-letter-delete-${l.id}`}
                        title="Delete this saved letter"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Edit dialog (font + date only) */}
      <Dialog open={!!quickEditLetter} onOpenChange={(v) => { if (!v) setQuickEditLetter(null); }}>
        <DialogContent
          className="bg-zinc-900 border-fuchsia-500/40 text-white max-w-md"
          data-testid="dispute-quick-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <PenLine className="w-4 h-4 text-fuchsia-300" />
              Quick Edit — Font &amp; Date
            </DialogTitle>
          </DialogHeader>
          {quickEditLetter && (
            <div className="space-y-4 pt-1">
              <div className="text-xs text-zinc-400 bg-black/40 border border-white/10 rounded-md p-3">
                Editing <b className="text-white">
                  {quickEditLetter.letter_type === 'second_round' ? 'Second Round' :
                   quickEditLetter.letter_type === 'inquiry_removal' ? 'Inquiry Removal' :
                   quickEditLetter.letter_type === 'chexsystems' ? 'ChexSystems' :
                   quickEditLetter.letter_type === 'business' ? 'Business' :
                   'Regular'}
                </b> letter to <b className="text-white">{String(quickEditLetter.bureau).toUpperCase()}</b>.
                Accounts, bureau, and letter body stay untouched. Use <b>Edit Full</b> if you
                need to change more.
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold block mb-1.5">
                  Body Font
                </label>
                <select
                  value={quickEditFont}
                  onChange={(e) => setQuickEditFont(e.target.value)}
                  className="w-full bg-black/60 border border-white/10 focus:border-fuchsia-400/60 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  data-testid="quick-edit-font-select"
                >
                  <option value="times">Times</option>
                  <option value="georgia">Georgia</option>
                  <option value="garamond">Garamond</option>
                  <option value="helvetica">Helvetica</option>
                  <option value="courier">Courier</option>
                </select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold block mb-1.5">
                  Letter Date
                </label>
                <input
                  type="date"
                  value={quickEditDate}
                  onChange={(e) => setQuickEditDate(e.target.value)}
                  className="w-full bg-black/60 border border-white/10 focus:border-fuchsia-400/60 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  data-testid="quick-edit-date-input"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Leave blank to use today&apos;s date (Pacific time).
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setQuickEditLetter(null)}
              disabled={quickEditGenerating}
              className="text-xs uppercase tracking-wider text-zinc-300 hover:text-white px-3 py-2 rounded-md disabled:opacity-50"
              data-testid="quick-edit-cancel"
            >
              Cancel
            </button>
            <Button
              onClick={runQuickEditRegenerate}
              disabled={quickEditGenerating}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
              data-testid="quick-edit-regenerate"
            >
              {quickEditGenerating ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Re-generating…</>
              ) : (
                <><Download className="w-3.5 h-3.5 mr-1.5" /> Save &amp; Re-download</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FTC Report EDITABLE Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="w-[95vw] h-[95vh] bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl flex flex-col overflow-hidden">
            {/* Unsaved Changes Banner */}
            {hasUnsavedChanges && (
              <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Edit2 className="w-4 h-4" />
                  <span className="font-medium">You have unsaved changes</span>
                  <span className="text-amber-900">- Click "Generate & Download PDF" to save your edits</span>
                </div>
                <CheckCircle2 className="w-5 h-5 text-amber-800" />
              </div>
            )}
            
            {/* Modal Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 sm:p-4 border-b border-zinc-700 bg-gradient-to-r from-blue-900 to-blue-800 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <Edit2 className="w-6 h-6 text-blue-300" />
                <div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Edit FTC Report Before Generating</h2>
                  <p className="text-sm text-blue-200">Edit account names to abbreviate • Click "Save & Generate PDF" when done</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Button
                  onClick={async () => {
                    // 2026-03-02 — one-click auto-shorten: run every visible
                    // account through the abbreviation API, then apply the
                    // suggestion in-place.  Preserves the DATE and $AMOUNT
                    // suffix — we only shorten the creditor prefix.
                    if (!previewData?.sections) return;
                    const rows = [];
                    previewData.sections.forEach((s, si) => s.accounts.forEach((a, ai) => {
                      const dn = a.display_name || '';
                      // Extract the creditor prefix (before "-" or "$")
                      const m = dn.match(/^(.*?)(\s*[-–]\s*\d.*)?$/);
                      const prefix = (m ? m[1] : dn).trim();
                      rows.push({ si, ai, prefix, suffix: (dn.slice(prefix.length) || '') });
                    }));
                    if (!rows.length) return;
                    try {
                      const { data } = await axios.post(`${API}/api/ftc-report/suggest-abbreviations`, {
                        names: rows.map(r => r.prefix),
                      });
                      const map = new Map((data?.items || []).map((x, i) => [rows[i].prefix + '|' + i, x.suggested]));
                      let changed = 0;
                      rows.forEach((r, i) => {
                        const shortP = map.get(r.prefix + '|' + i) || r.prefix;
                        if (shortP && shortP !== r.prefix) {
                          const newName = (shortP + r.suffix).trim();
                          updatePreviewAccount(r.si, r.ai, 'display_name', newName);
                          changed++;
                        }
                      });
                      toast.success(changed ? `Auto-shortened ${changed} row(s)` : 'Nothing to shorten');
                    } catch (e) {
                      toast.error('Auto-shorten failed');
                    }
                  }}
                  variant="outline"
                  className="border-purple-400 bg-purple-950 text-purple-200 hover:bg-purple-900"
                  data-testid="preview-auto-shorten-btn"
                >
                  ✂️ Auto-shorten long names
                </Button>
                <Button
                  onClick={() => {
                    // 2026-03-02 — flipping the toggle ON triggers an
                    // IMMEDIATE build (no debounce) so the user sees
                    // real progress right away.  Subsequent edits keep
                    // using the 900ms debounced auto-refresh below.
                    setShowLivePdf(prev => {
                      const next = !prev;
                      if (next) setTimeout(() => buildLivePdf(), 50);
                      return next;
                    });
                  }}
                  variant="outline"
                  className={`border-2 font-semibold ${
                    showLivePdf
                      ? 'bg-amber-500 border-amber-300 text-black hover:bg-amber-400'
                      : 'bg-blue-950 border-amber-400 text-amber-200 hover:bg-blue-900'
                  }`}
                  data-testid="toggle-live-pdf-btn"
                >
                  {livePdfBuilding ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Building live PDF…</>
                  ) : showLivePdf ? (
                    <>✓ Live PDF ON — hide</>
                  ) : (
                    <>👁 View Live PDF (T-for-T)</>
                  )}
                </Button>
                <Button
                  onClick={closePreview}
                  variant="outline"
                  className="border-blue-400 text-blue-200 hover:bg-blue-800"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    downloadFromPreview();
                  }}
                  disabled={generatingFromPreview}
                  className="bg-green-600 hover:bg-green-700 font-bold px-6"
                  data-testid="download-from-preview-btn"
                >
                  {generatingFromPreview ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Save & Generate PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* EDITABLE FTC Report Preview - Official FTC Style */}
            <div className="flex-1 overflow-auto sm:overflow-hidden bg-slate-100 flex flex-col sm:flex-row">
              {/* LEFT PANE — editable HTML view */}
              <div className={`shrink-0 sm:shrink sm:overflow-auto p-2 sm:p-8 ${showLivePdf ? 'w-full sm:w-1/2 sm:border-r-2 border-slate-300' : 'w-full'}`}>
                <div className="max-w-[850px] mx-auto bg-white shadow-xl rounded-lg overflow-hidden text-black" style={{fontFamily: 'Arial, Helvetica, sans-serif'}}>
                {/* FTC Header - Official Blue Style */}
                <div className="bg-[#003366] text-white p-4">
                  <div className="flex items-center gap-4">
                    {/* FTC Seal */}
                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center flex-shrink-0 border-2 border-gray-300">
                      <img src="/ftc_seal_clean.png" alt="FTC Seal" className="w-14 h-14" onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span class="text-[#003366] font-bold text-xs text-center">FTC<br/>SEAL</span>';
                      }} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold tracking-wide opacity-90">FEDERAL TRADE COMMISSION</div>
                      <div className="text-2xl font-bold mt-1">Identity Theft Report</div>
                    </div>
                    {/* Report Number Box */}
                    <div className="bg-white/10 backdrop-blur px-4 py-2 rounded border border-white/20">
                      <div className="text-xs text-blue-200 mb-1">FTC Report Number:</div>
                      <input
                        type="text"
                        value={previewData.reportNumber}
                        onChange={(e) => updatePreviewField('reportNumber', e.target.value)}
                        className="bg-white/90 border-2 border-blue-300 px-2 py-1 rounded text-sm font-mono w-32 text-[#003366] font-bold focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Subtitle Bar */}
                <div className="bg-gray-100 px-4 py-3 text-sm text-gray-700 italic border-b border-gray-300">
                  I am a victim of Identity theft. This is my official statement about the crime.
                </div>
                
                {/* Contact Information Section */}
                <div className="p-4 border-b-2 border-gray-200">
                  <h2 className="text-sm font-bold text-[#003366] mb-3 pb-2 border-b-2 border-[#003366] uppercase tracking-wide">Contact Information</h2>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <label className="text-[#003366] text-xs font-semibold block mb-1">Full Name:</label>
                      <input
                        type="text"
                        value={previewData.clientInfo.name || ''}
                        onChange={(e) => updatePreviewField('clientInfo.name', e.target.value)}
                        data-testid="preview-client-name"
                        className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <label className="text-[#003366] text-xs font-semibold block mb-1">Date of Birth:</label>
                      <input
                        type="text"
                        value={previewData.clientInfo.dob}
                        onChange={(e) => updatePreviewField('clientInfo.dob', e.target.value)}
                        className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <label className="text-[#003366] text-xs font-semibold block mb-1">SSN (Last 4):</label>
                      <input
                        type="text"
                        value={previewData.clientInfo.ssn}
                        onChange={(e) => updatePreviewField('clientInfo.ssn', e.target.value)}
                        className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <label className="text-[#003366] text-xs font-semibold block mb-1">Phone:</label>
                      <input
                        type="text"
                        value={previewData.clientInfo.phone || ''}
                        onChange={(e) => updatePreviewField('clientInfo.phone', e.target.value)}
                        className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                    <div className="col-span-2 bg-gray-50 p-3 rounded border border-gray-200">
                      <label className="text-[#003366] text-xs font-semibold block mb-1">Street address:</label>
                      <input type="text" value={previewData.clientInfo.address || ''} onChange={(e) => updatePreviewField('clientInfo.address', e.target.value)}
                        className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400" data-testid="preview-client-address" />
                      <div className="grid grid-cols-6 gap-2 mt-2">
                        {[['city', 'City', 'col-span-3'], ['state', 'State', 'col-span-1'], ['zip_code', 'ZIP', 'col-span-2']].map(([k, l, span]) => (
                          <label key={k} className={`${span} text-[#003366] text-[11px] font-semibold`}>{l}
                            <input type="text" value={previewData.clientInfo[k] || ''} onChange={(e) => updatePreviewField(`clientInfo.${k}`, e.target.value)}
                              className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400" data-testid={`preview-client-${k}`} />
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2 bg-gray-50 p-3 rounded border border-gray-200">
                      <label className="text-[#003366] text-xs font-semibold block mb-1">Email:</label>
                      <input
                        type="text"
                        value={previewData.clientInfo.email || ''}
                        onChange={(e) => updatePreviewField('clientInfo.email', e.target.value)}
                        className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Personal Statement Section */}
                <div className="p-4 border-b-2 border-gray-200">
                  <h2 className="text-sm font-bold text-[#003366] mb-3 pb-2 border-b-2 border-[#003366] uppercase tracking-wide">Personal Statement</h2>
                  <textarea
                    value={previewData.personalStatement}
                    onChange={(e) => updatePreviewField('personalStatement', e.target.value)}
                    className="w-full h-32 bg-blue-50 border-2 border-blue-200 px-3 py-2 rounded text-sm text-black leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none"
                    placeholder="Enter your personal statement here..."
                  />
                </div>
                
                {/* Fraudulent Accounts Sections */}
                {previewData.sections.map((section, sectionIndex) => {
                  // 2026-03-02 — visual "will this fit?" indicator per row.
                  // The FTC template lays out ~34 characters comfortably in
                  // the Company/Organization box before the text wraps.
                  const FIT_LIMIT = 34;
                  return (
                  <div key={sectionIndex} className="p-4 border-b-2 border-gray-200">
                    {/* Section Header - Orange/Gold like official FTC */}
                    <div className="bg-gradient-to-r from-orange-500 to-orange-400 text-white px-4 py-2 rounded-t font-bold text-sm mb-0">
                      <input
                        type="text"
                        value={section.fraud_type_label}
                        onChange={(e) => updatePreviewSection(sectionIndex, 'fraud_type_label', e.target.value)}
                        className="bg-white/20 border border-white/30 px-2 py-1 rounded text-white placeholder-white/70 w-full focus:outline-none focus:ring-2 focus:ring-white/50"
                      />
                    </div>
                    
                    {/* Company/Organization Box */}
                    <div className="bg-gray-50 border-x border-b border-gray-300 p-3">
                      <div className="text-xs text-[#003366] font-semibold mb-2 flex items-center justify-between">
                        <span>Company or Organization:</span>
                        <span className="text-[10px] text-gray-500 normal-case">
                          {section.accounts.length} account{section.accounts.length === 1 ? '' : 's'} · char limit per line: {FIT_LIMIT}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {section.accounts.map((account, accountIndex) => {
                          const nm = account.display_name || '';
                          const over = nm.length > FIT_LIMIT;
                          const near = !over && nm.length > FIT_LIMIT - 4;
                          return (
                          <div key={accountIndex} className="relative">
                            <input
                              type="text"
                              value={account.display_name}
                              onChange={(e) => updatePreviewAccount(sectionIndex, accountIndex, 'display_name', e.target.value)}
                              className={`w-full pr-16 bg-blue-50 border-2 px-3 py-2 rounded text-sm font-medium text-black focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                                over ? 'border-red-500 bg-red-50' : near ? 'border-amber-400 bg-amber-50' : 'border-blue-200'
                              }`}
                              placeholder="CREDITOR-DATE-$AMOUNT"
                              data-testid={`preview-acct-${sectionIndex}-${accountIndex}`}
                            />
                            <span
                              className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                over ? 'bg-red-500 text-white' : near ? 'bg-amber-400 text-black' : 'bg-slate-200 text-slate-600'
                              }`}
                              title={over ? 'Will wrap — shorten this line' : near ? 'Close to wrapping — consider shortening' : 'Fits on one line'}
                            >
                              {nm.length}/{FIT_LIMIT}
                            </span>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Dates Grid - 3 columns like official FTC */}
                    <div className="grid grid-cols-3 border-x border-b border-gray-300">
                      <div className="bg-gray-50 p-3 border-r border-gray-300">
                        <div className="text-xs text-[#003366] font-semibold mb-1">Date fraud began:</div>
                        <input
                          type="text"
                          value={section.date_fraud_began}
                          onChange={(e) => updatePreviewSection(sectionIndex, 'date_fraud_began', e.target.value)}
                          className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                          placeholder="M/YYYY"
                        />
                      </div>
                      <div className="bg-gray-50 p-3 border-r border-gray-300">
                        <div className="text-xs text-[#003366] font-semibold mb-1">Date that I discovered it:</div>
                        <input
                          type="text"
                          value={section.date_discovered}
                          onChange={(e) => updatePreviewSection(sectionIndex, 'date_discovered', e.target.value)}
                          className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                          placeholder="M/YYYY"
                        />
                      </div>
                      <div className="bg-gray-50 p-3">
                        <div className="text-xs text-[#003366] font-semibold mb-1">Total fraudulent amount:</div>
                        <input
                          type="text"
                          value={`$${parseInt(section.total_fraud_amount || 0).toLocaleString()}`}
                          onChange={(e) => updatePreviewSection(sectionIndex, 'total_fraud_amount', e.target.value.replace(/[$,]/g, ''))}
                          className="w-full bg-blue-50 border-2 border-blue-200 px-2 py-1.5 rounded text-sm font-bold text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
                
                {/* Declaration Section */}
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-sm font-bold text-[#003366] mb-3 pb-2 border-b-2 border-[#003366] uppercase tracking-wide">Declaration</h2>
                  <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                    Under penalty of perjury, I declare this information is true and correct to the best of my knowledge. 
                    I understand that knowingly making any false statements to the government may violate federal, state, 
                    or local criminal statutes, and may result in a fine, imprisonment, or both.
                  </p>
                  {previewData.signatureData && (
                    <div className="border-2 border-gray-300 rounded p-3 bg-gray-50 inline-block">
                      <div className="text-xs text-gray-500 mb-1">Signature:</div>
                      <img src={previewData.signatureData} alt="Signature" className="max-h-16" />
                    </div>
                  )}
                </div>
                
                {/* Footer - Official FTC Style */}
                <div className="bg-[#003366] px-4 py-3 text-xs text-blue-200 flex justify-between items-center">
                  <span>https://www.identitytheft.gov</span>
                  <span className="text-white font-medium">Federal Trade Commission</span>
                </div>
              </div>
              </div>{/* /LEFT PANE */}

              {/* RIGHT PANE — live PDF (T-for-T) */}
              {showLivePdf && (
                <div className="w-full sm:w-1/2 min-h-[70vh] sm:min-h-0 flex flex-col bg-slate-200" data-testid="live-pdf-pane">
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white text-xs">
                    <div className="flex items-center gap-2">
                      👁 <b>Live PDF</b>
                      <span className="text-slate-400">— exactly what will download</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {livePdfBuilding && (
                        <span className="text-amber-300 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3 animate-spin" /> refreshing…
                        </span>
                      )}
                      {livePdfUrl && !livePdfBuilding && (
                        <a
                          href={livePdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="h-7 px-2 flex items-center gap-1 text-[11px] bg-blue-600 hover:bg-blue-700 text-white rounded"
                          data-testid="live-pdf-open-new-tab"
                        >
                          ↗ Open in new tab
                        </a>
                      )}
                      <Button
                        onClick={buildLivePdf}
                        disabled={livePdfBuilding}
                        variant="outline"
                        className="h-7 px-2 text-[11px] bg-slate-800 border-slate-600 text-white hover:bg-slate-700"
                        data-testid="live-pdf-refresh-btn"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden bg-slate-400">
                    {livePdfError ? (
                      <div className="flex flex-col items-center justify-center h-full p-6 text-center gap-3">
                        <div className="text-red-700 font-bold text-sm">
                          ⚠️ Live PDF build failed
                        </div>
                        <div className="text-red-900 text-xs max-w-md font-mono bg-white/80 p-2 rounded border border-red-300 whitespace-pre-wrap break-words">
                          {livePdfError}
                        </div>
                        <Button onClick={buildLivePdf} className="bg-red-600 hover:bg-red-700 text-white text-xs">
                          <RefreshCw className="w-3 h-3 mr-1" /> Retry
                        </Button>
                      </div>
                    ) : livePdfPages.length > 0 ? (
                      <div className="w-full h-full overflow-y-auto bg-slate-300 p-3 space-y-3" data-testid="live-pdf-pages-scroll">
                        {livePdfPages.map((pg) => (
                          <img
                            key={pg.page}
                            src={pg.data_url}
                            alt={`FTC Report page ${pg.page}`}
                            className="w-full max-w-full shadow-lg rounded-sm bg-white"
                            style={{ display: 'block' }}
                            data-testid={`live-pdf-page-${pg.page}`}
                          />
                        ))}
                        {livePdfBuilding && (
                          <div className="text-center text-slate-700 text-xs py-2 flex items-center justify-center gap-2">
                            <RefreshCw className="w-3 h-3 animate-spin" /> refreshing…
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-slate-700 text-sm gap-3">
                        {livePdfBuilding ? (
                          <>
                            <RefreshCw className="w-6 h-6 animate-spin text-slate-600" />
                            <div>Building live PDF…</div>
                          </>
                        ) : (
                          <>
                            <div>Live PDF will appear here.</div>
                            <Button onClick={buildLivePdf} className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                              <RefreshCw className="w-3 h-3 mr-1" /> Build now
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 sm:p-4 border-t border-zinc-700 bg-[#003366] flex-shrink-0">
              <div className="text-sm text-blue-200 flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-blue-300" />
                <span><span className="text-blue-100 font-medium">Blue highlighted fields</span> are editable - click to modify</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-blue-300">
                  {previewData.sections.reduce((acc, s) => acc + s.accounts.length, 0)} accounts • 
                  ${previewData.sections.reduce((acc, s) => acc + parseInt(s.total_fraud_amount || 0), 0).toLocaleString()} total
                </span>
                <Button
                  onClick={closePreview}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    downloadFromPreview();
                  }}
                  disabled={generatingFromPreview}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6"
                >
                  {generatingFromPreview ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Save & Generate PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FTC Report Number Modal - Shows BEFORE generating */}
      {showReportNumberModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">FTC Report Number</h2>
                <p className="text-sm text-zinc-400">
                  {isFirstTimeReport 
                    ? 'Enter your first FTC report number' 
                    : 'Confirm or edit the suggested number'}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {isFirstTimeReport ? (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-300 text-sm">
                    <strong>First time setup:</strong> Please enter the FTC report number you want to start with.
                    Future reports will automatically suggest numbers 152-200 higher than the last one.
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-300 text-sm">
                    <strong>Suggested:</strong> Based on your last report, we suggest <span className="font-mono font-bold">{suggestedReportNumber}</span>
                    <br />
                    <span className="text-zinc-400">(Previous + 152-200)</span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  FTC Report Number
                </label>
                <input
                  type="text"
                  value={reportNumber}
                  onChange={(e) => setReportNumber(e.target.value)}
                  placeholder="e.g., 199103389"
                  className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-3 text-lg font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="ftc-report-number-modal-input"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowReportNumberModal(false);
                  setPendingGenerateAction(null);
                }}
                variant="outline"
                className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmReportNumber}
                disabled={!reportNumber.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-ftc-report-number-btn"
              >
                {pendingGenerateAction === 'preview' ? 'Continue to Preview' : 'Continue to Generate'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Credit Repair Steps Email Popup */}
      <Dialog open={showCreditRepairEmailPopup} onOpenChange={setShowCreditRepairEmailPopup}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-white">
              <Mail className="w-6 h-6 text-blue-400" />
              Send Credit Repair Steps to Client
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Client Info */}
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
              <p className="text-zinc-400 text-sm">Sending to:</p>
              <p className="text-white font-medium">{clientData?.name}</p>
              <p className="text-blue-400">{clientData?.email}</p>
            </div>
            
            {/* Email Preview */}
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
              <p className="text-zinc-400 text-sm mb-2">Subject:</p>
              <p className="text-white font-medium">Credit Repair Steps</p>
              
              <div className="mt-4 border-t border-zinc-700 pt-4">
                <p className="text-zinc-400 text-sm mb-2">Accounts to be listed under Step 3:</p>
                
                {/* TransUnion */}
                <div className="mb-3">
                  <p className="text-red-400 font-medium text-sm">TRANSUNION:</p>
                  <ul className="text-zinc-300 text-sm ml-4">
                    {ftcAccountsForEmail.transunion.map((acc, i) => (
                      <li key={`tu-${i}`}>• {acc}</li>
                    ))}
                  </ul>
                </div>
                
                {/* Equifax */}
                <div className="mb-3">
                  <p className="text-blue-400 font-medium text-sm">EQUIFAX:</p>
                  <ul className="text-zinc-300 text-sm ml-4">
                    {ftcAccountsForEmail.equifax.map((acc, i) => (
                      <li key={`eq-${i}`}>• {acc}</li>
                    ))}
                  </ul>
                </div>
                
                {/* Experian */}
                <div className="mb-3">
                  <p className="text-green-400 font-medium text-sm">EXPERIAN:</p>
                  <ul className="text-zinc-300 text-sm ml-4">
                    {ftcAccountsForEmail.experian.map((acc, i) => (
                      <li key={`ex-${i}`}>• {acc}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            <p className="text-zinc-400 text-xs">
              📧 <strong className="text-zinc-200">Pick how you want the steps to reach the client below.</strong> Choose the 5-part drip (Step 1 → Step 5, one email each), one combined email, or send both. The default is the 5-part drip; switch to <span className="text-blue-300">1 combined email</span> if past clients reported the drip landing in spam.
            </p>
          </div>

          {/* 2026-Feb: The pre-email "Create Dispute Letter" shortcut was
              removed. Dispute letters now flow ONLY through the post-email
              prompt so the sequence stays linear: emails scheduled →
              dispute letter → download. */}

          {/* 2026-02-24 — Send Style selector.  Because 5 back-to-back
              emails from a personal @gmail.com sometimes land in spam, the
              user can now pick between the 5-part drip, one combined email,
              or BOTH (combined first, then the drip 5 min later). */}
          <div className="border-t border-zinc-700 pt-4">
            <p className="text-zinc-300 text-sm font-medium mb-2">Send style</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { key: 'series',   emoji: '📧',   title: '5 separate emails', sub: 'One per step, 1 min apart' },
                { key: 'combined', emoji: '📄',   title: '1 combined email',  sub: 'All steps in one message' },
                { key: 'both',     emoji: '📧+📄', title: 'Send both',         sub: 'Combined now, drip 5 min later' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setCreditRepairSendMode(opt.key)}
                  disabled={sendingCreditRepairEmail}
                  data-testid={`credit-repair-mode-${opt.key}-btn`}
                  className={`text-left rounded-lg border-2 px-3 py-2 transition ${
                    creditRepairSendMode === opt.key
                      ? 'border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/40'
                      : 'border-zinc-700 bg-zinc-800/60 hover:border-zinc-500'
                  }`}
                >
                  <div className="text-lg leading-none mb-1">{opt.emoji}</div>
                  <div className="text-white text-sm font-semibold leading-tight">{opt.title}</div>
                  <div className="text-zinc-400 text-[11px] mt-0.5 leading-tight">{opt.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-zinc-500 text-[11px] mt-2 leading-snug">
              Tip: If clients report the 5 drip emails hitting spam, pick <span className="text-blue-300">1 combined email</span> — a single message from a familiar sender is far less likely to be flagged.
            </p>
          </div>

          {/* Delivery choice — Skip / Send Now / 1 h / Come back later */}
          <div className="border-t border-zinc-700 pt-4">
            <p className="text-zinc-300 text-sm font-medium mb-3">When should it send?</p>
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
              <Button
                onClick={() => handleCreditRepairDeliveryChoice('now')}
                disabled={sendingCreditRepairEmail}
                className="bg-blue-600 hover:bg-blue-700 text-white col-span-2 sm:col-span-1"
                data-testid="credit-repair-choice-now-btn"
              >
                {sendingCreditRepairEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                🚀 Send Now
              </Button>
              <Button
                onClick={() => handleCreditRepairDeliveryChoice('in_1h')}
                disabled={sendingCreditRepairEmail}
                variant="outline"
                className="border-zinc-600 text-zinc-200 hover:bg-zinc-800 col-span-2 sm:col-span-1"
                data-testid="credit-repair-choice-1h-btn"
              >
                ⏳ Start in 1 hour
              </Button>
              <Button
                onClick={() => handleCreditRepairDeliveryChoice('manual')}
                disabled={sendingCreditRepairEmail}
                variant="outline"
                className="border-amber-600 text-amber-300 hover:bg-amber-900/20 col-span-2 sm:col-span-1"
                data-testid="credit-repair-choice-manual-btn"
                title="Save as pending — send later from the Saved FTC Reports gallery"
              >
                💾 Come back later
              </Button>
              <Button
                onClick={() => handleCreditRepairDeliveryChoice('skip')}
                disabled={sendingCreditRepairEmail}
                variant="outline"
                className="border-zinc-600 text-zinc-400 hover:bg-zinc-800 col-span-2 sm:col-span-1"
                data-testid="credit-repair-choice-skip-btn"
              >
                Skip
              </Button>
            </div>
            <p className="text-zinc-500 text-xs mt-3 leading-snug">
              Choose <span className="text-amber-400">Come back later</span> to save as pending — you can send it manually from the Saved FTC Reports gallery. Scheduled sends fire automatically — no need to keep the app open.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Payment Reminder Popup - After Credit Repair Steps */}
      <Dialog open={showQuickReminderPopup} onOpenChange={setShowQuickReminderPopup}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md max-h-[92vh] p-0 gap-0 flex flex-col" data-testid="quick-reminder-dialog">
          <DialogHeader className="px-5 pt-5 pb-2 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg text-white">
              <DollarSign className="w-5 h-5 text-green-400" />
              Add to Quick Payment Reminder?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 px-5 py-3 overflow-y-auto flex-1 min-h-0">
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
              <p className="text-zinc-400 text-sm">Client:</p>
              <p className="text-white font-medium">{clientData?.name || 'N/A'}</p>
              <p className="text-blue-400 text-sm">{clientData?.email || 'N/A'}</p>
            </div>
            
            <p className="text-zinc-400 text-sm">
              Log their remaining balance and they'll automatically receive payment reminders every Friday.
            </p>
            
            <div>
              <label className="block text-zinc-300 text-sm font-medium mb-1">Remaining Balance *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="number"
                  value={quickReminderForm.balance}
                  onChange={(e) => setQuickReminderForm({ ...quickReminderForm, balance: e.target.value })}
                  placeholder="0.00"
                  className="w-full pl-9 pr-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500"
                  data-testid="quick-reminder-balance-input"
                />
              </div>
            </div>

            {/* Automated Payment Reminder cadence — OFF / WEEKLY / BIWEEKLY / MONTHLY */}
            <div className="bg-blue-950/30 border border-blue-500/30 rounded-lg p-3" data-testid="quick-reminder-frequency-section">
              <label className="block text-blue-200 text-xs font-semibold mb-2">
                How often should reminders be emailed?
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { value: 'off', label: 'Off' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'biweekly', label: 'Biweekly' },
                  { value: 'monthly', label: 'Monthly' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setQuickReminderForm({ ...quickReminderForm, reminderFrequency: opt.value })}
                    data-testid={`quick-reminder-frequency-${opt.value}`}
                    className={`px-2 py-2 rounded text-xs font-semibold transition-colors ${
                      (quickReminderForm.reminderFrequency || 'weekly') === opt.value
                        ? 'bg-blue-600 text-white border border-blue-400'
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-600 hover:bg-zinc-700 hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-blue-200/70 text-[11px] mt-2 leading-relaxed">
                Weekly = every Friday · Biweekly = every other Friday · Monthly = first Friday of the month. Sent automatically at 9 AM Pacific.
              </p>
            </div>

            {/* Promised Payment Date — optional. If set, a reminder is auto-emailed
                day-before and day-of to the client + to your business email. */}
            <div className="bg-amber-950/30 border border-amber-500/30 rounded-lg p-3 space-y-2.5">
              <p className="text-amber-200 text-xs font-semibold flex items-center gap-1.5">
                <span style={{display:'inline-block',width:6,height:6,borderRadius:99,background:'#fbbf24'}} />
                Did the client commit to a payment date?
              </p>
              <div>
                <label className="block text-zinc-300 text-xs font-medium mb-1">Promised Payment Date <span className="text-zinc-500">(optional)</span></label>
                <input
                  type="date"
                  value={quickReminderForm.promisedDate}
                  onChange={(e) => setQuickReminderForm({ ...quickReminderForm, promisedDate: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm"
                  data-testid="quick-reminder-promised-date"
                />
              </div>
              <div>
                <label className="block text-zinc-300 text-xs font-medium mb-1">Promise note <span className="text-zinc-500">(optional)</span></label>
                <input
                  type="text"
                  value={quickReminderForm.promisedNotes}
                  onChange={(e) => setQuickReminderForm({ ...quickReminderForm, promisedNotes: e.target.value })}
                  placeholder="e.g. Will pay via Zelle on payday"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm"
                  data-testid="quick-reminder-promised-notes"
                />
              </div>
              <p className="text-amber-200/70 text-[11px] leading-relaxed">
                If set, both you and the client get an automatic email reminder the day before and the day of.
              </p>
            </div>
            
            <div>
              <button
                type="button"
                onClick={() => setQuickReminderForm({ ...quickReminderForm, priority: !quickReminderForm.priority })}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                  quickReminderForm.priority 
                    ? 'bg-red-600/20 border-red-500/50 text-red-400' 
                    : 'bg-zinc-800 border-zinc-600 text-zinc-400'
                }`}
                data-testid="quick-reminder-priority-btn"
              >
                <span className="text-sm font-medium">
                  {quickReminderForm.priority ? 'Priority Client' : 'Mark as Priority'}
                </span>
                <div className={`w-10 h-5 rounded-full transition-colors relative ${quickReminderForm.priority ? 'bg-red-500' : 'bg-zinc-600'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${quickReminderForm.priority ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </button>
            </div>
          </div>
          
          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            <Button
              variant="outline"
              onClick={async () => {
                setShowQuickReminderPopup(false);
                await saveCompletedReportLog({ outstanding_balance: 0 });
                // Per user request 2026-03-03: ask about Copy & Fax conversion
                // first, then Mail Report if they skip.
                // 2026-02-24 — Handwriting removed; go straight to Copy & Fax.
                setMailReportResults(null);
                setShowCopyFaxPrompt(true);
              }}
              className="border-zinc-600 text-zinc-300 w-full whitespace-nowrap"
              disabled={savingReportLog}
              data-testid="quick-reminder-skip-btn"
            >
              {savingReportLog ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                'Skip & Save'
              )}
            </Button>
            <Button
              onClick={handleAddToQuickReminder}
              disabled={savingQuickReminder || savingReportLog || !quickReminderForm.balance}
              className="bg-green-600 hover:bg-green-700 w-full whitespace-nowrap"
              data-testid="add-to-quick-reminder-btn"
            >
              {(savingQuickReminder || savingReportLog) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {savingReportLog ? 'Saving…' : 'Adding...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Add & Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ════════════════════════════════════════════════════════════════
          COPY & FAX CONVERTER PROMPT (per user request 2026-03-03)
          Opens automatically after Skip & Save / Add & Save.  Asks whether
          the user needs to run docs through the Copy & Fax Converter
          before mailing.  Yes → opens /copy-fax in a new tab and chains to
          Mail Report.  Skip → goes straight to Mail Report.
          ════════════════════════════════════════════════════════════════ */}
      {/* ════════════════════════════════════════════════════════════════
          HANDWRITTEN LETTER CONVERTER PROMPT (2026-06-25)
          - Asks before the Copy & Fax prompt: do you want to convert any
            dispute letters to handwritten form first?  Skipping continues
            to the Copy & Fax prompt.
          ════════════════════════════════════════════════════════════════ */}
      <Dialog open={showHandwritingPrompt} onOpenChange={setShowHandwritingPrompt}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md" data-testid="handwriting-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-white">
              <PenLine className="w-5 h-5 text-violet-300" />
              Convert any letters to handwriting?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-zinc-300">
              Want to turn any of these dispute letters into{" "}
              <span className="font-semibold text-violet-300">handwritten letters</span> first?
              Drop in PDFs, pick from 13 readable handwriting styles, and the &ldquo;Sincerely, [Name]&rdquo;
              line becomes a unique cursive signature.
            </p>
            <ul className="text-[12px] text-zinc-400 space-y-1 pl-4 list-disc">
              <li>13 handwriting styles + 6 signature fonts, with a live preview</li>
              <li>Per-glyph baseline + rotation jitter so it looks organic</li>
              <li>Real cursive signature on the closing line, unique per client</li>
              <li>Preview each result before downloading · multiple files at once</li>
            </ul>
          </div>
          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowHandwritingPrompt(false);
                setShowCopyFaxPrompt(true);
              }}
              className="border-zinc-600 text-zinc-300 w-full"
              data-testid="handwriting-skip-btn"
            >
              Skip
            </Button>
            <Button
              onClick={() => {
                setShowHandwritingPrompt(false);
                setShowHandwritingModal(true);
              }}
              className="bg-violet-600 hover:bg-violet-700 w-full"
              data-testid="handwriting-yes-btn"
            >
              <PenLine className="w-4 h-4 mr-2" />
              Yes, convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The actual converter — sits as a sibling dialog so closing it
          rolls us forward into the Copy & Fax prompt. */}
      <HandwritingConverterModal
        open={showHandwritingModal}
        onClose={() => {
          setShowHandwritingModal(false);
          setShowCopyFaxPrompt(true);
        }}
      />

      <Dialog open={showCopyFaxPrompt} onOpenChange={setShowCopyFaxPrompt}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md" data-testid="copy-fax-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-white">
              <Printer className="w-5 h-5 text-blue-400" />
              Convert documents first?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-zinc-300">
              Before mailing, do you need to run any documents through the
              <span className="font-semibold text-blue-300"> Copy & Fax Converter</span>?
              That tool sanitizes scans / faxes and converts them to clean print-ready PDFs.
            </p>
            <ul className="text-[12px] text-zinc-400 space-y-1 pl-4 list-disc">
              <li>Cleans up dark photocopies or faxes</li>
              <li>Standardizes to letter-size PDFs</li>
              <li>Downloaded files plug straight into the Mail Report drop zone next</li>
            </ul>
          </div>
          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                // 2026-02-24 — ID Converter prompt removed; go straight to
                // the Print Per Bureau modal.
                setShowCopyFaxPrompt(false);
                setShowMailReportModal(true);
              }}
              className="border-zinc-600 text-zinc-300 w-full"
              data-testid="copy-fax-skip-btn"
            >
              Skip
            </Button>
            <Button
              onClick={() => {
                // Open Copy & Fax Converter in a new tab, then straight to
                // Print Per Bureau modal.
                setShowCopyFaxPrompt(false);
                window.open('/copy-fax', '_blank', 'noopener');
                toast.info('Copy & Fax opened in a new tab. Come back here when you have your converted PDFs ready.', { duration: 5000 });
                setShowMailReportModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 w-full"
              data-testid="copy-fax-yes-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Yes, open it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════
          ID + SSN CONVERTER PROMPT (Feb 2026 — user request)
          Sits between Copy & Fax and Mail Report.  If the client's ID or
          Social Security Card need to be resized to portal-approved
          JPEG format, opening the converter here keeps the packet flow
          intact.  Skip → straight to Mail Report.
          ════════════════════════════════════════════════════════════════ */}
      <Dialog open={showIdConverterPrompt} onOpenChange={setShowIdConverterPrompt}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md" data-testid="id-converter-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-white">
              <FileText className="w-5 h-5 text-cyan-300" />
              Convert Client ID + Social Security Card?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-zinc-300">
              Bureaus require the client&apos;s{" "}
              <span className="font-semibold text-cyan-300">Government ID</span>{" "}
              (and optionally their <span className="font-semibold text-cyan-300">Social Security Card</span>)
              as a portal-approved{" "}
              <span className="font-semibold text-white">6.67 × 8.89 in @ 500 DPI JPEG</span>.
              Open the converter, download the JPEG(s), then drag them into the Mail Report drop zone next.
            </p>
            <ul className="text-[12px] text-zinc-400 space-y-1 pl-4 list-disc">
              <li>Accepts PDF, HEIC, JPG, PNG, WebP, BMP, TIFF</li>
              <li>Aggressive sharpen + denoise so phone shots come out crisp</li>
              <li>Runs in the background so large captures won&apos;t time out</li>
            </ul>
          </div>
          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                // Skip converter — but offer to save the raw ID to the
                // client's file so it can be reused in the packet
                // builder without having to open the Files app again
                // (2026-02-08 user request).
                setShowIdConverterPrompt(false);
                if (clientId) {
                  setShowSaveIdPrompt(true);
                } else {
                  // No client selected — nothing to save against, so
                  // just go to Mail Report.
                  toast.info('Now drag the documents you want to mail.', { duration: 3500 });
                  setShowMailReportModal(true);
                }
              }}
              className="border-zinc-600 text-zinc-300 w-full"
              data-testid="id-converter-prompt-skip-btn"
            >
              Skip
            </Button>
            <Button
              onClick={() => {
                setShowIdConverterPrompt(false);
                // Stay in-app — no new tab.  IDConverterPage reads the
                // returnTo query param and renders a Back button that
                // brings the user back here without losing their FTC
                // flow state (React Router keeps this component mounted
                // so packet-builder blobs + saved report state survive
                // the round trip).
                navigate(`/id-converter?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
                toast.info('ID Converter opened. Tap ← Back when your JPEG(s) are downloaded.', { duration: 5000 });
              }}
              className="bg-cyan-600 hover:bg-cyan-700 w-full"
              data-testid="id-converter-prompt-yes-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Yes, open it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════
          SAVE ID TO CLIENT FILE PROMPT
          Shown when the user skips the ID Converter — offers to save
          the raw ID upload to the client's profile so it can be
          re-used later as a checkbox in the Dispute Packet Builder
          (2026-02-08 user request).
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={showSaveIdPrompt} onOpenChange={setShowSaveIdPrompt}>
        <DialogContent className="max-w-md bg-zinc-950 border-zinc-800 text-white p-0 max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="px-5 py-4 border-b border-zinc-800 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  // ← Back to the ID Converter prompt.
                  setShowSaveIdPrompt(false);
                  setShowIdConverterPrompt(true);
                }}
                className="text-xs uppercase tracking-wider font-bold text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 hover:border-cyan-300/60 rounded-full px-2.5 py-1 mr-1 inline-flex items-center gap-1"
                data-testid="save-id-back-btn"
              >
                ← Back
              </button>
              <DialogTitle className="text-lg font-bold text-cyan-300 flex items-center gap-2">
                📎 Save ID for Later?
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className="px-5 py-4 space-y-3 overflow-y-auto">
            <p className="text-sm text-zinc-300 leading-relaxed">
              Want to attach the client&apos;s ID to their file? Once
              saved, it&apos;ll appear as a checkbox in the Dispute Packet
              Builder — no need to hunt through your camera roll every
              time you build a packet.
            </p>
            <div className="bg-zinc-900/70 border border-white/10 rounded-lg p-3 space-y-2">
              <label className="text-[11px] uppercase tracking-wider font-bold text-cyan-300 block">
                ID Photo or PDF
              </label>
              <input
                type="file"
                accept="application/pdf,image/*,.heic,.heif"
                onChange={(e) => setSaveIdFile(e.target.files?.[0] || null)}
                className="w-full text-xs text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-cyan-500/20 file:text-cyan-200 file:text-[10px] file:uppercase file:tracking-wider file:font-bold hover:file:bg-cyan-500/30"
                data-testid="save-id-file-input"
              />
              {saveIdFile && (
                <div className="text-[10px] text-emerald-300 flex items-center gap-1" data-testid="save-id-file-selected">
                  ✓ {saveIdFile.name}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                // Skip saving — continue to Mail Report.
                setShowSaveIdPrompt(false);
                setSaveIdFile(null);
                toast.info('Now drag the documents you want to mail.', { duration: 3500 });
                setShowMailReportModal(true);
              }}
              disabled={savingIdToFile}
              className="border-zinc-600 text-zinc-300 w-full"
              data-testid="save-id-skip-btn"
            >
              No, skip
            </Button>
            <Button
              onClick={async () => {
                if (!saveIdFile) {
                  toast.error('Pick a file to save first.');
                  return;
                }
                setSavingIdToFile(true);
                try {
                  const fd = new FormData();
                  fd.append('file', saveIdFile);
                  fd.append('document_type', 'id_document');
                  await axios.post(
                    `${API}/api/clients/${clientId}/upload-document`,
                    fd,
                    { timeout: 120000 },
                  );
                  toast.success("ID saved to client's file — it'll show up as a checkbox in the packet builder.");
                  setShowSaveIdPrompt(false);
                  setSaveIdFile(null);
                  setShowMailReportModal(true);
                } catch (e) {
                  const msg = e?.response?.data?.detail || e?.message || 'Save failed';
                  toast.error(`Couldn't save ID: ${msg}`);
                } finally {
                  setSavingIdToFile(false);
                }
              }}
              disabled={!saveIdFile || savingIdToFile}
              className="bg-cyan-600 hover:bg-cyan-700 w-full disabled:opacity-50"
              data-testid="save-id-confirm-btn"
            >
              {savingIdToFile ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <>💾 Save to client file</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════
          MAIL REPORT MODAL (via Lob.com)
          - Drag the FTC PDF (auto-prefilled from last download) + any
            supporting docs into the drop zone.
          - Pick which bureau(s) to mail to (default: all 3).
          - Backend `/api/mail/send-uploaded-files` prepends a blank cover
            page so Lob's address overlay never collides with content.
          ════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showMailReportModal}
        onOpenChange={(v) => {
          setShowMailReportModal(v);
          // 2026-02-05 (user request) — when the mail modal closes,
          // FIRST offer the Dispute Packet builder (bundles all 4 docs
          // into one uploadable PDF for phone / bureau website uploads).
          // The packet prompt itself advances to the review prompt on
          // close so the user always ends the flow with the section
          // jump-back menu.
          if (v === false) {
            setPacketFilename(`${(clientData?.name || 'Client').split(/\s+/).slice(0, 2).join(' ')} Dispute Packet`.trim());
            setTimeout(() => setShowDisputePacketPrompt(true), 250);
          }
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-xl" data-testid="mail-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-white">
              <Mail className="w-5 h-5 text-blue-400" />
              Print Per Bureau
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Ready-to-mail status banner */}
            <div className={`rounded-lg border px-3 py-2.5 text-sm ${
              mailReportDocs.length > 0
                ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-200'
                : 'bg-amber-950/40 border-amber-500/40 text-amber-200'
            }`} data-testid="mail-report-status-banner">
              {mailReportDocs.length > 0 ? (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold">
                      {mailReportDocs.filter(d => d.checked !== false).length} of {mailReportDocs.length} document{mailReportDocs.length === 1 ? '' : 's'} selected
                    </p>
                    <p className="text-[11px] opacity-80 mt-0.5">Each bureau's PDF starts with its own cover page (bureau name at the top) followed by the checked docs.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">No documents loaded yet.</p>
                    <p className="text-[11px] opacity-80 mt-0.5">Docs you generated in this flow will auto-load here.  You can also drag PDFs (client ID, extra letters) into the drop zone below.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Bureau picker — hardcoded color classes so Tailwind purge keeps them */}
            <div>
              <label className="block text-zinc-300 text-sm font-medium mb-2">Bureau(s) to mail to · pick the ones you want</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setMailReportBureaus({ ...mailReportBureaus, experian: !mailReportBureaus.experian })}
                  className={`py-2 px-2 rounded-lg border text-sm font-semibold transition-colors ${
                    mailReportBureaus.experian ? 'bg-sky-600/30 border-sky-500/60 text-sky-100' : 'bg-zinc-800 border-zinc-700 text-zinc-500'
                  }`}
                  data-testid="mail-bureau-experian"
                >Experian</button>
                <button
                  type="button"
                  onClick={() => setMailReportBureaus({ ...mailReportBureaus, equifax: !mailReportBureaus.equifax })}
                  className={`py-2 px-2 rounded-lg border text-sm font-semibold transition-colors ${
                    mailReportBureaus.equifax ? 'bg-red-600/30 border-red-500/60 text-red-100' : 'bg-zinc-800 border-zinc-700 text-zinc-500'
                  }`}
                  data-testid="mail-bureau-equifax"
                >Equifax</button>
                <button
                  type="button"
                  onClick={() => setMailReportBureaus({ ...mailReportBureaus, transunion: !mailReportBureaus.transunion })}
                  className={`py-2 px-2 rounded-lg border text-sm font-semibold transition-colors ${
                    mailReportBureaus.transunion ? 'bg-emerald-600/30 border-emerald-500/60 text-emerald-100' : 'bg-zinc-800 border-zinc-700 text-zinc-500'
                  }`}
                  data-testid="mail-bureau-transunion"
                >TransUnion</button>
              </div>
            </div>

            {/* Drop zone */}
            <div>
              <label className="block text-zinc-300 text-sm font-medium mb-2">Documents to include (check the ones to print per bureau)</label>
              <div
                onDragOver={(e) => { e.preventDefault(); setMailReportDragOver(true); }}
                onDragLeave={() => setMailReportDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setMailReportDragOver(false);
                  const dropped = Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
                  if (dropped.length === 0) { toast.error('Only PDF files are accepted.'); return; }
                  setMailReportDocs(prev => [...prev, ...dropped.map(f => ({ file: f, name: f.name, size: f.size, checked: true }))]);
                }}
                className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
                  mailReportDragOver ? 'border-blue-500 bg-blue-900/20' : 'border-zinc-600 bg-zinc-800/40'
                }`}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
                <p className="text-sm text-zinc-300">Drag additional PDFs here (ID, extra letters) or
                  <label className="text-blue-400 underline cursor-pointer ml-1">
                    pick from device
                    <input
                      type="file"
                      accept="application/pdf"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        if (picked.length) {
                          setMailReportDocs(prev => [...prev, ...picked.map(f => ({ file: f, name: f.name, size: f.size, checked: true }))]);
                        }
                        e.target.value = '';
                      }}
                      data-testid="mail-report-file-input"
                    />
                  </label>
                </p>
                <p className="text-[11px] text-zinc-500 mt-1">Docs generated in this flow are auto-loaded below. Each bureau's PDF opens with its own cover page (bureau name on top).</p>
              </div>

              {/* File list — checkboxes so admin picks per-bureau, auto-loaded flow docs
                  show an "Auto" badge and can be unchecked but not removed. */}
              {mailReportDocs.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {mailReportDocs.map((d, i) => {
                    const isChecked = d.checked !== false;
                    return (
                      <div
                        key={`${d.name}-${i}`}
                        className={`flex items-center justify-between border rounded-md px-3 py-2 text-sm transition-colors ${
                          isChecked ? 'bg-zinc-800 border-zinc-700' : 'bg-zinc-900 border-zinc-800 opacity-60'
                        }`}
                        data-testid={`mail-doc-${i}`}
                      >
                        <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const v = e.target.checked;
                              setMailReportDocs(prev => prev.map((x, idx) => idx === i ? { ...x, checked: v } : x));
                            }}
                            className="w-4 h-4 accent-blue-500 flex-shrink-0"
                            data-testid={`mail-doc-checkbox-${i}`}
                          />
                          <FileText className={`w-4 h-4 flex-shrink-0 ${isChecked ? 'text-blue-400' : 'text-zinc-500'}`} />
                          <span className="text-zinc-200 truncate">{d.name}</span>
                          {d.system && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex-shrink-0">
                              Auto
                            </span>
                          )}
                          <span className="text-[11px] text-zinc-500 flex-shrink-0">{(d.size / 1024).toFixed(0)} KB</span>
                        </label>
                        {!d.system && (
                          <button
                            onClick={() => setMailReportDocs(prev => prev.filter((_, idx) => idx !== i))}
                            className="text-zinc-400 hover:text-red-400 w-8 h-8 flex items-center justify-center flex-shrink-0"
                            aria-label={`Remove ${d.name}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mail class */}
            <div>
              <label className="block text-zinc-300 text-sm font-medium mb-2">Mail class</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { val: 'usps_first_class',          label: 'First Class', cost: '$1.50' },
                  { val: 'certified',                 label: 'Certified',   cost: '$8.50' },
                  { val: 'certified_return_receipt',  label: 'Certified+RR',cost: '$12.50' },
                ].map(m => (
                  <button
                    key={m.val}
                    type="button"
                    onClick={() => setMailReportClass(m.val)}
                    className={`py-2 px-2 rounded-lg border text-xs font-semibold ${
                      mailReportClass === m.val
                        ? 'bg-blue-600/30 border-blue-500/60 text-blue-100'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                    }`}
                  >
                    {m.label}<br /><span className="text-[10px] opacity-70">{m.cost}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Results panel (after send) */}
            {mailReportResults && (
              <div className="space-y-2" data-testid="mail-report-results">
                {mailReportResults.map((r, i) => (
                  <div key={i} className={`rounded-lg border p-3 text-sm ${r.success ? 'bg-emerald-950/40 border-emerald-500/40' : 'bg-red-950/40 border-red-500/40'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-white">{r.bureau}</span>
                      <span className={r.success ? 'text-emerald-300' : 'text-red-300'}>
                        {r.success ? '✓ Mailed' : `✗ ${r.error || 'Failed'}`}
                      </span>
                    </div>
                    {r.tracking_id && <p className="text-[11px] text-zinc-300 mt-1">Tracking: <code className="text-emerald-300">{r.tracking_id}</code></p>}
                    {r.lob_url && (
                      <a href={r.lob_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-300 hover:underline mt-1">
                        Preview on Lob <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            <Button
              variant="outline"
              onClick={closeMailReportAndOfferPortal}
              className="border-zinc-600 text-zinc-300 w-full"
              disabled={mailReportSending}
              data-testid="mail-report-cancel"
            >
              Cancel
            </Button>
            {/* 2026-02-24 — Replaced "Mail to N bureaus" with per-bureau
                PRINT buttons.  Each opens a downloadable cover sheet in a
                new tab; the user prints it, staples the sworn/dispute/
                FTC/ID pages under it, and drops the packet at USPS.
                2026-02-28 — Upgraded to /print-bureau-packet: cover page
                (with bureau name at the top) is merged with the CHECKED
                docs into ONE printable PDF per bureau. */}
            {(['transunion','equifax','experian']).map(b => (
              mailReportBureaus[b] ? (
                <Button
                  key={b}
                  onClick={() => {
                    const clientInfo = clientData?.clientInfo || {};
                    const fd = new FormData();
                    fd.append('bureau', b);
                    if (savedReportId) fd.append('ftc_report_id', savedReportId);
                    fd.append('client_name',   clientData?.name || clientInfo.fullName || '');
                    fd.append('date_of_birth', clientInfo.dateOfBirth || '');
                    fd.append('ssn',           clientInfo.ssn || '');
                    fd.append('address_line1', clientInfo.address || '');
                    fd.append('city',          clientInfo.city || '');
                    fd.append('state',         clientInfo.state || '');
                    fd.append('zip_code',      clientInfo.zipCode || '');
                    fd.append('email',         clientData?.email || clientInfo.email || '');
                    fd.append('phone',         clientInfo.phone || clientData?.phone || '');
                    // Attach checked docs (auto-loaded + user-added).
                    const checked = mailReportDocs.filter(d => d.checked !== false && d.file);
                    checked.forEach(d => fd.append('docs', d.file, d.name || 'document.pdf'));
                    axios.post(`${API}/api/ftc-report/print-bureau-packet`, fd, { responseType: 'blob' })
                      .then(r => {
                        const url = URL.createObjectURL(r.data);
                        const w = window.open(url, '_blank');
                        if (w) { w.focus(); setTimeout(() => { try { w.print(); } catch(_){} }, 800); }
                        const bureauLabel = b.charAt(0).toUpperCase() + b.slice(1);
                        if (checked.length > 0) {
                          toast.success(`${bureauLabel} packet ready — cover + ${checked.length} doc${checked.length === 1 ? '' : 's'} merged for printing`);
                        } else {
                          toast.success(`${bureauLabel} cover sheet ready — print + staple your packet under it`);
                        }
                      })
                      .catch(e => toast.error(e?.response?.data?.detail || 'Failed to build bureau packet'));
                  }}
                  className={`w-full text-white ${b === 'transunion' ? 'bg-blue-700 hover:bg-blue-800' : b === 'equifax' ? 'bg-red-700 hover:bg-red-800' : 'bg-emerald-700 hover:bg-emerald-800'}`}
                  data-testid={`print-cover-${b}-btn`}
                >
                  🖨️ Print {b.charAt(0).toUpperCase() + b.slice(1)} Packet
                </Button>
              ) : null
            ))}
            {/* Legacy mail send button — hidden.  All third-party mail
                providers were removed 2026-02-24; per-bureau printing
                replaces the automated mail-off flow. */}
            {false && (
            <Button
              onClick={handleSendMailReport}
              disabled={mailReportSending || mailReportDocs.length === 0 || !Object.values(mailReportBureaus).some(Boolean)}
              className="bg-blue-600 hover:bg-blue-700 w-full"
              data-testid="mail-report-send"
            >
              {mailReportSending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              ) : (() => {
                const bureauCount = Object.values(mailReportBureaus).filter(Boolean).length;
                const unitCost = { 'usps_first_class': 1.50, 'certified': 8.50, 'certified_return_receipt': 12.50 }[mailReportClass] || 8.50;
                const totalCost = (bureauCount * unitCost).toFixed(2);
                return (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Mail to {bureauCount} bureau{bureauCount === 1 ? '' : 's'} · ${totalCost}
                  </>
                );
              })()}
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════
          Portal Announcement Prompt — appears AFTER Mail Report modal.
          Optional; owner can Skip or send the client an email introducing
          the new Client Update Portal.  (Added 2026-02-27 per user
          request — the FTC flow now offers to notify the client about
          the portal right in the same session.)
          ═══════════════════════════════════════════════════════════════ */}
      <Dialog open={showPortalPromptModal} onOpenChange={setShowPortalPromptModal}>
        <DialogContent
          className="bg-zinc-900 border-purple-500/40 max-w-md p-0 gap-0"
          data-testid="portal-announcement-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-2">
            <DialogTitle className="flex items-center gap-2 text-lg text-white">
              <Send className="w-5 h-5 text-purple-400" />
              Also Send Portal Link?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 px-5 py-3">
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3">
              <p className="text-zinc-400 text-xs">Client:</p>
              <p className="text-white font-medium">{clientData?.name || 'N/A'}</p>
              <p className="text-purple-300 text-sm">{clientData?.email || 'N/A'}</p>
            </div>

            <p className="text-zinc-300 text-sm leading-relaxed">
              Send <strong className="text-white">{clientData?.name?.split(' ')[0] || 'this client'}</strong> a
              separate email introducing the <strong className="text-purple-300">Client Update Portal</strong> —
              the fastest way for them to update you on their credit journey and the best way for you to
              know what&apos;s happening on their credit file every week.
            </p>

            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-xs text-purple-100 leading-relaxed">
              The email explains what the portal does, includes the link, and matches the announcement
              being added to their Credit Repair Steps email — so they&apos;ll see the link twice for
              maximum awareness.
            </div>
          </div>

          <DialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900">
            <Button
              variant="outline"
              onClick={() => setShowPortalPromptModal(false)}
              className="border-zinc-600 text-zinc-300 w-full"
              disabled={sendingPortalAnnouncement}
              data-testid="portal-announce-skip"
            >
              Skip
            </Button>
            <Button
              onClick={sendPortalAnnouncementToThisClient}
              disabled={sendingPortalAnnouncement || !clientData?.email}
              className="bg-purple-600 hover:bg-purple-500 w-full"
              data-testid="portal-announce-send"
            >
              {sendingPortalAnnouncement ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send Portal Link</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Letter modal — opens on demand from the credit-repair-email modal */}
      {/* 2026-02-28 — Account picker sits before the modal.  On Continue,
          it overwrites `ftcAccountsStructuredForDispute` with just the
          checked subset, then opens the modal.  Cancel returns the user
          to wherever they came from (no letter created). */}
      <DisputeAccountsPickerModal
        open={showDisputePicker}
        onClose={() => setShowDisputePicker(false)}
        accounts={ftcAccountsStructuredForDispute}
        onConfirm={(subset) => {
          setFtcAccountsStructuredForDispute(subset);
          setShowDisputePicker(false);
          setShowDisputeLetterModal(true);
        }}
      />
      <DisputeLetterModal
        open={showDisputeLetterModal}
        onClose={() => {
          setShowDisputeLetterModal(false);
          setActiveSavedLetter(null);
          proceedAfterDisputeLetters();
          if (swornJustCreatedRef.current) {
            swornJustCreatedRef.current = false;
            onSwornStatementCreated?.();
          }
        }}
        clientId={clientId}
        clientName={clientData?.name || ''}
        clientAddressLines={[
          clientData?.address,
          [clientData?.city, clientData?.state, clientData?.zip].filter(Boolean).join(', '),
        ].filter(Boolean)}
        defaultAccountsStructured={ftcAccountsStructuredForDispute}
        defaultAccounts={ftcAccountsStructuredForDispute.length === 0 ? [
          ...(ftcAccountsForEmail?.transunion || []),
          ...(ftcAccountsForEmail?.equifax || []),
          ...(ftcAccountsForEmail?.experian || []),
        ].filter((v, i, arr) => arr.indexOf(v) === i) : undefined}
        initialSavedLetter={activeSavedLetter}
        savedLetterId={activeSavedLetter?.id || null}
        onSaved={() => { refreshSavedDisputeLetters(); }}
        onDone={() => {
          // Feb 2026 — flag that at least one dispute letter successfully
          // downloaded so the modal's onClose knows to offer the bureau
          // paper forms next (instead of jumping straight to the credit
          // repair email popup).  Uses a ref because close() runs in the
          // same tick as onDone — useState would be stale here.
          disputeLettersJustDownloadedRef.current = true;
        }}
        onDisputeBlobsCaptured={({ swornBlob, swornName, disputeBlob, disputeName }) => {
          // Cache the fresh PDFs so the packet builder can just check
          // boxes instead of re-picking files from disk on mobile.
          if (swornBlob) {
            setLastGeneratedSwornBlob({ blob: swornBlob, name: swornName || 'Sworn Statement.pdf' });
            swornJustCreatedRef.current = true;
          }
          if (disputeBlob) setLastGeneratedDisputeBlob({ blob: disputeBlob, name: disputeName || 'Dispute Letter.pdf' });
          // 2026-02-28 (user request) — silent auto-save of the parent
          // FTC report row so switching tabs mid-generation doesn't
          // lose sworn/dispute progress.  Fire-and-forget.
          try { saveCompletedReportLog({}).catch(() => {}); } catch { /* ignore */ }
        }}
      />

      {/* ─── Post-FTC-generation dispute-letter prompt (RESTORED 2026-02-05) ───
          Fires immediately after the FTC report PDF is downloaded so the
          user can create a matching handwritten dispute letter right away.
          Skipping proceeds to the credit-repair email popup as usual. */}
      {showPostFtcDisputePrompt && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="post-ftc-dispute-prompt-overlay"
        >
          <div
            className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-fuchsia-500/40 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-fuchsia-900/40"
            data-testid="post-ftc-dispute-prompt"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-fuchsia-300" />
              </div>
              <div>
                <div className="font-bold text-white">FTC Report generated ✅</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Create a handwritten dispute letter for {clientData?.name || 'this client'} now?
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed bg-black/30 border border-white/5 rounded-lg p-3 mb-4">
              You&apos;ll pick <b>Regular</b> (first template) or <b>Second Round Verified Account</b>, choose
              original-pen (handwritten) or copied (typed), select a font, and download.  Every letter is uniquely
              reworded by AI so e-OSCAR can&apos;t pattern-match — while keeping all the template&apos;s legal
              citations exactly.  Accounts appear with their <b>open date + balance</b> just like on the FTC report.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowPostFtcDisputePrompt(false);
                  // Continue to the Credit Repair email popup as usual.
                  if (clientData?.email) {
                    setShowCreditRepairEmailPopup(true);
                  }
                }}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.10] text-slate-200 text-sm font-medium py-2.5 rounded-xl"
                data-testid="post-ftc-dispute-skip-btn"
              >
                Skip — send emails first
              </button>
              <button
                onClick={() => {
                  setShowPostFtcDisputePrompt(false);
                  // 2026-02-28 (user request) — open the account picker
                  // first so the admin can uncheck any accounts they
                  // don't want on the letter.  If no accounts are staged
                  // (rare — user opened the prompt with an empty report),
                  // fall through to the modal directly.
                  if (ftcAccountsStructuredForDispute.length > 0) {
                    setShowDisputePicker(true);
                  } else {
                    setShowDisputeLetterModal(true);
                  }
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-semibold py-2.5 rounded-xl shadow-md shadow-fuchsia-900/40"
                data-testid="post-ftc-dispute-open-btn"
              >
                <FileText className="w-4 h-4" /> Create Dispute Letter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Post-email dispute-letter prompt (RESTORED 2026-02-05) ─── */}
      {showPostEmailDisputePrompt && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="post-email-dispute-prompt-overlay"
        >
          <div
            className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-amber-500/40 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-amber-900/40"
            data-testid="post-email-dispute-prompt"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <div className="font-bold text-white">Credit Repair emails scheduled ✅</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Create a handwritten dispute letter for {clientData?.name || 'this client'} now?
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed bg-black/30 border border-white/5 rounded-lg p-3 mb-4">
              You&apos;ll pick <b>Regular</b> (first template) or <b>Second Round Verified Account</b>, choose original-pen
              (handwritten) or copied (typed), select a font, and download. Every letter is uniquely reworded by AI so
              e-OSCAR can&apos;t pattern-match — while keeping all the template&apos;s legal citations exactly.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowPostEmailDisputePrompt(false);
                  setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
                  setShowQuickReminderPopup(true);
                }}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.10] text-slate-200 text-sm font-medium py-2.5 rounded-xl"
                data-testid="post-email-dispute-skip-btn"
              >
                Skip for now
              </button>
              <button
                onClick={() => {
                  setShowPostEmailDisputePrompt(false);
                  setDisputeOpenedFromPostEmail(true);
                  if (ftcAccountsStructuredForDispute.length > 0) {
                    setShowDisputePicker(true);
                  } else {
                    setShowDisputeLetterModal(true);
                  }
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white text-sm font-semibold py-2.5 rounded-xl shadow-md shadow-amber-900/40"
                data-testid="post-email-dispute-open-btn"
              >
                <FileText className="w-4 h-4" /> Create Dispute Letter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── NEW 2026-02-05 (user request) — Dispute Packet Builder ───
          Fires right after the mail modal closes, BEFORE the review
          prompt.  Bundles FTC Report + Dispute Letter + Sworn Statement
          + ID into ONE byte-faithful PDF the user can save to Files
          (iPhone) or the desktop and upload to bureau sites directly.
          Uses PyMuPDF insert_pdf on the backend so pages are COPIED
          verbatim — no re-render, no visual change. */}
      {showDisputePacketPrompt && (
        <div
          className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="dispute-packet-prompt-overlay"
        >
          <div
            className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-blue-500/40 rounded-2xl w-full max-w-lg p-6 shadow-2xl shadow-blue-900/40 max-h-[95vh] overflow-y-auto"
            data-testid="dispute-packet-prompt"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-300" />
              </div>
              <div>
                <div className="font-bold text-white">Build a Dispute Packet</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Combine the FTC report, dispute letter, sworn statement, and ID
                  into ONE PDF you can upload to a bureau&apos;s website from your
                  phone or desktop.
                </div>
              </div>
            </div>

            {/* ── In-session document checklist ── */}
            {/* We hold onto every PDF the user just generated in this
                session (Sworn Statement + Dispute Letter + FTC Report)
                so they can be included with a single tap — no need to
                dig through the phone's Files app on mobile.  If a doc
                is missing (user hasn't generated it yet), the row is
                shown as disabled with a hint. */}
            <div className="space-y-2 mb-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-blue-300">
                Documents from this session
              </div>

              {/* Sworn Statement */}
              <PacketCheckboxRow
                label="Sworn Statement"
                blobEntry={lastGeneratedSwornBlob}
                checked={!!lastGeneratedSwornBlob && packetIncludeSworn}
                onToggle={(v) => setPacketIncludeSworn(v)}
                testid="packet-check-sworn"
              />

              {/* Dispute Letter */}
              <PacketCheckboxRow
                label="Dispute Letter"
                blobEntry={lastGeneratedDisputeBlob}
                checked={!!lastGeneratedDisputeBlob && packetIncludeDispute}
                onToggle={(v) => setPacketIncludeDispute(v)}
                testid="packet-check-dispute"
              />

              {/* FTC Identity Theft Report */}
              <PacketCheckboxRow
                label="FTC Identity Theft Report"
                blobEntry={lastGeneratedFtcBlob}
                checked={!!lastGeneratedFtcBlob && packetIncludeFtc}
                onToggle={(v) => setPacketIncludeFtc(v)}
                testid="packet-check-ftc"
              />
            </div>

            {/* ID (last page) — auto-suggest the client's saved ID if
                we have one on file (2026-02-06 user request).  User can
                still upload a fresh photo to override — same slot. */}
            <div className="space-y-3">
              {savedClientId && (
                <label
                  className={`flex items-start gap-2 p-2.5 rounded-lg border transition cursor-pointer ${
                    packetUseSavedId
                      ? 'bg-emerald-500/10 border-emerald-400/40'
                      : 'bg-white/[0.04] border-white/10 hover:border-emerald-400/30'
                  }`}
                  data-testid="packet-check-saved-id"
                >
                  <input
                    type="checkbox"
                    checked={packetUseSavedId}
                    onChange={(e) => {
                      setPacketUseSavedId(e.target.checked);
                      // If the user checks "use saved", clear any fresh
                      // upload so we don't accidentally send both.
                      if (e.target.checked) {
                        setPacketFiles((p) => ({ ...p, id: null }));
                      }
                    }}
                    className="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                      Use ID on file
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold uppercase tracking-wider">
                        Saved
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                      {savedClientId.filename}
                    </div>
                  </div>
                </label>
              )}
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-blue-300 block mb-1">
                  {savedClientId ? 'Or upload a fresh ID' : 'ID (photo or PDF, last page)'}
                </label>
                <input type="file" accept="application/pdf,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setPacketFiles((p) => ({ ...p, id: f }));
                    if (f) setPacketUseSavedId(false);
                  }}
                  className="w-full text-xs text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-500/20 file:text-blue-200 file:text-[10px] file:uppercase file:tracking-wider file:font-bold hover:file:bg-blue-500/30"
                  data-testid="packet-id-input" />
                {packetFiles.id && (
                  <div className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1" data-testid="packet-id-selected">
                    ✓ {packetFiles.id.name}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-blue-300 block mb-1">
                  Name this file
                </label>
                <input type="text" value={packetFilename}
                  onChange={(e) => setPacketFilename(e.target.value)}
                  placeholder="e.g. Smith Dispute Packet"
                  className="w-full bg-black/60 border border-white/10 focus:border-blue-400/60 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  data-testid="packet-filename-input" />
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed bg-black/30 border border-white/5 rounded-md p-2">
                The merged PDF copies each file&apos;s pages VERBATIM — no re-rendering, no visual changes. The output looks exactly like the individual downloads.
              </p>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setShowDisputePacketPrompt(false);
                  setPacketFiles({ ftc: null, dispute: null, sworn: null, id: null });
                  setTimeout(() => setShowPostMailReviewPrompt(true), 200);
                }}
                disabled={packetBuilding}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.10] text-slate-200 text-sm font-medium py-2.5 rounded-xl disabled:opacity-50"
                data-testid="packet-skip-btn"
              >
                Skip
              </button>
              <button
                onClick={async () => {
                  // Which docs is the user including? Checked in-session
                  // blobs + optional uploaded ID.  If a checkbox is on
                  // but the blob is null (edge case), skip it.
                  const swornBlob = packetIncludeSworn ? lastGeneratedSwornBlob?.blob : null;
                  const disputeBlob = packetIncludeDispute ? lastGeneratedDisputeBlob?.blob : null;
                  const ftcBlob = packetIncludeFtc ? (lastGeneratedFtcBlob?.blob || lastGeneratedFtcBlob?.file) : null;
                  const includingSavedId = packetUseSavedId && savedClientId && !packetFiles.id;
                  const partsCount = [swornBlob, disputeBlob, ftcBlob, packetFiles.id, includingSavedId ? true : null].filter(Boolean).length;
                  if (partsCount === 0) {
                    toast.error('Nothing to bundle — check at least one document or upload an ID.');
                    return;
                  }
                  setPacketBuilding(true);
                  try {
                    const fd = new FormData();
                    if (swornBlob) fd.append('sworn_statement', swornBlob, lastGeneratedSwornBlob.name || 'sworn_statement.pdf');
                    if (disputeBlob) fd.append('dispute_letter', disputeBlob, lastGeneratedDisputeBlob.name || 'dispute_letter.pdf');
                    if (ftcBlob) fd.append('ftc_report', ftcBlob, lastGeneratedFtcBlob.name || 'ftc_report.pdf');
                    if (packetFiles.id) {
                      // Fresh upload takes precedence over the saved ID.
                      fd.append('id_document', packetFiles.id);
                    } else if (includingSavedId && clientId) {
                      // Ask the backend to pull the client's saved ID directly.
                      fd.append('client_id', clientId);
                      fd.append('use_saved_id', '1');
                    }
                    if (packetFilename) fd.append('filename', packetFilename);
                    const res = await axios.post(`${API}/api/dispute-packet/build`, fd, {
                      responseType: 'blob', timeout: 300000,
                    });
                    const cd = res.headers?.['content-disposition'] || '';
                    const match = cd.match(/filename="([^"]+)"/);
                    const outName = match?.[1] || `${packetFilename || 'Dispute Packet'}.pdf`;
                    // 2026-02-28 iPhone bug fix — mobile-safe.
                    await downloadBlobMobileSafe(res.data, outName, {
                      shareTitle: 'Dispute Packet',
                    });
                    toast.success(`Dispute packet ready — ${outName}`);
                    setShowDisputePacketPrompt(false);
                    setPacketFiles({ ftc: null, dispute: null, sworn: null, id: null });
                    setTimeout(() => setShowPostMailReviewPrompt(true), 200);
                  } catch (e) {
                    let msg = e.message;
                    if (e.response?.data instanceof Blob) {
                      try {
                        const text = await e.response.data.text();
                        const parsed = JSON.parse(text);
                        msg = parsed.detail || text;
                      } catch { /* ignore */ }
                    }
                    toast.error(`Packet build failed: ${msg}`);
                  } finally {
                    setPacketBuilding(false);
                  }
                }}
                disabled={packetBuilding}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-br from-blue-600 to-cyan-700 hover:from-blue-500 hover:to-cyan-600 text-white text-sm font-semibold py-2.5 rounded-xl shadow-md shadow-blue-900/40 disabled:opacity-60"
                data-testid="packet-build-btn"
              >
                {packetBuilding ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Building…</>
                ) : (
                  <><Download className="w-4 h-4" /> Build &amp; Download</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── NEW 2026-02-05 — Post-mail "review any section" prompt ───
          Fires whenever the Mail Report modal closes (whether the mail
          succeeded, was skipped, or the user cancelled).  Lists every
          section and every prompt in the flow so nothing gets missed. */}
      {showPostMailReviewPrompt && (
        <div
          className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="post-mail-review-prompt-overlay"
        >
          <div
            className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-emerald-500/40 rounded-2xl w-full max-w-lg p-6 shadow-2xl shadow-emerald-900/40"
            data-testid="post-mail-review-prompt"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <div className="font-bold text-white">Mail step complete ✅</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Anything for {clientData?.name || 'this client'} that needs a second look?
                  Jump back to any section or prompt below.
                </div>
              </div>
            </div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-300 mb-2">
              Every section &amp; prompt in the flow
            </p>
            <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="flow-overview-list">
              {/* 1. Client Info */}
              <button
                onClick={() => revisitFromOverview(() => document.getElementById('ftc-client-info-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-client-info"
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">1. Client Info</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 2. Accounts */}
              <button
                onClick={() => revisitFromOverview(() => document.getElementById('ftc-accounts-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-accounts"
              >
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">2. Accounts &amp; Categories</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 3. Personal Statement */}
              <button
                onClick={() => revisitFromOverview(() => document.getElementById('ftc-personal-statement-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-personal-statement"
              >
                <div className="flex items-center gap-2">
                  <PenLine className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">3. Personal Statement</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 4. Generate FTC Report */}
              <button
                onClick={() => revisitFromOverview(() => document.getElementById('ftc-generate-report-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-generate"
              >
                <div className="flex items-center gap-2">
                  <FileDown className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">4. Generate / Re-download FTC Report</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 5. Dispute Letter prompt */}
              <button
                onClick={() => revisitFromOverview(() => {
                  if (ftcAccountsStructuredForDispute.length > 0) {
                    setShowDisputePicker(true);
                  } else {
                    setShowDisputeLetterModal(true);
                  }
                })}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-dispute-letter-create"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">5. Create a New Dispute Letter</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 6. Saved Dispute Letters (with count) */}
              <button
                onClick={() => revisitFromOverview(() => document.querySelector('[data-testid="saved-dispute-letters-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-dispute-letters"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">6. Saved Dispute Letters ({savedDisputeLetters.length}) — Edit / Add / Remove Accounts</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 7. TransUnion Bureau Form */}
              <button
                onClick={() => revisitFromOverview(() => {
                  setBureauFormsChoice({
                    id: 'transunion', label: 'TransUnion', bureaus: ['transunion'],
                    activeCls: 'bg-sky-600/25 border-sky-500 text-white',
                  });
                  setBureauFormsPromptOpen(true);
                })}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-sky-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-transunion-form"
              >
                <div className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-sky-300" />
                  <span className="text-sm text-white">7. TransUnion Bureau Form (preview / edit / download)</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 8. Equifax Bureau Form */}
              <button
                onClick={() => revisitFromOverview(() => {
                  setBureauFormsChoice({
                    id: 'equifax', label: 'Equifax', bureaus: ['equifax'],
                    activeCls: 'bg-red-600/25 border-red-500 text-white',
                  });
                  setBureauFormsPromptOpen(true);
                })}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-red-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-equifax-form"
              >
                <div className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-red-300" />
                  <span className="text-sm text-white">8. Equifax Bureau Form (preview / edit / download)</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 9. Handwriting Conversion */}
              <button
                onClick={() => revisitFromOverview(() => setShowHandwritingPrompt(true))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-handwriting"
              >
                <div className="flex items-center gap-2">
                  <Feather className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">9. Handwriting Conversion for Letters</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 10. Copy & Fax Converter */}
              <button
                onClick={() => revisitFromOverview(() => setShowCopyFaxPrompt(true))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-copy-fax"
              >
                <div className="flex items-center gap-2">
                  <Printer className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">10. Copy &amp; Fax Converter</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 11. ID Converter */}
              <button
                onClick={() => revisitFromOverview(() => setShowIdConverterPrompt(true))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-id-converter"
              >
                <div className="flex items-center gap-2">
                  <IdCard className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">11. ID &amp; SSN Card Converter</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 12. Dispute Packet Builder */}
              <button
                onClick={() => revisitFromOverview(() => setShowDisputePacketPrompt(true))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-dispute-packet"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">12. Full Dispute Packet Builder</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 13. Credit Repair Emails */}
              <button
                onClick={() => revisitFromOverview(() => setShowCreditRepairEmailPopup(true))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-emails"
              >
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">13. Credit Repair Email (send / re-send)</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 14. Quick Payment Reminder */}
              <button
                onClick={() => revisitFromOverview(() => {
                  setQuickReminderForm({ balance: '', priority: false, promisedDate: '', promisedNotes: '', reminderFrequency: 'weekly' });
                  setShowQuickReminderPopup(true);
                })}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-quick-reminder"
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">14. Quick Payment Reminder</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {/* 15. Mail Report again */}
              <button
                onClick={() => revisitFromOverview(() => setShowMailReportModal(true))}
                className="w-full flex items-center justify-between gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-fuchsia-400/50 rounded-lg px-3 py-2 text-left transition"
                data-testid="post-mail-review-mail-again"
              >
                <div className="flex items-center gap-2">
                  <Send className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-sm text-white">15. Mail-off through the Program (again)</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
            </div>
            <button
              onClick={() => {
                pendingReturnToOverviewRef.current = false;
                setShowPostMailReviewPrompt(false);
                setShowDoneChoiceModal(true);
              }}
              className="w-full mt-4 bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white text-sm font-semibold py-2.5 rounded-xl"
              data-testid="post-mail-review-done-btn"
            >
              ✓ All good — I&apos;m done
            </button>
          </div>
        </div>
      )}

      {/* "I'm done" choice: back to FTC Report Builder or to Dashboard. */}
      {showDoneChoiceModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="flow-done-choice-overlay"
          onClick={() => setShowDoneChoiceModal(false)}
        >
          <div
            className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-emerald-500/40 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-emerald-900/40"
            data-testid="flow-done-choice-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <div className="font-bold text-white">Where would you like to go next?</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  You can jump back to this Flow Overview any time from the FTC Report Builder.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => {
                  setShowDoneChoiceModal(false);
                  // 2026-09-26 — Was scrolling to #ftc-generate-report-anchor
                  // which is only present when the user is already inside
                  // Step 3 of the wizard.  If they finished the flow and
                  // the anchor isn't in the DOM, the click did nothing.
                  // Now we explicitly navigate to /ftc-builder (react-router
                  // no-ops if already there) and defer the scroll one tick.
                  navigate('/ftc-builder');
                  setTimeout(() => {
                    document.getElementById('ftc-generate-report-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 150);
                }}
                className="w-full bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                data-testid="flow-done-back-to-builder-btn"
              >
                <FileDown className="w-4 h-4" /> Back to FTC Report Builder
              </button>
              <button
                onClick={() => {
                  setShowDoneChoiceModal(false);
                  // 2026-09-26 — Fixed blank-screen bug.  The dashboard
                  // route is mounted at "/" (see App.js), not "/dashboard".
                  // Navigating to the non-existent "/dashboard" path
                  // matched the catch-all and rendered a blank screen.
                  navigate('/');
                }}
                className="w-full bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                data-testid="flow-done-goto-dashboard-btn"
              >
                <Home className="w-4 h-4" /> Go to Dashboard
              </button>
              <button
                onClick={() => setShowDoneChoiceModal(false)}
                className="w-full mt-1 text-xs text-slate-400 hover:text-slate-200"
                data-testid="flow-done-cancel-btn"
              >
                Not yet — keep me here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Persistent "Open Flow Overview" chip — visible at ALL times in the
          FTC Report Builder so the user can pop the list open from any
          point of the flow, not just after Mail. */}
      {!showPostMailReviewPrompt && !showDoneChoiceModal && !showPreview && (
        <button
          onClick={() => {
            pendingReturnToOverviewRef.current = false;
            setShowPostMailReviewPrompt(true);
          }}
          className="fixed bottom-20 right-4 sm:bottom-auto sm:top-4 z-[55] flex items-center gap-2 bg-fuchsia-600/90 hover:bg-fuchsia-500 text-white text-xs font-semibold px-3.5 py-2 rounded-full shadow-2xl shadow-fuchsia-900/40 border border-fuchsia-400/40 backdrop-blur-sm"
          data-testid="open-flow-overview-btn"
          title="Every section & prompt in this FTC flow"
        >
          <LayoutList className="w-3.5 h-3.5" /> Flow Overview
        </button>
      )}

      {/* Persistent "Return to Flow Overview" pill button.
          Visible whenever the user came from the Flow Overview via an
          anchor-scroll target (which doesn't fire a close event we can
          watch) so they can hop back to the list without losing their
          place. */}
      {pendingReturnToOverviewRef.current && !showPostMailReviewPrompt && (
        <button
          onClick={() => {
            pendingReturnToOverviewRef.current = false;
            setShowPostMailReviewPrompt(true);
          }}
          className="fixed bottom-6 right-6 z-[65] flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-2xl shadow-emerald-900/40 border border-emerald-400/40"
          data-testid="return-to-flow-overview-btn"
        >
          <LayoutList className="w-4 h-4" /> Return to Flow Overview
        </button>
      )}

      {/* Feb 2026 — Bureau Paper Forms prompt (mirrors DisputeWizard).
          Fires after dispute letters are downloaded, BEFORE Quick
          Reminder / Copy & Fax.  Users can auto-download the pre-filled
          form or open the free-form editor to nudge fields first. */}
      {bureauFormsPromptOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="ftc-bureau-forms-prompt"
          onClick={() => !bureauFormsBusy && setBureauFormsPromptOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Pre-fill the official bureau paper forms</h3>
                <p className="text-sm text-zinc-400 mt-1">
                  Auto-fill the TransUnion Investigation Request and the Equifax Dispute Request using the same client info + accounts from your dispute letters.
                </p>
                <ul className="mt-3 text-xs text-zinc-500 list-disc list-inside space-y-1">
                  <li>Signature and dispute-details wording copied from your letter</li>
                  <li>Auto-split: TU 4 accounts / form, Equifax 12 accounts / form</li>
                  <li>&ldquo;Account Not Mine&rdquo; checked by default; Driver&apos;s License marked on Equifax</li>
                </ul>
              </div>
              {!bureauFormsBusy && (
                <button
                  onClick={() => { setBureauFormsPromptOpen(false); proceedAfterDisputeLetters(); }}
                  className="text-zinc-400 hover:text-white ml-2 text-xl leading-none"
                  data-testid="ftc-bureau-forms-close-btn"
                  aria-label="Close"
                >×</button>
              )}
            </div>

            {/* Bureau selector — Feb 2026 redesign per user request:
                one clean prompt that asks WHICH bureau, then two big
                action buttons.  Way easier to scan than the old 2×3
                grid of six buttons. */}
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Which bureau?</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'both', label: 'Both',       bureaus: ['transunion','equifax'], activeCls: 'bg-emerald-600/25 border-emerald-500 text-white' },
                  { id: 'tu',   label: 'TransUnion', bureaus: ['transunion'],           activeCls: 'bg-blue-600/25 border-blue-500 text-white' },
                  { id: 'eq',   label: 'Equifax',    bureaus: ['equifax'],              activeCls: 'bg-red-600/25 border-red-500 text-white' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setBureauFormsChoice(opt)}
                    disabled={bureauFormsBusy}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      bureauFormsChoice.id === opt.id
                        ? opt.activeCls
                        : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
                    }`}
                    data-testid={`ftc-bureau-forms-bureau-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Two big action buttons — matches user's mental model of
                "Preview / Edit" (see & tweak) vs "Generate" (just
                download). */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                onClick={() => runBureauFormsFillFTC(bureauFormsChoice.bureaus, { editFirst: true })}
                disabled={bureauFormsBusy}
                variant="outline"
                className="bg-zinc-800 border-fuchsia-500/60 text-fuchsia-200 hover:bg-fuchsia-500/20 h-11"
                data-testid="ftc-bureau-forms-preview-edit"
              >
                👁 Preview / Edit
              </Button>
              <Button
                onClick={() => runBureauFormsFillFTC(bureauFormsChoice.bureaus)}
                disabled={bureauFormsBusy}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-11"
                data-testid="ftc-bureau-forms-generate"
              >
                {bureauFormsBusy ? 'Working…' : '⬇ Generate & Download'}
              </Button>
            </div>

            <div className="mt-3 text-right">
              <button
                type="button"
                onClick={() => { setBureauFormsPromptOpen(false); proceedAfterDisputeLetters(); }}
                disabled={bureauFormsBusy}
                className="text-xs text-zinc-500 hover:text-zinc-300 underline"
                data-testid="ftc-bureau-forms-skip-btn"
              >
                Skip — I don&apos;t need these forms
              </button>
            </div>
          </div>
        </div>
      )}

      <BureauFormEditor
        open={bureauEditorOpen}
        onClose={advanceBureauEditorQueueFTC}
        basePdfDataUrl={bureauEditorPdf}
        filename={bureauEditorFilename}
        bureauLabel={bureauEditorLabel}
      />
    </div>
  );
}
